// Generated by tools/as3-to-ts from work/decomp/main/scripts/FWP.as; hand-edits allowed (see docs/architecture.md)

import { AssetLibrary, Bitmap, BitmapData, BitmapFilterQuality, BlurFilter, Event, KeyboardEvent, MouseEvent, MovieClip, Point, Rectangle, Security, SharedObject, Sprite } from '@flash';
import * as as3 from './runtime/as3';
import { setClipboard } from './runtime/flash-shims';
import { GAME_STATE_FORMAT, numericPlayerId, type GameBackend } from './services';
import { MSG_CONNECTION_LOST, MSG_CONNECTION_RESTORED, MSG_NET_RETRY_NOW, MSG_PLAYER_CHANGED, MSG_PROFILE_CONFLICT, MSG_PROFILE_UPDATED, MSG_RESTART, MSG_SESSION_EXPIRED, MSG_SESSION_REPLACED, MSG_VERSION_MISMATCH, netErrorText, netRetryText } from './runtime/net-messages';
import { NetEventQueue } from './runtime/net-events';
import { OnlineLobby } from './runtime/online-lobby';
import { ClanHub } from './runtime/clan-hub';
import { BattleInput, LocalInput } from './runtime/battle-input';
import { BATTLE_FINISH_TIMEOUT_MS, MSG_BATTLE_CHECK, MSG_BATTLE_NOT_COUNTED, MSG_BATTLE_REJECTED, MSG_GRAVE_REFUSED, MSG_ZONE_NOT_CAPTURED, battleCommand, battleRefusedText, battleSurrendered, serverEndGame } from './runtime/battle-net';
import { NetOverlay, addRestartButton, type NetOverlayView } from './ui/net-overlay';
import { NET_CALL_TIMEOUT_MS, banHoursLeft, classifyNetFailure, netCallTimeoutMs, netErrorCode, netRetryDelayMs } from './runtime/net-policy';
import { claimEndGame, commandRefusedText, skillsCommitArgs } from './runtime/net-commands';
import { MSG_AUCTION_UNAVAILABLE, auctionMemory, loadAuction } from './runtime/auction-ui';
import { MSG_AUTH_ACCOUNT_DELETED, MSG_AUTH_SESSION_EXPIRED, MSG_AUTH_SESSION_REPLACED, MSG_AUTH_SIGNED_OUT } from './runtime/auth-texts';
import { AuthScreen, type AuthView, type ServerSettingPort } from './ui/auth-screen';
import { DeleteAccountDialog } from './ui/delete-account';
import { AccountDialog } from './ui/account-dialog';
import { EmailDialog } from './ui/email-dialog';
import { MSG_EMAIL_BUTTON } from './runtime/auth-texts';
import { type IntegrationsPort, integrationExtras } from './runtime/integrations-port';
import { WideScreen } from './ui/wide-screen';
import { scaleWormLabels, separateWormLabels, wormLabelScale } from './ui/worm-labels';
import { touchScannerInfo } from './ui/battle-scanner';
import { touchSheetLayout } from './ui/battle-sheet';
import { WidePopups } from './ui/wide-popups';
import { WIDE_MARGIN_X } from '../shared/wide-view';
import { MD5 } from './com/adobe/crypto/MD5';
import { ProtectedInt } from './com/ProtectedInt';
import { new_interf } from './classes/new_interf';
import { obj } from './classes/obj';
import { proc } from './classes/proc';
import { sound } from './classes/sound';
import { world } from './classes/world';
import { create_RANDOM_NUMBERS } from './data/RANDOM_NUMBERS';
import { get_team as team_from_def } from './data/TEAMS_DEF';
import { createGameDefs } from './rules/defs';
import type { MonetizationPort } from './runtime/monetization';
import { MSG_AD_DELAYED, MSG_AD_FAILED, MSG_STORE_CREDITED, MSG_STORE_FAILED, MSG_STORE_PENDING, MSG_STORE_SOON, accountExtras } from './runtime/monetization-ui';
import { ABOUT_MODE, ABOUT_NAME, MSG_ABOUT, addSplashNotice } from './ui/legal-notice';
import { REPLAY_OFFER_PREF, type ReplayOffer, offerReplay, replayOfferResult, replayTitle, saveRefused } from './runtime/replay-offer';
import { CHAT_HIDDEN_PREF, ChatController } from './runtime/chat';
import { ChatPanel } from './ui/chat-panel';

/**
 * Why the game restarts itself (FWPDeps.restart): the host keeps it for the next start (FWPDeps.restartReason), whose
 * sign-in screen then says e.g. that the session expired. 'language': a language was picked; 'signed_out': the player
 * signed out; 'account_deleted': the player deleted the account (settings); 'session_expired': the session ended and
 * could not be renewed; 'session_replaced': the account signed in on another device, which ended this one's session
 * (one active session per account); 'player_changed': the stored session now belongs to another player; 'error': the
 * restart button of an error.
 */
export type RestartReason = 'language' | 'signed_out' | 'account_deleted' | 'session_expired' | 'session_replaced' | 'player_changed' | 'error';

/**
 * Dependencies injected into the game root (replaces the embedded sub-SWF ByteArrays and VK flashVars).
 * PORT: the original document class took no constructor arguments.
 */
export interface FWPDeps {
  /** Loaded asset bundles: 'fwp', 'interf', 'obj', 'proc', 'sound', 'world' and map_<id> terrain bitmaps. */
  library: AssetLibrary;
  /** Own backend (profile persistence, tops, clans, assault zones, replays); see src/game/services.ts. */
  services: GameBackend;
  /**
   * Developer test profile (the original granted it to the developer's account); off by default. A request: the
   * backend creates it for a new account only where it allows it (profile.load { dev }); at L0/L1 the game makes it,
   * with the in-process (offline) backend only.
   */
  devProfile?: boolean;
  /**
   * Restarts the game from scratch (browser: reload; no state of this run survives). Used by the language switch
   * (a fresh catalogue), the sign-out, an ended session and the restart button of errors.
   */
  restart?: (reason?: RestartReason) => void;
  /** Why the previous run restarted the game (see RestartReason); the sign-in screen shows it. */
  restartReason?: RestartReason | null;
  /**
   * Clock (ms) of the backend bridge: call watchdog, retry backoff, connection notices. Default `as3.now()`. It must
   * run in real time when the backend does (a fast-ticking headless client would otherwise time out real network
   * calls); a headless game with an in-process backend keeps the virtual clock, so its tests stay deterministic.
   */
  netClock?: () => number;
  /**
   * PORT: the server setting of clients that offer one (the native app, the dev server): the sign-in screen shows the
   * server and lets the player change it (src/game/ui/auth-screen.ts). Absent on the production web page.
   */
  serverSetting?: ServerSettingPort | null;
  /**
   * PORT: the store, the opt-in rewarded ads and Google Play Games of the host (src/game/monetization.ts); absent on
   * the web page. The game adds its buttons only when they are offered and works the same without them.
   */
  monetization?: MonetizationPort | null;
  /**
   * PORT: the app's diagnostics and notification switches (src/game/runtime/integrations-port.ts); absent on the web
   * page and in a build without Firebase.
   */
  integrations?: IntegrationsPort | null;
}

export class FWP extends Sprite {
  // PORT: injected dependencies (see FWPDeps).
  deps: FWPDeps;
  // PORT: own backend bridge state (see net_run).
  services: any = null;
  DEV_PROFILE: any = false;
  net_pending: any = null;
  /** Bridge clock (net_now) time the running attempt of the pending call started / its next retry is due (0: none). */
  net_started_at: any = 0;
  net_retry_at: any = 0;
  /** Server events and connection status changes, taken by _main (net_frame). */
  net_events: any = null;
  /** Unsubscribes the session-replaced listener (net_attach). */
  net_replaced_off: any = null;
  // PORT: design D5 - the online lobby and the running online battle (runtime/online-lobby.ts), created at boot.
  online: any = null;
  // PORT: the game's accounts - the sign-in screen (ui/auth-screen.ts) while no session is stored (net_show_auth).
  auth_screen: any = null;
  /** PORT: the delete-account dialog over the settings screen (src/game/ui/delete-account.ts), while it is open. */
  delete_dialog: any = null;
  /** PORT: the store's purchase dialog while it is open (src/game/ui/store-dialog.ts). */
  store_dialog: any = null;
  /** PORT: the settings screen's account and language dialog while it is open (ui/account-dialog.ts). */
  account_dialog: any = null;
  // PORT: the account's e-mail dialog (ui/email-dialog.ts) while it is open.
  email_dialog: any = null;
  /** PORT: the chat (runtime/chat.ts: the main map's button and its unread badge, the hidden players). */
  chat: ChatController = new ChatController(this);
  /** PORT: the chat panel over the main map (ui/chat-panel.ts) while it is open. */
  chat_ui: any = null;
  /** Retry countdown, connection indicator and notices over the game (src/game/ui/net-overlay.ts). */
  net_overlay: any = null;
  net_link_lost: any = false;
  // PORT: the last won battle ({ battleId, mode, gold }: the ad bonus of the victory screen and of its graves), the
  // PORT: grave searched last, the ad bonuses already taken, a running ad / purchase, the store tab of the pay screen.
  last_win: any = null;
  last_grave_index: any = 0;
  ad_used: any = {};
  ad_busy: any = false;
  store_tab: any = "diamonds";
  net_restart_armed: any = false;
  /** PORT: the restart this run asked the host for (the first one wins: a later one must not replace its reason). */
  net_restart_reason: RestartReason | null = null;
  /** Wall-clock time a backend call may stay unsettled before it is failed and retried (> the transport's whole failure time). */
  static readonly NET_CALL_TIMEOUT_MS: number = NET_CALL_TIMEOUT_MS;
  player_ids: any = {};
  profile_revision: any = 0;
  state_dirty_reason: any = "";
  save_reason_override: any = "";

  // PORT: static ASSET_* : Class constants (embedded FWP_ASSET_* ByteArrayAsset sub-SWFs) removed; assets come from the AssetLibrary.
  static readonly ASSET_ID_FWP: number = 0;
  static readonly ASSET_ID_INTERF: number = 1;
  static readonly ASSET_ID_OBJ: number = 2;
  static readonly ASSET_ID_PROC: number = 3;
  static readonly ASSET_ID_SOUND: number = 4;
  static readonly ASSET_ID_WORLD: number = 5;
  // PORT: VK removed: LOCAL_TEST / MAIN_APPL (the developer's switches of the VK build that bypassed the network).
  LAST_CONTEST_STATE: any = 6;
  cur_random: any = 500;
  RANDOM_NUMBERS: any = new Array();
  WINDOW_WIDTH: number = 807;
  WINDOW_HEIGHT: number = 600;
  /** PORT (wide screens): dresses the original's screens for the margin a wide screen shows (ui/wide-screen.ts). */
  wide_screen: WideScreen | null = null;
  wide_popups: WidePopups | null = null;
  /**
   * PORT (touch screens): the stage rectangles the on-screen battle controls' clusters take on this screen (the shell
   * sets it when the touch controls are on: src/main.ts), or null without them. The wide screen's battle HUD keeps
   * clear of them and hides the backpack (the controls have that button). Presentation only.
   */
  touch_zones: (() => Array<{ x: number; y: number; width: number; height: number }>) | null = null;

  /**
   * PORT (touch screens): whether the on-screen battle controls are shown now (the shell sets it with touch_zones;
   * they hide during the opponent's turn, while touch_zones keeps their places for the HUD). Presentation only.
   */
  touch_shown: (() => boolean) | null = null;

  /**
   * PORT (touch screens): a target mode of the touch controls waits for the finger's first point on the map (the shell
   * sets it with touch_zones): until then the game's target cursor and girder are not drawn (proc.check_mousecontrol),
   * where the mouse's last point (a cell of the weapon sheet, the wind bar) would look like a chosen target.
   */
  touch_target_pending: (() => boolean) | null = null;

  /**
   * PORT (touch screens): the stage rectangle of the touch controls' close button of the battle's menus (the weapon
   * sheet, the scanner: their own close buttons lie under it, ui/battle-sheet.ts), or null; set by the shell with
   * touch_zones. Presentation only.
   */
  touch_close_rect: (() => { x: number; y: number; width: number; height: number } | null) | null = null;

  /**
   * PORT (touch screens): the on-screen battle controls' buttons shown now (stage rectangles) and the controls' mode
   * ('turn': the player aims), or null while they are hidden; set by the shell with touch_zones. Presentation only.
   */
  touch_buttons: (() => { mode: string; rects: Array<{ x: number; y: number; width: number; height: number }> } | null) | null = null;

  /** PORT (touch screens): the touch buttons shown as frame_touch_buttons last saw them. */
  private touch_buttons_key: string = "";

  /**
   * PORT (touch screens): the controls' buttons came or moved while the player aims (a weapon with a fuse and a bounce
   * button picked, the fire button of another weapon, a new screen shape): where one of them now covers the current
   * worm (its sprite: hud_focus), the camera shows it clear of the controls as at the turn's start (proc.check_displace,
   * forced; the camera keeps the worm out of the clusters' places, FWP.camera_margin). The camera stays where it is while
   * the worm is not under a button (a player looking around). Presentation only: the camera is not read by the rules.
   */
  private readonly frame_touch_buttons = (): void => {
    const f = this.touch_buttons;
    const p: any = this._proc;
    let b: { mode: string; rects: Array<{ x: number; y: number; width: number; height: number }> } | null = null;
    if (p && typeof f === "function") {
      try {
        b = f();
      } catch {
        b = null;
      }
    }
    const key = b ? b.mode + "|" + b.rects.map((r) => `${Math.round(r.x)},${Math.round(r.y)},${Math.round(r.width)},${Math.round(r.height)}`).join(";") : "";
    if (key === this.touch_buttons_key) return;
    this.touch_buttons_key = key;
    if (!b || b.mode != "turn" || !p.cur_worm || p.mousecontrol || p.wpsheet) return;
    const w = this.hud_focus();
    if (!w) return;
    const st: any = this.stage;
    const gap: number = 4 / (st && st.cssScale > 0 ? st.cssScale : 1);
    if (!b.rects.some((r) => w.x < r.x + r.width + gap && r.x - gap < w.x + w.width && w.y < r.y + r.height + gap && r.y - gap < w.y + w.height)) return;
    p.check_displace(true);
  };

  /**
   * PORT (touch screens): the weapon sheet's touch layout while it is open (ui/battle-sheet.ts: the grid new_interf.render
   * pages the weapons by, the footer row the touch controls' menu info goes to), or null: the original's layout.
   */
  sheet_layout(): { grid: { cols: number; rows: number }; info: { x: number; y: number; width: number; height: number }; covered: boolean } | null {
    const c: any = this._interf && this._interf.cur_screen;
    const n: number = c ? Number(c[0]) || 0 : 0;
    for (let i = 1; i <= n; i++) {
      if (c[i] && c[i].name == "weapon_sheet") return touchSheetLayout(c[i].link);
    }
    return null;
  }

  /**
   * PORT (touch screens): where the battle camera keeps the current worm on a touch screen with the on-screen controls
   * (stage px, presentation only). `keep_left` / `keep_right`: how far from the shown area's side the worm's sprite
   * stays (proc.check_displace): the room the controls' clusters on that side take from it plus CAMERA_GAP. `left` /
   * `right`: how far past the world's left / right limit the camera may go for that (proc.game_displace, its clamp):
   * the same, at most WIDE_MARGIN_X, so that a map click still lies within the area any client's camera can show
   * (runtime/battle-input.ts clickArea, session/online.ts onlineClickBounds: the recorded input stays valid everywhere);
   * the world's limits the rules read stay as they are. The clusters' places count for the whole battle, shown or not
   * (they show from the frame after a turn starts, while the camera is placed at its start; the HUD keeps clear of those
   * places too). All zero without touch controls or without a wide view (a desktop, ?wide=0).
   */
  camera_margin(): { left: number; right: number; keep_left: number; keep_right: number } {
    const st: any = this.stage;
    const f = this.touch_zones;
    if (!st || !st.$visibleArea || typeof f !== "function") return FWP.NO_CAMERA_MARGIN;
    // The clusters' places change with the screen only (a new visible or safe area): computed once per shape.
    const c = this.camera_margin_cache;
    if (c && c.visible === st.$visibleArea && c.safe === st.$safeArea && c.zones === f) return c.margin;
    let zones: Array<{ x: number; y: number; width: number; height: number }> = [];
    try {
      const z = f();
      if (Array.isArray(z)) zones = z;
    } catch {
      zones = [];
    }
    const vr = this.view_rect();
    const mid = (vr.x0 + vr.x1) / 2;
    let left = 0;
    let right = 0;
    for (const z of zones) {
      if (!(z.width > 0 && z.height > 0)) continue;
      if (z.x + z.width / 2 < mid) left = Math.max(left, z.x + z.width - vr.x0);
      else right = Math.max(right, vr.x1 - z.x);
    }
    const keep = (room: number): number => (room > 0 ? Math.ceil(room + FWP.CAMERA_GAP) : 0);
    const margin = Object.freeze({ left: Math.min(WIDE_MARGIN_X, keep(left)), right: Math.min(WIDE_MARGIN_X, keep(right)), keep_left: keep(left), keep_right: keep(right) });
    this.camera_margin_cache = { visible: st.$visibleArea, safe: st.$safeArea, zones: f, margin };
    return margin;
  }

  /** PORT (touch screens): the gap the battle camera keeps between the current worm and a controls' cluster (stage px). */
  static readonly CAMERA_GAP: number = 16;

  private static readonly NO_CAMERA_MARGIN = Object.freeze({ left: 0, right: 0, keep_left: 0, keep_right: 0 });

  private camera_margin_cache: { visible: unknown; safe: unknown; zones: unknown; margin: { left: number; right: number; keep_left: number; keep_right: number } } | null = null;

  /**
   * PORT: a team's name as the battle shows it (its HUD bar, the log's turn lines): in an online battle with the player
   * who plays it and the local player's own team marked (runtime/online-match.ts battleTeamLabel), else its name.
   * Display only: the battle's teams keep their names (nothing the simulation or its hash reads changes).
   */
  team_label(param1: number, param2: any): string {
    const m: any = this.online && this.online.match;
    return m && typeof m.battleTeamLabel == "function" && m.phase != "closed" ? m.battleTeamLabel(Number(param1), String(param2)) : String(param2);
  }

  /**
   * PORT (wide screens): a worm's name and health labels drawn larger with a wide host (ui/worm-labels.ts; `fresh`: the
   * game has just set the boxes' widths), or as the original draws them. Every worm of the battle is scaled again when
   * the screen changes (Event.RESIZE). Drawing only.
   */
  worm_labels(sprite: any, fresh: boolean = false): void {
    scaleWormLabels(sprite, wormLabelScale(this.stage), fresh);
  }

  /**
   * PORT (wide screens): the scale of the battle's markers of moving objects out of view (their distance, 10 px as the
   * original draws it): the worms' labels' (ui/worm-labels.ts), 1 without a wide host. Drawing only.
   */
  distance_box_scale(): number {
    return wormLabelScale(this.stage);
  }

  /**
   * PORT (wide screens): a marker of a moving object out of view (proc.check_displace placed it at the screen's side)
   * moved along that side (`along_x`: a marker at the top or the bottom) to the nearest place clear of the battle's HUD at
   * the top (camera_keep_top) and the touch controls' clusters (touch_zones), within the shown area; left where it is
   * without them (a desktop) or where there is no such place. Presentation only.
   */
  distance_box_clear(param1: any, along_x: boolean): void {
    const st: any = this.stage;
    if (!param1 || !st || !st.$visibleArea || !param1.parent) return;
    let keep: Array<{ x: number; y: number; width: number; height: number }> = this.camera_keep_top();
    if (typeof this.touch_zones === "function" && (typeof this.touch_shown !== "function" || this.touch_shown())) {
      try {
        keep = keep.concat(this.touch_zones() || []);
      } catch {
        // (none)
      }
    }
    if (!keep.length) return;
    const b = param1.getBounds(st);
    const gap = 4 / (st.cssScale > 0 ? st.cssScale : 1);
    const vr = this.view_rect(true);
    const free = (dx: number, dy: number): boolean => {
      const x = b.x + dx;
      const y = b.y + dy;
      if (x < vr.x0 || y < vr.y0 || x + b.width > vr.x1 || y + b.height > vr.y1) return false;
      return !keep.some((k) => x < k.x + k.width + gap && k.x - gap < x + b.width && y < k.y + k.height + gap && k.y - gap < y + b.height);
    };
    if (free(0, 0)) return;
    const reach = along_x ? vr.x1 - vr.x0 : vr.y1 - vr.y0;
    for (let d = 2; d <= reach; d += 2) {
      for (const s of [d, -d]) {
        if (!free(along_x ? s : 0, along_x ? 0 : s)) continue;
        const p0 = param1.parent.globalToLocal(new Point(b.x, b.y));
        const p1 = param1.parent.globalToLocal(new Point(b.x + (along_x ? s : 0), b.y + (along_x ? 0 : s)));
        param1.x += p1.x - p0.x;
        param1.y += p1.y - p0.y;
        return;
      }
    }
  }

  /** PORT (wide screens): the sprites of the running battle's worms. */
  private worm_sprites(): any[] {
    const p: any = this._proc;
    const out: any[] = [];
    if (!p || !p.worms || !this._obj) return out;
    for (let i = 1; i <= (Number(p.worms[0]) || 0); i++) {
      const o: any = p.worms[i] ? this._obj[p.worms[i].fid] : null;
      if (o && o.sprite && o.model == "W") out.push(o.sprite);
    }
    return out;
  }

  /** PORT (wide screens): the labels of every worm of the running battle at the screen's scale (worm_labels). */
  private readonly rescale_worm_labels = (): void => {
    for (const s of this.worm_sprites()) this.worm_labels(s);
  };

  /**
   * PORT (wide screens): the enlarged labels of worms standing close together kept apart (ui/worm-labels.ts), every
   * frame of a battle with a wide host. Drawing only.
   */
  private readonly frame_worm_labels = (): void => {
    if (this._proc && this.stage && (this.stage as any).$visibleArea) separateWormLabels(this.worm_sprites());
  };

  /**
   * PORT (wide screens): the stage rectangles of the battle's HUD at the top that the camera keeps the current worm clear
   * of (proc.check_displace, clear_of_hud: the log's lines shown, the team names and bars, the wind, the timer;
   * ui/wide-screen.ts cameraKeep), none without the wide HUD (a desktop). Presentation only.
   */
  camera_keep_top(): Array<{ x: number; y: number; width: number; height: number }> {
    return this.wide_screen ? this.wide_screen.cameraKeep() : [];
  }

  /**
   * PORT (wide screens): the stage rectangle of the battle's current worm (its sprite: the worm, its name and health
   * above it), or null: the wide screen's battle HUD fades while it covers it (the camera cannot always keep it clear
   * of the HUD: a worm high at the map's ends). Presentation only: it reads the battle, changes nothing.
   */
  hud_focus(): { x: number; y: number; width: number; height: number } | null {
    const p: any = this._proc;
    const w: any = p && p.cur_worm && p.worms ? p.worms[p.cur_worm] : null;
    const o: any = w && w.state == "ok" && this._obj ? this._obj[w.fid] : null;
    const s: any = o && o.sprite;
    if (!s || !s.stage || !s.visible) return null;
    return this.worm_rect(s);
  }

  /**
   * PORT (wide screens): the stage rectangle a worm's sprite shows: the worm, its name and health boxes (not their text
   * fields, which are wider than the boxes, the more so enlarged: ui/worm-labels.ts), or null. Presentation only.
   */
  worm_rect(param1: any): { x: number; y: number; width: number; height: number } | null {
    const s: any = param1;
    if (!s || !s.stage) return null;
    let x0: number = Infinity;
    let y0: number = Infinity;
    let x1: number = -Infinity;
    let y1: number = -Infinity;
    for (let i: number = 0; i < s.numChildren; i++) {
      const c: any = s.getChildAt(i);
      if (!c.visible || c.name == "worm_name" || c.name == "worm_hp") continue;
      const r = c.getBounds(s.stage);
      if (!(r.width > 0 && r.height > 0)) continue;
      x0 = Math.min(x0, r.x);
      y0 = Math.min(y0, r.y);
      x1 = Math.max(x1, r.x + r.width);
      y1 = Math.max(y1, r.y + r.height);
    }
    if (x1 > x0 && y1 > y0) return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    const b = s.getBounds(s.stage);
    return b.width > 0 && b.height > 0 ? { x: b.x, y: b.y, width: b.width, height: b.height } : null;
  }

  /**
   * PORT (wide screens): the part of the game's window the screen shows, in window coordinates: the window itself
   * (0, 0, WINDOW_WIDTH, WINDOW_HEIGHT) without a wide host (headless, a desktop), else wider (Stage.visibleRect;
   * `safe`: its part clear of notches and system bars, Stage.safeRect). Presentation only (the battle camera, the
   * markers of objects out of view): nothing a battle's simulation reads.
   */
  view_rect(safe: boolean = false): { x0: number; y0: number; x1: number; y1: number } {
    const st: any = this.stage;
    const r: any = st && (safe ? (st.$safeArea ?? st.$visibleArea) : st.$visibleArea);
    if (!r) return { x0: 0, y0: 0, x1: this.WINDOW_WIDTH, y1: this.WINDOW_HEIGHT };
    // Whole pixels (the camera stays on whole pixels, as in the original), never less than the window.
    return {
      x0: Math.min(0, Math.ceil(r.x)),
      y0: Math.min(0, Math.ceil(r.y)),
      x1: Math.max(this.WINDOW_WIDTH, Math.floor(r.x + r.width)),
      y1: Math.max(this.WINDOW_HEIGHT, Math.floor(r.y + r.height)),
    };
  }
  TIRED_THRESHOLD: any = 1000;
  loading_screen: any = undefined;
  /** PORT: the trademark notice of the loading splash (src/game/ui/legal-notice.ts). */
  legal_notice: any = null;
  debug_text: any = "";
  _play_mode: any = "";
  _play_id: any = 0;
  _play_zone_stage: any = 0;
  TOP_BY_LEVEL: any = new Array();
  TOP_BY_RATING: any = new Array();
  TOP_BY_ASSAULT: any = new Array();
  top_uids: any = "";
  top_names: any = {};
  INC: any = new Array();
  inc_uids: any = "";
  inc_names: any = {};
  FRIENDS: any = new Array<any>(0, {});
  friends_uids: any = "";
  friends_names: any = {};
  /** The player's clan id (0 = none; clan.mine through the clans tab, runtime/clan-hub.ts). */
  user_clan: any = 0;
  /**
   * PORT: item 8 - the clan of a player by the game's numeric uid ({clanId, name, emblem} or null): filled from what the
   * PORT: server says about players (the tops' entries, the assault zones' owners, the player's own clan), so the
   * PORT: original's return_clan (a search in the fixed clans' member lists, CLANS_PEOPLE_DEF) needs no member lists.
   */
  clan_of_uid: any = {};
  /** PORT: item 8 - the clans of the players (runtime/clan-hub.ts): the clans tab, the card of a clan, the best clans. */
  clans: any = null;
  ASSAULT: any = new Array();
  assault_uids: any = "";
  assault_names: any = {};
  assault_cur_time: any = 0;
  PAPA_HERE: any = false;
  end_game: any = {};
  display_front_world: any = new MovieClip();
  display_obj: any = new MovieClip();
  display_back_world: any = new MovieClip();
  display_interf: any = new MovieClip();
  display_banner: any = new MovieClip();
  display_system: any = new MovieClip();
  display_big_inv: any = new MovieClip();
  splash_error: any = undefined;
  splash_wait: any = undefined;
  splash_debug: any = undefined;
  debug_window: any = undefined;
  splash_big_error: any = undefined;
  main_mode: any = "";
  main_timeout: any = 0;
  main_execute: any = "";
  wait_for_complete: any = false;
  wait_for_avatars: any = false;
  avatar_to_download: any = 0;
  files_preloaded: any = 0;
  FILES_TO_PRELOAD: any = 5;
  FPS: any = 20;
  // PORT: VK removed: `flashVars:Object` (VK application parameters).
  // PORT: VK removed: `VK:EIApiConnection` (VK JS-API bridge, vk/EIApiConnection.as is not ported).
  _world: any = undefined;
  _obj: any = {};
  max_object: any = 0;
  _proc: any = undefined;
  _interf: any = undefined;
  _sound: any = undefined;
  TIRED_EFFECTS: any = undefined;
  STIM_DEF: any = undefined;
  PEACHFUL_BONUS: any = undefined;
  LEVEL_THRESHOLD: any = undefined;
  PERKS_DEF: any = undefined;
  WEAPONS_DEF: any = undefined;
  TEAMS_DEF: any = undefined;
  GUM_DEF: any = undefined;
  MISSION_WEAP_DEF: any = undefined;
  MISSION_DESC: any = undefined;
  ROUL_VER: any = undefined;
  SPEC_DEF: any = undefined;
  TASK_DEF: any = undefined;
  ACHIEV_DEF: any = undefined;
  ACHIEV_PRISE_DEF: any = undefined;
  ACHIEV_RATING_BONUS: any = undefined;
  CRAFT_DEF: any = undefined;
  WORLD_DEF: any = undefined;
  FLAGS_DEF: any = undefined;
  RIPS_DEF: any = undefined;
  IMPL_DEF: any = undefined;
  OBJ_DEF: any = undefined;
  BOOKS_DEF: any = undefined;
  WEAPON_ICONS: any = undefined;
  WP_PLACES_SORT: any = undefined;
  LEAGUE_THRESHOLD: any = undefined;
  COREAN_RANDOM: any = undefined;
  PAPA_HOURS: any = undefined;
  PAPA_WEAPONS: any = undefined;
  PAPA_OBJECTS: any = undefined;
  C_RED: any = 1;
  C_BLUE: any = 2;
  C_GREEN: any = 3;
  C_YELLOW: any = 4;
  C_CYAN: any = 5;
  C_PURPLE: any = 6;
  C_WHITE: any = 7;
  WORM_WALK_MOMENT: any = 5;
  FIRING_START: any = 12;
  TIRED_INC: any = 10;
  user: any = {
    "user_id": 0,
    "name": "",
    "params": "",
    "custom_params": "",
    "unix_time": 0
  };
  map_info: any = new Array();
  player: any = {
    "level": 1,
    "perks": {},
    "stim_behave": {},
    "stim_to_fight": {},
    "weapons": {},
    "settings": {},
    "achiev": {},
    "last_achiev": {},
    "progress": {},
    "quick_weapons": {},
    "flags": {},
    "rip": {},
    "impl": {},
    "objs": {},
    "recipes": []
  };
  temp_player: any = {
    "level": 1,
    "perks": {},
    "stim_behave": {},
    "stim_to_fight": {},
    "weapons": {},
    "settings": {},
    "achiev": {},
    "last_achiev": {},
    "progress": {},
    "quick_weapons": {},
    "flags": {},
    "rip": {},
    "impl": {},
    "objs": {},
    "recipes": []
  };
  saved_stims: any = {};
  get_info: any = {
    "level": 1,
    "perks": {},
    "stim_behave": {},
    "stim_to_fight": {},
    "weapons": {},
    "settings": {},
    "achiev": {},
    "last_achiev": {},
    "progress": {},
    "quick_weapons": {},
    "flags": {},
    "rip": {},
    "impl": {},
    "objs": {},
    "recipes": []
  };
  get_info_uid: any = 0;
  get_info_string: any = "";
  get_roul: any = 0;
  roul_weapon: any = "";
  grave_found: any = "";
  grave_found_type: any = "";
  get_present: any = 0;
  // PORT: design D3 - perk slot purchases of the skill editor ('gold' / 'diam', in order) for skills.commit, and Papa
  // PORT: John's assortment of today as the server opened his shop (papa.enter).
  temp_slot_buys: any[] = [];
  papa_assortment: any = null;
  string_to_save: any = "";
  string_to_save255: any = "";
  world_to_save: any = "";
  execute_after: any = "";
  user_info_mode: any = "";
  image_loaders: any = {};
  images: any = {};
  avdatas: any = {};
  animation: any = false;
  animation_y: any = 0;
  animation_mode: any = "show";
  execute_after_animation: any = "";
  main_map_dl: any = false;
  main_map_dl_id: any = "";
  main_execute_after_map_dl: any = "";
  grave_process: any = 0;
  grave_searching: any = false;
  GSEARCHTIME: any = 500;
  interf_mainframe: any = false;
  auc_weapons: any = { "annihilator": 0 };
  win_auc_weapons: any = { "annihilator": 0 };
  auc_info: any = {
    "user_id": 0,
    // PORT: design D7 - the holder's backend player id ("" = nobody holds it this round), see net_auction_adopt.
    "holder": "",
    "price": 0,
    "last_buy": 0,
    "winners": "",
    "cur_time": 0
  };
  auc_names: any = {};
  KEY_PRESSED: any = "";
  SPACE_PRESSED: boolean = false;
  QUICK_WEAPON_PRESSED: any = "";
  KEYS_ARRAY: any = new Array();
  // PORT: the audio switches are persisted client-side in the "local_data" SharedObject (localStorage in the
  // PORT: browser), so a player who turned music or sound off does not have to do it again after every restart.
  // PORT: The original kept them per session (the VK build reloaded the SWF on every visit anyway).
  get MUSIC(): boolean {
    return this.$audio_pref("music");
  }
  set MUSIC(value: boolean) {
    this.$audio_pref("music", !!value);
  }
  get SOUND(): boolean {
    return this.$audio_pref("sound");
  }
  set SOUND(value: boolean) {
    this.$audio_pref("sound", !!value);
  }
  /** @internal Client-side preferences (SharedObject -> localStorage in the browser); created on first use. */
  $prefs: any = null;
  /** @internal Reads a client preference, or writes and flushes it. */
  pref(key: string, value?: any): any {
    if (!this.$prefs) this.$prefs = SharedObject.getLocal("fwp_prefs");
    if (value === undefined) return this.$prefs.data[key];
    this.$prefs.data[key] = value;
    this.$prefs.flush();
    return value;
  }
  /**
   * PORT: client preferences that belong to the signed-in player, not to the device: the online lobby's memory of the
   * player's room and battle (src/game/runtime/online-lobby.ts), the offer to save the last battle's replay and the
   * decisions on it (src/game/runtime/replay-offer.ts), the players the chat hides (src/game/runtime/chat.ts). Wiped at a sign-out (net_sign_out); the language and the audio
   * switches stay with the device.
   */
  static readonly PLAYER_PREFS: readonly string[] = ["online", REPLAY_OFFER_PREF, CHAT_HIDDEN_PREF];
  /** @internal Deletes client preferences and flushes (a sign-out: FWP.PLAYER_PREFS). */
  pref_delete(keys: readonly string[]): void {
    if (!this.$prefs) this.$prefs = SharedObject.getLocal("fwp_prefs");
    for (const key of keys) delete this.$prefs.data[key];
    this.$prefs.flush();
  }
  /** @internal Reads a persisted audio switch (default: on), or writes and flushes it. */
  $audio_pref(key: string, value?: boolean): boolean {
    if (value === undefined) return this.pref(key) !== false;
    this.pref(key, value);
    return value;
  }
  INIT_STAGES: any = 10;
  // PORT: VK removed: ADMIN_FRIEND (the admin's VK friends got a double daily present).
  USER_NOTES: any = new Array<any>(0, {});
  new_weapon: any = "";
  STORAGE_TRYES: any = 0;
  STORAGE_MAX_TRYES: any = 5;
  // PORT: VK removed: `CURRENT_SID` (VK session id for check_sid).
  OOPS_BANNED: any = false;
  BAN_DESC: any = new Array();
  ADVICES: any = [9, "Огонь не наносит вашим червям никакого урона при защите больше 120 или при Пороге урона больше 0.", "При навыке Защита больше 210 яд не отравляет ваших червяков.", "При навыке Защита больше 270 ваших червей невозможно парализовать.", "При усталости 80% и выше ваши параметры Атаки, Защиты\nи Здоровья ухудшаются.", "Если вас поймали при краже оружия, можно запудрить продавцу мозги или пожаловаться на высокие цены.", "Чем выше уровень сложности, тем больше опыта и золота\nвы получаете при победе.", "Чтобы понизить уровень усталости, отдохните некоторое время или используйте стимулятор.", "Чтобы цены в магазине были меньше, можно взять способность \"Торговец\".", "Рейтинг в игре зависит от количества побед, одержанных сегодня, и от количества вчерашних побед."];
  fwproject_time: any = 0;
  banner_clicked: number = 0;
  read_params_end: boolean = false;
  _assets: any = null;
  private _filesToLoad: any = null;
  private _filesToLoadAmount: number = 0;
  private _filesToLoadPointer: number = 0;
  private _loader: any = null;
  mapLoader: any = undefined;
  mapRequest: any = undefined;
  LoadedBitmap: any = null;
  bug_report: any = "";
  REPLAY: any = false;
  REPLAY_STRING: any = "";
  REPLAY_STRING_PARAMS: any = "";
  REPLAY_STRING_MOVES: any = "";
  REPLAY_STRING_CUR_MOVE: any = "";
  REPLAY_CUR_MOVE: any = new Array();
  replay_play_mode: any = "";
  replay_play_id: any = 0;
  replay_cur_random: any = 0;
  player_perks_saved: any = {};
  player_skill_saved: any = 0;
  player_tired_saved: any = 0;
  player_impl_saved: any = 0;
  test_params: any = "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";

  // PORT: the constructor queued the six embedded sub-SWF ByteArrayAsset classes for Loader.loadBytes;
  // PORT: now it queues AssetLibrary bundle names (docs/architecture.md section 7). Otherwise identical.
  constructor(deps: FWPDeps) {
    super();
    as3.bindMethods(this, FWP.prototype);
    this.deps = deps;
    this.services = deps.services;
    this.DEV_PROFILE = Boolean(deps.devProfile);
    Security.allowDomain("*");
    this._assets = [];
    this._filesToLoad = [];
    this._filesToLoad.push([FWP.ASSET_ID_FWP, "fwp"]);
    this._filesToLoad.push([FWP.ASSET_ID_INTERF, "interf"]);
    this._filesToLoad.push([FWP.ASSET_ID_OBJ, "obj"]);
    this._filesToLoad.push([FWP.ASSET_ID_PROC, "proc"]);
    this._filesToLoad.push([FWP.ASSET_ID_SOUND, "sound"]);
    this._filesToLoad.push([FWP.ASSET_ID_WORLD, "world"]);
    this._filesToLoadAmount = as3.int(this._filesToLoad.length);
    this._filesToLoadPointer = 0;
    this.startAssetLoading();
  }

  // PORT: getLocalStorage/setLocalStorage (SharedObject "local_data" holding the VK-storage string) removed;
  // PORT: the profile is persisted through the backend (net_save / net_load_profile).

  // PORT: Loader.loadBytes(new ASSET_*() as ByteArray) replaced by AssetLibrary.container(bundle); the
  // PORT: startAssetLoading -> assetLoaded -> startAssetLoading loop is kept.
  private startAssetLoading(param1: any = null): void {
    if (this.stage == null) {
      // The original completion was asynchronous (Loader COMPLETE), by which time the document class was on stage;
      // allAssetsLoaded() needs the stage, so wait until the game root has been added to it.
      this.addEventListener(Event.ADDED_TO_STAGE, this.startAssetLoading);
      return;
    }
    this.removeEventListener(Event.ADDED_TO_STAGE, this.startAssetLoading);
    this.assetLoaded({ target: { content: this.deps.library.container(this._filesToLoad[this._filesToLoadPointer][1]) } });
  }

  private assetLoaded(param1: any): void {
    // PORT: the Loader COMPLETE listener is gone; param1.target.content is the AssetLibrary container of the bundle.
    this._assets[this._filesToLoad[this._filesToLoadPointer][0]] = param1.target.content;
    this._filesToLoadPointer = as3.int(this._filesToLoadPointer + 1);
    if (this._filesToLoadPointer == this._filesToLoadAmount) {
      this.allAssetsLoaded();
    } else {
      this.startAssetLoading();
    }
  }

  get container_world(): any {
    return this._assets[FWP.ASSET_ID_WORLD];
  }

  get container_obj(): any {
    return this._assets[FWP.ASSET_ID_OBJ];
  }

  get container_proc(): any {
    return this._assets[FWP.ASSET_ID_PROC];
  }

  get container_interf(): any {
    return this._assets[FWP.ASSET_ID_INTERF];
  }

  /**
   * PORT (touch screens): the place an open battle menu keeps for the touch controls' menu info (their long-press hint,
   * an online battle's clock) in its touch layout: the weapon sheet's footer row (`covered`: a weapon's description
   * over it now), the scanner's room beside its tabs (ui/battle-scanner.ts); null: none (the controls' own place).
   */
  menu_info_slot(): { x: number; y: number; width: number; height: number; covered: boolean } | null {
    const l = this.sheet_layout();
    if (l) return { ...l.info, covered: l.covered };
    const c: any = this._interf && this._interf.cur_screen;
    const n: number = c ? Number(c[0]) || 0 : 0;
    for (let i = 1; i <= n; i++) {
      if (c[i] && c[i].name == "scaner") {
        const a = touchScannerInfo(c[i].link);
        return a ? { ...a, covered: false } : null;
      }
    }
    return null;
  }

  get container_sound(): any {
    return this._assets[FWP.ASSET_ID_SOUND];
  }

  allAssetsLoaded(): any {
    // PORT (wide screens): the original's screens fill the margin a wide screen shows around the stage (ui/wide-screen.ts).
    if (!this.wide_screen && this.stage) {
      this.wide_screen = new WideScreen(this, this.stage, () => [this.container_interf, this._assets[FWP.ASSET_ID_FWP]].filter(Boolean));
      this.stage.addEventListener(Event.RESIZE, this.rescale_worm_labels);
      this.addEventListener(Event.EXIT_FRAME, this.frame_worm_labels);
      this.addEventListener(Event.EXIT_FRAME, this.frame_touch_buttons);
    }
    // PORT (wide screens): the popups shown larger on a phone, over the HUD, as a finger's targets (ui/wide-popups.ts).
    if (!this.wide_popups && this.stage) this.wide_popups = new WidePopups(this, this.stage, () => [this.container_interf].filter(Boolean));
    this.splash_error = this._assets[FWP.ASSET_ID_FWP].get("_splash_error");
    this.splash_wait = this._assets[FWP.ASSET_ID_FWP].get("_splash_waiting");
    this.splash_debug = this._assets[FWP.ASSET_ID_FWP].get("_splash_debug");
    this.debug_window = this._assets[FWP.ASSET_ID_FWP].get("_debug_window");
    this.splash_big_error = this._assets[FWP.ASSET_ID_FWP].get("_splash_big_error");
    this.init_data_arrays();
    this.init_random_numbers();
    // PORT: VK removed: stage.loaderInfo.parameters (flashVars api_id/viewer_id/sid/secret/api_result), the
    // PORT: EIApiConnection instance and the vkontakte.ru context menu are not ported (docs/architecture.md section 5).
    // PORT: api_result was absent in the offline build, so the parsed name was always undefined.
    this.user.name = undefined;
    if (this.user.name == undefined) {
      this.user.name = "Игрок";
    }
    this.debug_text += "user_name: " + this.user.name + "\n";
    // PORT: VK removed: the flashVars.api_id whitelist check (api_id defaulted to 4329578, so it always passed).
    // PORT: The LOCAL_TEST start (a local profile from test_params instead of the network) is gone: the state comes
    // PORT: from the backend (net_boot); test_params stays as the legacy save fixture of the importer tests.
    this.addChild(this.display_back_world);
    this.addChild(this.display_obj);
    this.addChild(this.display_front_world);
    this.addChild(this.display_interf);
    this.addChild(this.display_banner);
    this.addChild(this.display_system);
    this.addChild(this.display_big_inv);
    this.display_big_inv.addChild(this._assets[FWP.ASSET_ID_FWP].get("empty_button"));
    this.display_big_inv.visible = false;
    this.display_big_inv.getChildAt(0).useHandCursor = false;
    this.splash_debug.close_debug.addEventListener(MouseEvent.CLICK, (param1: any): any => {
      this.display_system.removeChild(this.splash_debug);
    });
    this.addEventListener(Event.ENTER_FRAME, this._main);
    this.loading_screen = this._assets[FWP.ASSET_ID_FWP].get("splash_loading");
    this.display_system.addChild(this.loading_screen);
    this.loading_screen.loading.text = "0%";
    this.loading_screen.stat_text.text = "Загрузка...";
    this.loading_screen.bar.mask.width = 0;
    this.loading_screen.advice.advice_text.text = this.ADVICES[this.true_random(1, this.ADVICES[0])];
    this.loading_screen.stat_text.text = "Инициализация...";
    this.loading_screen.loading.text = "0 / " + this.INIT_STAGES;
    // PORT: the trademark notice at the splash's bottom, clear of its bar, status line and advice (ui/legal-notice.ts).
    const splash: any = this.loading_screen;
    this.legal_notice = addSplashNotice(splash, () => [splash.bar, splash.loading, splash.stat_text, splash.advice]);
    this.net_boot();
    this.stage!.tabChildren = false;
  }

  _main(param1: any): any {
    let _loc2_: any = undefined;
    if (Boolean(this.wait_for_avatars) || Boolean(this.main_map_dl)) {
      return;
    }
    if (this.animation) {
      this.animation_y += 50;
      this._interf.cur_screen[this._interf.cur_screen[0]].link.y = this.animation_y;
      if (this.animation_y >= 0 && this.animation_mode == "show") {
        this.animation = false;
        this.set_invisible_button(false);
      }
      if (this.animation_y >= 600 && this.animation_mode == "hide") {
        this.animation = false;
        this.set_invisible_button(false);
        this._interf.remove_instant();
        if (this.execute_after_animation == "new level") {
          this._interf.init_new_level();
        }
        if (this.execute_after_animation == "surprise") {
          this._interf.init("surprise");
        }
        this.execute_after_animation = "";
      }
    }
    if (this.grave_searching) {
      ++this.grave_process;
      this._interf.render_grave_search();
      _loc2_ = 1;
      if (this.player.perks["gsearch"]) {
        _loc2_ = this.get_perk_effect("gsearch", this.player.perks.gsearch, "");
      }
      if (this.grave_process >= this.GSEARCHTIME * _loc2_) {
        this.grave_process = 0;
        this.grave_searching = false;
        this._interf.finish_grave_search();
        this.set_invisible_button(false);
      }
    }
    if (this.main_mode == "PRELOADING") {
      return;
    }
    // PORT: backend events and the connection indicator (non-blocking, see net_frame).
    this.net_frame();
    // PORT: watchdog for the async backend bridge: the original retried a stalled request after main_timeout;
    // PORT: a call whose promise has not settled after NET_CALL_TIMEOUT_MS of the bridge clock (wall-clock time, not
    // PORT: frames: a fast-ticking headless client must not time out real network calls) is failed and retried.
    if (this.main_mode == "NET_INIT" && this.wait_for_complete && this.net_pending) {
      if (this.net_pending.attempts > 0) {
        this.net_show_retry();
      }
      if (this.net_now() - this.net_started_at > (this.net_pending.timeout || FWP.NET_CALL_TIMEOUT_MS)) {
        this.net_fail({ "code": "UNAVAILABLE", "message": "backend call timed out: " + this.main_execute });
      }
      return;
    }
    if (this.main_mode == "NET_INIT" && !this.wait_for_complete) {
      // PORT: the retry countdown (the original's main_timeout) runs on the bridge clock with backoff (net_fail).
      if (this.net_pending && this.net_retry_at > 0) {
        this.net_show_retry();
        if (this.net_now() >= this.net_retry_at) {
          this.net_send();
        }
      }
      return;
    }
    // PORT: design D5 - the online lobby's server events and the online battle (runtime/online-lobby.ts): an online
    // PORT: battle runs a tick only when its input is known (it waits for the other players, catches up with several
    // PORT: ticks per frame), so this frame's tick is its own.
    if (this.online && this.online.frame()) {
      return;
    }
    // PORT: item 8 - the clans' server events and the answers of the clans tab (runtime/clan-hub.ts), in the game loop.
    if (this.clans) {
      this.clans.frame();
    }
    if (this.main_mode == "IDLE") {
      if (this.interf_mainframe) {
        this._interf.mainframe_animation();
      }
      return;
    }
    if (this.main_mode == "LOCAL_GAME") {
      this._proc.ongoing();
    }
  }

  init_data_arrays(): any {
    // PORT: the tables are built by the shared rules module (src/game/rules/defs.ts createGameDefs: the same data
    // PORT: factories in the same order, the same WEAPONS_DEF post-processing - wpclass by level, hidden weapon prices
    // PORT: / 0.6 - and the TEAMS_DEF literal moved to data/TEAMS_DEF.ts), so the game and the backend rules can never
    // PORT: diverge. Fresh copies on every call, as before.
    const defs: any = createGameDefs();
    this.WORLD_DEF = defs.WORLD_DEF;
    this.TIRED_EFFECTS = defs.TIRED_EFFECTS;
    this.STIM_DEF = defs.STIM_DEF;
    this.FLAGS_DEF = defs.FLAGS_DEF;
    this.RIPS_DEF = defs.RIPS_DEF;
    this.IMPL_DEF = defs.IMPL_DEF;
    this.OBJ_DEF = defs.OBJ_DEF;
    this.BOOKS_DEF = defs.BOOKS_DEF;
    this.SPEC_DEF = defs.SPEC_DEF;
    this.PEACHFUL_BONUS = defs.PEACHFUL_BONUS;
    this.LEVEL_THRESHOLD = defs.LEVEL_THRESHOLD;
    this.LEAGUE_THRESHOLD = defs.LEAGUE_THRESHOLD;
    this.PERKS_DEF = defs.PERKS_DEF;
    this.WEAPONS_DEF = defs.WEAPONS_DEF;
    this.GUM_DEF = defs.GUM_DEF;
    this.MISSION_WEAP_DEF = defs.MISSION_WEAP_DEF;
    this.TEAMS_DEF = defs.TEAMS_DEF;
    this.MISSION_DESC = defs.MISSION_DESC;
    this.ROUL_VER = defs.ROUL_VER;
    // PORT: item 8 - no CLANS_DEF: the 14 fixed clans of the original are gone, the players make their clans.
    this.ACHIEV_DEF = defs.ACHIEV_DEF;
    this.ACHIEV_PRISE_DEF = defs.ACHIEV_PRISE_DEF;
    this.ACHIEV_RATING_BONUS = defs.ACHIEV_RATING_BONUS;
    this.CRAFT_DEF = defs.CRAFT_DEF;
    this.TASK_DEF = defs.TASK_DEF;
    this.COREAN_RANDOM = defs.COREAN_RANDOM;
    this.PAPA_HOURS = defs.PAPA_HOURS;
    this.PAPA_WEAPONS = defs.PAPA_WEAPONS;
    this.PAPA_OBJECTS = defs.PAPA_OBJECTS;
    this.WEAPON_ICONS = defs.WEAPON_ICONS;
    for (const k of as3.keys(defs.BAN_DESC)) {
      this.BAN_DESC[k] = defs.BAN_DESC[k];
    }
    this.WP_PLACES_SORT = defs.WP_PLACES_SORT;
  }

  gfr(param1: number): any {
    param1 = Number(param1);
    return [0, param1, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  }

  // PORT: moved verbatim to data/TEAMS_DEF.ts (get_team), which builds TEAMS_DEF without the game root.
  get_team(param1: any): any {
    return team_from_def(param1);
  }

  get_weapon_flags(param1: any, param2: number): any {
    param1 = as3.str(param1);
    param2 = Number(param2);
    let _loc5_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = false;
    if (param2 == 1) {
      _loc4_ = true;
    }
    _loc5_ = {
      "wid": "",
      "wptype": "",
      "crit": 0,
      "ALLOWLUCK": true,
      "w_bounce": 0.4,
      "w_seconds": 3000,
      "defaultvx": 0,
      "defaultvy": 0,
      "FRAME": 24,
      "SPECIALFRAME": 0,
      "SOUND": "",
      "FORCE": false,
      "FULLVECTOR": false,
      "HALFVECTOR": false,
      "PARTVECTOR": false,
      "ROPEVECTOR": false,
      "ALLOWCROSSHAIR": false,
      "ALLOWLASERSIGHT": false,
      "TAKEMOVE": false,
      "NOESCAPE": false,
      "FROMJETPACK": false,
      "FROMJUMP": false,
      "NOWAITFORREADY": false,
      "THROWING": false,
      "ALLOWSETTIME": false,
      "ALLOWSETBOUNCE": false,
      "MAKEPROJECTILE": false,
      "MULTIPLESHOT": false,
      "SHOTSAMOUNT": 1,
      "ONFALLINGONLY": false,
      "AUTOONFALLING": false,
      "IMPACT": false,
      "MOUSECONTROL": false,
      "HMISSILE_TYPE": false,
      "TELEPORT_TYPE": false,
      "SPECIAL": "",
      "NOHIDINGWEAPON": false,
      "INSTANTTHINK": false,
      "BULLETAMOUNT": 0,
      "BULLETINTERVAL": 0,
      "IDLEANIMATE": false,
      "AUTOLASER": 0,
      "STRIKEAMOUNT": 5
    };
    _loc5_.wid = param1;
    _loc5_.wptype = this.WEAPONS_DEF[param1].wptype;
    _loc5_.wpclass = this.WEAPONS_DEF[param1].wpclass;
    _loc5_.crit = this.WEAPONS_DEF[param1].crit;
    _loc5_.unfail = this.WEAPONS_DEF[param1].unfail;
    _loc5_.spec_mod = this.WEAPONS_DEF[param1].spec_mod;
    _loc5_.ap_mod = this.WEAPONS_DEF[param1].ap_mod;
    if (param1 == "bazooka" || param1 == "unfail_bazooka" || param1 == "fire_bazooka" || param1 == "poison_bazooka" || param1 == "ap_bazooka" || param1 == "howitzer" || param1 == "upg_mortar" || param1 == "nuclear_bomb" || param1 == "nuclear_bomb2" || param1 == "ap_bazooka2" || param1 == "rj46" || param1 == "multip14" || param1 == "multip17" || param1 == "multip83" || param1 == "kenton_bazooka") {
      _loc5_.FORCE = true;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.SOUND = "fire";
    }
    if (param1 == "grenade" || param1 == "unfail_grenade" || param1 == "plasma_grenade" || param1 == "pulse_grenade" || param1 == "holy_hand_grenade" || param1 == "super_hhg" || param1 == "cluster_bomb" || param1 == "unfail_cluster_bomb" || param1 == "putty_grenade" || param1 == "molotov" || param1 == "banana_bomb" || param1 == "unfail_banana_bomb" || param1 == "super_banana_bomb" || param1 == "mega_banana_bomb" || param1 == "dl19" || param1 == "gravity_grenade" || param1 == "rainbow_burst" || param1 == "infinity_rainbow" || param1 == "skyfire" || param1 == "kenton_grenade") {
      _loc5_.FORCE = true;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.THROWING = true;
      if (param1 != "molotov" && param1 != "gravity_grenade" && param1 != "holy_hand_grenade" && param1 != "putty_grenade") {
        _loc5_.ALLOWSETTIME = true;
        _loc5_.ALLOWSETBOUNCE = true;
      }
      _loc5_.SOUND = "throwing";
    }
    if (param1 == "mortar") {
      _loc5_.defaultvx = 16;
      _loc5_.defaultvy = 16;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.SOUND = "fire";
    }
    if (param1 == "multi_bazooka") {
      _loc5_.defaultvx = 16;
      _loc5_.defaultvy = 16;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.MULTIPLESHOT = true;
      _loc5_.SHOTSAMOUNT = 4;
      _loc5_.SOUND = "fire";
    }
    if (param1 == "mine" || param1 == "unfail_mine" || param1 == "fire_mine" || param1 == "plasma_mine" || param1 == "pulse_mine" || param1 == "nuclear_mine" || param1 == "dynamite" || param1 == "unfail_dynamite" || param1 == "dynamite_bunch" || param1 == "sheep" || param1 == "unfail_sheep" || param1 == "mine_sheep" || param1 == "mine_sheep2" || param1 == "old_woman" || param1 == "unfail_old_woman" || param1 == "fire_sheep" || param1 == "poison_sheep") {
      _loc5_.defaultvx = -1;
      _loc5_.defaultvy = -1;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.THROWING = true;
      _loc5_.SOUND = "throwing";
    }
    if (param1 == "prod" || param1 == "shocker") {
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "prod";
    }
    if (param1 == "firepunch") {
      _loc5_.FRAME = 28;
      _loc5_.FROMJUMP = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "firepunch";
    }
    if (param1 == "fireball") {
      _loc5_.FRAME = 28;
      _loc5_.defaultvx = -3;
      _loc5_.defaultvy = 0;
      _loc5_.FROMJUMP = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.SPECIAL = "fireball";
    }
    if (param1 == "kamikaze") {
      _loc5_.FRAME = 28;
      _loc5_.TAKEMOVE = true;
      _loc5_.NOESCAPE = true;
      _loc5_.SPECIAL = "kamikaze";
    }
    if (param1 == "baseball_bat" || param1 == "power_bat") {
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "bat";
      _loc5_.SOUND = "baseball_bat";
    }
    if (param1 == "battle_axe" || param1 == "power_axe") {
      _loc5_.FRAME = 31;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "axe";
    }
    if (param1 == "blow_torch") {
      _loc5_.FRAME = 33;
      _loc5_.PARTVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "blow_torch";
      _loc5_.SOUND = "blow_torch";
    }
    if (param1 == "pneumatic_drill") {
      _loc5_.FRAME = 34;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "pneumatic_drill";
      _loc5_.SOUND = "drill";
    }
    if (param1 == "parachute") {
      _loc5_.FRAME = 25;
      _loc5_.FROMJUMP = true;
      _loc5_.ONFALLINGONLY = true;
      _loc5_.AUTOONFALLING = true;
      _loc5_.SPECIAL = "parachute";
    }
    if (param1 == "pistol" || param1 == "uzi" || param1 == "unfail_uzi" || param1 == "peacemaker" || param1 == "hearts_ace" || param1 == "minigun" || param1 == "unfail_minigun" || param1 == "air_minigun" || param1 == "bozar" || param1 == "bozar16" || param1 == "bozar24s" || param1 == "avenger" || param1 == "sniper_rifle" || param1 == "rad_rifle") {
      _loc5_.defaultvx = 40;
      _loc5_.defaultvy = 40;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.ALLOWLASERSIGHT = true;
      if (param1 == "bozar" || param1 == "bozar16" || param1 == "bozar24s") {
        _loc5_.AUTOLASER = 5;
      }
      if (param1 == "sniper_rifle" || param1 == "rad_rifle") {
        _loc5_.AUTOLASER = 5;
      }
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "bullets";
      if (param1 == "pistol") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 5;
      } else if (param1 == "uzi" || param1 == "unfail_uzi") {
        _loc5_.BULLETINTERVAL = 300;
        _loc5_.BULLETAMOUNT = 10;
      } else if (param1 == "minigun" || param1 == "unfail_minigun") {
        _loc5_.BULLETINTERVAL = 160;
        _loc5_.BULLETAMOUNT = 20;
      } else if (param1 == "air_minigun") {
        _loc5_.BULLETINTERVAL = 100;
        _loc5_.BULLETAMOUNT = 30;
      } else if (param1 == "bozar" || param1 == "bozar16" || param1 == "bozar24s") {
        _loc5_.BULLETINTERVAL = 100;
        _loc5_.BULLETAMOUNT = 20;
      } else if (param1 == "peacemaker") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 2;
      } else if (param1 == "hearts_ace") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 2;
      } else if (param1 == "avenger") {
        _loc5_.BULLETINTERVAL = 100;
        _loc5_.BULLETAMOUNT = 30;
      } else if (param1 == "sniper_rifle") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 1;
      } else if (param1 == "rad_rifle") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 1;
      }
      if (_loc4_) {
        _loc5_.BULLETAMOUNT += this.get_bullet_amount(param1);
      }
    }
    if (param1 == "beretta" || param1 == "pulse_pistol" || param1 == "luiston" || param1 == "de_lameter" || param1 == "laser_at_rifle" || param1 == "plasma_at_rifle" || param1 == "ultra_rifle" || param1 == "ec_caws" || param1 == "ec_panhammer" || param1 == "void_melody" || param1 == "annihilator") {
      _loc5_.defaultvx = 40;
      _loc5_.defaultvy = 40;
      if (param1 == "laser_at_rifle" || param1 == "plasma_at_rifle") {
        _loc5_.AUTOLASER = 5;
      }
      if (param1 == "ultra_rifle" || param1 == "annihilator") {
        _loc5_.AUTOLASER = 6;
      }
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.ALLOWLASERSIGHT = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      if (param1 == "sniper_rifle") {
        _loc5_.SOUND = "sniper";
      }
      if (param1 == "rad_rifle") {
        _loc5_.SOUND = "sniper";
      }
      if (param1 == "beretta") {
        _loc5_.SOUND = "pistol";
      }
      if (param1 == "gauss_pistol") {
        _loc5_.SOUND = "gauss_pistol";
      }
      if (param1 == "gauss_rifle") {
        _loc5_.SOUND = "gauss_rifle";
      }
      if (param1 == "unfail_gauss_rifle") {
        _loc5_.SOUND = "gauss_rifle";
      }
      if (param1 == "pulse_pistol") {
        _loc5_.SOUND = "pulse_pistol";
      }
      if (param1 == "luiston") {
        _loc5_.SOUND = "luiston";
      }
      if (param1 == "de_lameter") {
        _loc5_.SOUND = "de_lameter";
      }
      if (param1 == "laser_at_rifle") {
        _loc5_.SOUND = "laser_at_rifle";
      }
      if (param1 == "plasma_at_rifle") {
        _loc5_.SOUND = "plasma_at_rifle";
      }
      if (param1 == "ultra_rifle") {
        _loc5_.SOUND = "ultra_rifle";
      }
      if (param1 == "ec_caws" || param1 == "ec_panhammer" || param1 == "void_melody" || param1 == "annihilator") {
        _loc5_.SOUND = "ec";
      }
    }
    if (param1 == "shotgun" || param1 == "hunting_rifle") {
      _loc5_.defaultvx = 40;
      _loc5_.defaultvy = 40;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.ALLOWLASERSIGHT = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.MULTIPLESHOT = true;
      _loc5_.SHOTSAMOUNT = 2;
      _loc5_.SOUND = "shotgun_3x";
      if (_loc4_) {
        _loc5_.SHOTSAMOUNT += this.get_bullet_amount(param1);
      }
    }
    if (param1 == "air_strike" || param1 == "napalm_strike" || param1 == "mine_strike" || param1 == "banana_strike" || param1 == "holy_strike" || param1 == "orbit_strike") {
      _loc5_.SPECIAL = "air_strike";
      _loc5_.MOUSECONTROL = true;
      _loc5_.TAKEMOVE = true;
      if (param1 == "banana_strike" || param1 == "holy_strike") {
        _loc5_.STRIKEAMOUNT = 3;
      }
      if (param1 != "orbit_strike") {
        _loc5_.SOUND = "air_strike";
      } else {
        _loc5_.SOUND = "orbit";
      }
    }
    if (param1 == "skip_go") {
      _loc5_.FRAME = 29;
      _loc5_.TAKEMOVE = true;
      _loc5_.IDLEANIMATE = true;
      _loc5_.NOESCAPE = true;
    }
    if (param1 == "laser_sight" || param1 == "low_gravity" || param1 == "fast_walk" || param1 == "add_time") {
      _loc5_.FRAME = 25;
      _loc5_.INSTANTTHINK = true;
    }
    if (param1 == "girder" || param1 == "girder_pack") {
      _loc5_.FRAME = 25;
      _loc5_.SPECIAL = "girder";
      _loc5_.MOUSECONTROL = true;
      _loc5_.TAKEMOVE = true;
      if (param1 == "girder_pack") {
        _loc5_.MULTIPLESHOT = true;
        _loc5_.SHOTSAMOUNT = 5;
      }
      _loc5_.SOUND = "girder";
    }
    if (param1 == "jet_pack" || param1 == "upg_jet_pack" || param1 == "ag_pack") {
      _loc5_.FRAME = 25;
      _loc5_.SPECIAL = "jet_pack";
      _loc5_.NOHIDINGWEAPON = true;
    }
    if (param1 == "teleport" || param1 == "upg_teleport" || param1 == "upg_teleport2") {
      _loc5_.SPECIAL = "teleport";
      _loc5_.NOHIDINGWEAPON = true;
      _loc5_.MOUSECONTROL = true;
      _loc5_.TAKEMOVE = param1 == "teleport";
      _loc5_.SOUND = "teleport";
    }
    if (param1 == "choose_worm") {
      _loc5_.FRAME = 25;
      _loc5_.SPECIAL = "choose_worm";
    }
    if (param1 == "medikit") {
      _loc5_.FRAME = 25;
      _loc5_.INSTANTTHINK = true;
    }
    if (param1 == "super_medikit") {
      _loc5_.FRAME = 25;
      _loc5_.INSTANTTHINK = true;
    }
    if (param1 == "invisibility") {
      _loc5_.FRAME = 25;
      _loc5_.INSTANTTHINK = true;
    }
    if (param1 == "gauss_pistol2") {
      _loc5_.defaultvx = 40;
      _loc5_.defaultvy = 40;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.ALLOWLASERSIGHT = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.MAKEPROJECTILE = true;
      _loc5_.MULTIPLESHOT = true;
      _loc5_.SHOTSAMOUNT = 2;
      _loc5_.SOUND = "gauss_pistol2";
      if (_loc4_) {
        _loc5_.SHOTSAMOUNT += this.get_bullet_amount(param1);
      }
    }
    if (param1 == "laser_pistol" || param1 == "laser_pistol2" || param1 == "laser_rifle" || param1 == "unfail_laser_rifle" || param1 == "laser_minigun" || param1 == "plasma_pistol" || param1 == "plasma_rifle" || param1 == "unfail_plasma_rifle" || param1 == "plasma_minigun" || param1 == "gauss_pistol" || param1 == "gauss_rifle" || param1 == "unfail_gauss_rifle" || param1 == "gauss_minigun" || param1 == "turbo_rifle" || param1 == "vindicator") {
      _loc5_.defaultvx = 40;
      _loc5_.defaultvy = 40;
      _loc5_.FULLVECTOR = true;
      _loc5_.ALLOWCROSSHAIR = true;
      _loc5_.ALLOWLASERSIGHT = true;
      _loc5_.TAKEMOVE = true;
      _loc5_.SPECIAL = "bullets";
      if (param1 == "vindicator") {
        _loc5_.AUTOLASER = 6;
      }
      if (param1 == "gauss_rifle" || param1 == "unfail_gauss_rifle") {
        _loc5_.AUTOLASER = 6;
      }
      if (param1 == "laser_pistol" || param1 == "plasma_pistol") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 5;
      } else if (param1 == "laser_pistol2") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 7;
      } else if (param1 == "laser_rifle" || param1 == "unfail_laser_rifle" || param1 == "plasma_rifle" || param1 == "unfail_plasma_rifle") {
        _loc5_.BULLETINTERVAL = 300;
        _loc5_.BULLETAMOUNT = 10;
      } else if (param1 == "laser_minigun" || param1 == "plasma_minigun") {
        _loc5_.BULLETINTERVAL = 160;
        _loc5_.BULLETAMOUNT = 20;
      } else if (param1 == "gauss_pistol") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 1;
      } else if (param1 == "gauss_rifle") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 1;
      } else if (param1 == "unfail_gauss_rifle") {
        _loc5_.BULLETINTERVAL = 640;
        _loc5_.BULLETAMOUNT = 1;
      } else if (param1 == "gauss_minigun") {
        _loc5_.BULLETINTERVAL = 160;
        _loc5_.BULLETAMOUNT = 10;
      } else if (param1 == "turbo_rifle" || param1 == "vindicator") {
        _loc5_.BULLETINTERVAL = 300;
        _loc5_.BULLETAMOUNT = 5;
      }
      if (_loc4_) {
        _loc5_.BULLETAMOUNT += this.get_bullet_amount(param1);
      }
    }
    return _loc5_;
  }

  get_object_flags(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    _loc2_ = {
      "W": 6,
      "H": 6,
      "ADV_W": -1,
      "ADV_H": -1,
      "FLAT": false,
      "ULTRA": false,
      "GRAVITY": false,
      "WIND": false,
      "DIAMETER": 0,
      "DAMAGE": 0,
      "EXPLODEDISPLACE": 0,
      "REFLECT": false,
      "ADVANCEDCALC": false,
      "GRAVITYMULTIPLY": 1,
      "WINDMULTIPLY": 1,
      "IMPACTMULTIPLY": 1,
      "INITEXPLODE": false,
      "INSTANTEXPLODE": false,
      "TIMINGEXPLODE": false,
      "RESTINGEXPLODE": false,
      "FUSETIMEOUT": 0,
      "TIMEOUT": 0,
      "MULTIPLEXPLOSIONS": 0,
      "VROTATING": false,
      "SROTATING": false,
      "DIRECTROTATING": false,
      "BOUNCE_X": 0.7,
      "BOUNCE_Y": 0.2,
      "SPEEDVX": 0,
      "SPEEDVY": 0,
      "CONSTANTSPEED": false,
      "LESSSPEEDDECREASE": false,
      "FLATPROJECTILE": false,
      "MOMENTAL": false,
      "SHOW_TIMER": false,
      "EXHAUST": false,
      "DOUBLEEXHAUST": false,
      "EXHAUSTONHIT": false,
      "EXHAUSTTYPE": "",
      "EXHAUSTTIME": 1,
      "SPECIAL": "",
      "BASESPEED": 0,
      "BREAKING": false,
      "BREAKINGTYPE": "",
      "BREAKINGAMOUNT": 0,
      "POISONONEXPLODE": false,
      "POISONONEXPLODERAD": false,
      "POISONAMOUNT": 10,
      "FIREONEXPLODE": false,
      "FIREGENRANDOMSPEED": false,
      "FIREAMOUNT": 0,
      "NAPALMONEXPLODE": false,
      "MOVING": false,
      "MOVINGJUMP": false,
      "JUMPHEIGHT": -6,
      "NOEXPLODESOUND": false,
      "NOEXPLODEANIMATION": false,
      "NOLANDSCAPEMODIFY": false,
      "WAVEEXPLODE": false,
      "DAMAGEWAVE": false,
      "KENTONWAVE": false,
      "DAMAGEWAVEDMG": 0,
      "PARALYZING": false,
      "PARATIME": 0,
      "PUTTY": false,
      "ANNIHILATOR": false
    };
    if (param1 == "worm") {
      _loc2_.W = 6;
      _loc2_.H = 16;
      _loc2_.GRAVITY = true;
    }
    if (param1 == "fire" || param1 == "napalm_fire") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.NOEXPLODESOUND = true;
      _loc2_.NOEXPLODEANIMATION = true;
      _loc2_.NOLANDSCAPEMODIFY = true;
    }
    if (param1 == "bazooka" || param1 == "unfail_bazooka" || param1 == "fire_bazooka" || param1 == "poison_bazooka" || param1 == "ap_bazooka" || param1 == "howitzer" || param1 == "multi_bazooka" || param1 == "nuclear_bomb" || param1 == "nuclear_bomb2" || param1 == "ap_bazooka2" || param1 == "rj46" || param1 == "multip14" || param1 == "multip17" || param1 == "multip83" || param1 == "kenton_bazooka") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      _loc2_.GRAVITY = true;
      _loc2_.WIND = true;
      _loc2_.EXHAUST = true;
      _loc2_.EXHAUSTTYPE = "standard_exhaust";
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      if (param1 == "fire_bazooka") {
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
      }
      if (param1 == "poison_bazooka") {
        _loc2_.POISONONEXPLODE = true;
      }
      if (param1 == "ap_bazooka") {
        _loc2_.MULTIPLEXPLOSIONS = 3;
      }
      if (param1 == "ap_bazooka2") {
        _loc2_.MULTIPLEXPLOSIONS = 5;
        _loc2_.DAMAGE = 140;
      }
      if (param1 == "nuclear_bomb") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 600;
        _loc2_.POISONONEXPLODE = true;
      }
      if (param1 == "nuclear_bomb2") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 900;
        _loc2_.POISONONEXPLODERAD = true;
      }
      if (param1 == "howitzer") {
        _loc2_.DIAMETER = 100;
        _loc2_.DAMAGE = 3360;
      }
      if (param1 == "rj46") {
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.DIAMETER = 150;
        _loc2_.DAMAGE = 200;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
      }
      if (param1 == "multip14") {
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.DIAMETER = 150;
        _loc2_.DAMAGE = 250;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
      }
      if (param1 == "multip17") {
        _loc2_.NAPALMONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.DIAMETER = 150;
        _loc2_.DAMAGE = 300;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
        _loc2_.MULTIPLEXPLOSIONS = 3;
        _loc2_.DAMAGEWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 60;
      }
      if (param1 == "multip83") {
        _loc2_.NAPALMONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.DIAMETER = 150;
        _loc2_.DAMAGE = 600;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 6;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 3;
        _loc2_.MULTIPLEXPLOSIONS = 4;
        _loc2_.DAMAGEWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 70;
      }
      if (param1 == "kenton_bazooka") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 1200;
        _loc2_.KENTONWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 50;
      }
      _loc2_.ADVANCEDCALC = true;
    }
    if (param1 == "grenade" || param1 == "unfail_grenade" || param1 == "plasma_grenade" || param1 == "pulse_grenade" || param1 == "cluster_bomb" || param1 == "unfail_cluster_bomb" || param1 == "putty_grenade" || param1 == "banana_bomb" || param1 == "unfail_banana_bomb" || param1 == "super_banana_bomb" || param1 == "mega_banana_bomb" || param1 == "dl19" || param1 == "rainbow_burst" || param1 == "infinity_rainbow" || param1 == "skyfire" || param1 == "kenton_grenade") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      if (param1 == "cluster_bomb" || param1 == "unfail_cluster_bomb") {
        _loc2_.DAMAGE = 15;
      }
      if (param1 == "banana_bomb" || param1 == "unfail_banana_bomb" || param1 == "super_banana_bomb" || param1 == "mega_banana_bomb") {
        _loc2_.DAMAGE = 75;
        _loc2_.DIAMETER = 150;
      }
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.SHOW_TIMER = true;
      if (param1 == "plasma_grenade") {
        _loc2_.DAMAGE = 110;
      }
      if (param1 == "pulse_grenade") {
        _loc2_.DAMAGE = 110;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
      }
      if (param1 == "dl19") {
        _loc2_.DAMAGE = 200;
        _loc2_.DIAMETER = 150;
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
      }
      if (param1 == "rainbow_burst") {
        _loc2_.DAMAGE = 250;
        _loc2_.DIAMETER = 150;
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
      }
      if (param1 == "infinity_rainbow") {
        _loc2_.DAMAGE = 300;
        _loc2_.DIAMETER = 150;
        _loc2_.NAPALMONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 4;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
        _loc2_.DAMAGEWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 50;
      }
      if (param1 == "skyfire") {
        _loc2_.DAMAGE = 800;
        _loc2_.DIAMETER = 150;
        _loc2_.NAPALMONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "rj_bomblet";
        _loc2_.BREAKINGAMOUNT = 6;
        _loc2_.POISONONEXPLODERAD = true;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 3;
        _loc2_.DAMAGEWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 80;
      }
      if (param1 == "kenton_grenade") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 1200;
        _loc2_.KENTONWAVE = true;
        _loc2_.DAMAGEWAVEDMG = 50;
      }
      if (param1 == "cluster_bomb" || param1 == "unfail_cluster_bomb") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "bomblet";
        _loc2_.BREAKINGAMOUNT = 5;
      }
      if (param1 == "banana_bomb" || param1 == "unfail_banana_bomb" || param1 == "super_banana_bomb") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "banana";
        _loc2_.BREAKINGAMOUNT = 5;
        if (param1 == "banana_bomb" || param1 == "unfail_banana_bomb") {
          _loc2_.BREAKINGAMOUNT = 3;
        }
      }
      if (param1 == "mega_banana_bomb") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "breaking_banana";
        _loc2_.BREAKINGAMOUNT = 4;
      }
      if (param1 == "putty_grenade") {
        _loc2_.DAMAGE = 100;
        _loc2_.PUTTY = true;
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "putty_bomblet";
        _loc2_.BREAKINGAMOUNT = 8;
        _loc2_.TIMEOUT = 8000;
      }
    }
    if (param1 == "molotov") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 45;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.FIREONEXPLODE = true;
      _loc2_.FIREAMOUNT = 30;
    }
    if (param1 == "gravity_grenade") {
      _loc2_.DIAMETER = 600;
      _loc2_.DAMAGE = 1;
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.SHOW_TIMER = true;
      _loc2_.NOLANDSCAPEMODIFY = true;
      _loc2_.EXPLODEDISPLACE = 50;
      _loc2_.IMPACTMULTIPLY = 0.65;
      _loc2_.WAVEEXPLODE = true;
    }
    if (param1 == "holy_hand_grenade" || param1 == "super_hhg" || param1 == "holy_strike") {
      _loc2_.DIAMETER = 200;
      _loc2_.DAMAGE = 160;
      _loc2_.GRAVITY = true;
      _loc2_.SROTATING = true;
      if (param1 == "holy_strike") {
        _loc2_.INSTANTEXPLODE = true;
        _loc2_.ADVANCEDCALC = true;
      } else {
        _loc2_.REFLECT = true;
        _loc2_.RESTINGEXPLODE = true;
      }
      if (param1 == "super_hhg") {
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
      }
    }
    if (param1 == "mortar" || param1 == "upg_mortar") {
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 15;
      if (param1 == "upg_mortar") {
        _loc2_.DAMAGE = 25;
      }
      _loc2_.GRAVITY = true;
      _loc2_.EXHAUST = true;
      _loc2_.EXHAUSTTYPE = "standard_exhaust";
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.BREAKING = true;
      _loc2_.BREAKINGTYPE = "bomblet";
      _loc2_.BREAKINGAMOUNT = 5;
    }
    if (param1 == "pistol" || param1 == "uzi" || param1 == "unfail_uzi" || param1 == "hunting_rifle" || param1 == "sniper_rifle" || param1 == "rad_rifle" || param1 == "peacemaker" || param1 == "shotgun" || param1 == "minigun" || param1 == "unfail_minigun" || param1 == "air_minigun" || param1 == "bozar" || param1 == "avenger" || param1 == "beretta" || param1 == "hearts_ace" || param1 == "bozar16" || param1 == "bozar24s") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      if (param1 == "pistol") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 6;
      } else if (param1 == "uzi" || param1 == "unfail_uzi") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 5;
      } else if (param1 == "sniper_rifle" || param1 == "peacemaker") {
        _loc2_.DIAMETER = 50;
        _loc2_.DAMAGE = 250;
      } else if (param1 == "hearts_ace") {
        _loc2_.DIAMETER = 50;
        _loc2_.DAMAGE = 300;
      } else if (param1 == "rad_rifle") {
        _loc2_.DIAMETER = 50;
        _loc2_.DAMAGE = 900;
      } else if (param1 == "shotgun") {
        _loc2_.DIAMETER = 50;
        _loc2_.DAMAGE = 25;
      } else if (param1 == "hunting_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 8;
      } else if (param1 == "minigun" || param1 == "unfail_minigun") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 4;
      } else if (param1 == "air_minigun") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 12;
      } else if (param1 == "bozar") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 26;
      } else if (param1 == "bozar16") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 100;
      } else if (param1 == "bozar24s") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 125;
      } else if (param1 == "avenger") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 40;
      } else if (param1 == "beretta") {
        _loc2_.DIAMETER = 50;
        _loc2_.DAMAGE = 75;
      }
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
      if (param1 == "hunting_rifle") {
        _loc2_.IMPACTMULTIPLY = 0.25;
      }
    }
    if (param1 == "air_strike") {
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 25;
      _loc2_.GRAVITY = true;
      _loc2_.EXHAUST = true;
      _loc2_.EXHAUSTTYPE = "standard_exhaust";
      _loc2_.EXHAUSTTIME = 4;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      _loc2_.ADVANCEDCALC = true;
    }
    if (param1 == "mine_strike") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.SROTATING = true;
      _loc2_.TIMEOUT = 3000;
      if (this.player.perks["miner"]) {
        _loc2_.TIMEOUT = 1000 * this.get_perk_effect("miner", this.player.perks.miner, "") + 1;
      }
    }
    if (param1 == "banana_strike") {
      _loc2_.DAMAGE = 75;
      _loc2_.DIAMETER = 150;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.SHOW_TIMER = true;
      _loc2_.BREAKING = true;
      _loc2_.BREAKINGTYPE = "banana";
      _loc2_.BREAKINGAMOUNT = 3;
      _loc2_.ADVANCEDCALC = true;
    }
    if (param1 == "mine" || param1 == "unfail_mine" || param1 == "fire_mine" || param1 == "plasma_mine" || param1 == "pulse_mine" || param1 == "nuclear_mine") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.TIMEOUT = 3000;
      if (this.player.perks["miner"]) {
        _loc2_.TIMEOUT = 1000 * this.get_perk_effect("miner", this.player.perks.miner, "") + 1;
      }
      _loc2_.SROTATING = true;
      if (param1 == "fire_mine") {
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
      }
      if (param1 == "plasma_mine") {
        _loc2_.DAMAGE = 110;
      }
      if (param1 == "pulse_mine") {
        _loc2_.DAMAGE = 110;
        _loc2_.PARALYZING = true;
        _loc2_.PARATIME = 2;
      }
      if (param1 == "nuclear_mine") {
        _loc2_.DIAMETER = 150;
        _loc2_.DAMAGE = 450;
        _loc2_.POISONONEXPLODE = true;
      }
    }
    if (param1 == "dynamite" || param1 == "unfail_dynamite" || param1 == "dynamite_bunch") {
      _loc2_.DIAMETER = 150;
      _loc2_.DAMAGE = 75;
      _loc2_.EXPLODEDISPLACE = 18;
      _loc2_.BOUNCE_X = 0;
      _loc2_.BOUNCE_Y = 0.4;
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.TIMEOUT = 5000;
      if (param1 == "dynamite_bunch") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "dynamite_bomblet";
        _loc2_.BREAKINGAMOUNT = 3;
      }
    }
    if (param1 == "sheep" || param1 == "unfail_sheep" || param1 == "fire_sheep" || param1 == "poison_sheep" || param1 == "mine_sheep" || param1 == "mine_sheep2") {
      _loc2_.W = 6;
      _loc2_.H = 16;
      _loc2_.DIAMETER = 150;
      _loc2_.DAMAGE = 75;
      _loc2_.EXPLODEDISPLACE = 18;
      _loc2_.GRAVITY = true;
      _loc2_.FUSETIMEOUT = 19000;
      _loc2_.TIMEOUT = 20000;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.CONTROLEXPLODE = true;
      _loc2_.MOVING = true;
      _loc2_.MOVINGJUMP = true;
      if (param1 == "fire_sheep") {
        _loc2_.FIREONEXPLODE = true;
        _loc2_.FIREAMOUNT = 30;
      }
      if (param1 == "poison_sheep") {
        _loc2_.POISONONEXPLODE = true;
      }
      if (param1 == "mine_sheep") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "mine";
        _loc2_.BREAKINGAMOUNT = 5;
      }
      if (param1 == "mine_sheep2") {
        _loc2_.BREAKING = true;
        _loc2_.BREAKINGTYPE = "plasma_mine";
        _loc2_.BREAKINGAMOUNT = 8;
      }
    }
    if (param1 == "old_woman" || param1 == "unfail_old_woman") {
      _loc2_.W = 6;
      _loc2_.H = 16;
      _loc2_.DIAMETER = 150;
      _loc2_.DAMAGE = 75;
      _loc2_.EXPLODEDISPLACE = 18;
      _loc2_.GRAVITY = true;
      _loc2_.FUSETIMEOUT = 19000;
      _loc2_.TIMEOUT = 20000;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.CONTROLEXPLODE = true;
      _loc2_.MOVING = true;
    }
    if (param1 == "fireball") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.ADV_W = 20;
      _loc2_.ADV_H = 20;
      _loc2_.DIAMETER = 15;
      _loc2_.NOLANDSCAPEMODIFY = true;
      _loc2_.DAMAGE = 30;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.DIRECTROTATING = true;
      _loc2_.TIMEOUT = 1152;
      if (this.player.perks["dragonball"]) {
        _loc2_.TIMEOUT *= this.get_perk_effect("dragonball", this.player.perks.dragonball, "");
      }
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
      _loc2_.IMPACTMULTIPLY = 10;
    }
    if (param1 == "laser_pistol" || param1 == "laser_pistol2" || param1 == "laser_rifle" || param1 == "unfail_laser_rifle" || param1 == "laser_minigun" || param1 == "plasma_pistol" || param1 == "plasma_rifle" || param1 == "unfail_plasma_rifle" || param1 == "plasma_minigun" || param1 == "pulse_pistol" || param1 == "luiston" || param1 == "de_lameter" || param1 == "turbo_rifle" || param1 == "laser_at_rifle" || param1 == "plasma_at_rifle" || param1 == "orbit_strike" || param1 == "ultra_rifle" || param1 == "vindicator") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      if (param1 == "laser_pistol" || param1 == "laser_rifle" || param1 == "unfail_laser_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 24;
      } else if (param1 == "laser_pistol2") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 30;
      } else if (param1 == "laser_minigun") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 32;
      } else if (param1 == "laser_at_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 250;
      } else if (param1 == "plasma_pistol" || param1 == "plasma_rifle" || param1 == "unfail_plasma_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 35;
      } else if (param1 == "plasma_at_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 450;
      } else if (param1 == "plasma_minigun") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 60;
      } else if (param1 == "pulse_pistol") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 900;
      } else if (param1 == "luiston" || param1 == "orbit_strike") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 1300;
      } else if (param1 == "de_lameter") {
        _loc2_.DIAMETER = 200;
        _loc2_.DAMAGE = 1300;
        if (this._world.chosen_map != "mission7" && this._world.chosen_map != "mission8" && this._world.chosen_map != "mission9" && this._world.chosen_map != "mission10") {
          _loc2_.MULTIPLEXPLOSIONS = 3;
          _loc2_.DAMAGE = 1850;
        }
      } else if (param1 == "turbo_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 1400;
      } else if (param1 == "ultra_rifle" || param1 == "vindicator") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 2000;
        _loc2_.ULTRA = true;
      }
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
    }
    if (param1 == "gauss_pistol" || param1 == "gauss_pistol2" || param1 == "gauss_rifle" || param1 == "unfail_gauss_rifle" || param1 == "gauss_minigun") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      _loc2_.DIAMETER = 24;
      if (param1 == "gauss_pistol") {
        _loc2_.DAMAGE = 350;
      } else if (param1 == "gauss_pistol2") {
        _loc2_.DAMAGE = 375;
      } else if (param1 == "gauss_minigun") {
        _loc2_.DAMAGE = 375;
      } else if (param1 == "gauss_rifle" || param1 == "unfail_gauss_rifle") {
        _loc2_.DIAMETER = 24;
        _loc2_.DAMAGE = 600;
      }
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
    }
    if (param1 == "ec_caws" || param1 == "ec_panhammer") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 2000;
      if (param1 == "ec_panhammer") {
        _loc2_.DAMAGE = 2500;
      }
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
    }
    if (param1 == "void_melody") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 15000;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
      _loc2_.KENTONWAVE = true;
      _loc2_.DAMAGEWAVEDMG = 50;
    }
    if (param1 == "annihilator") {
      _loc2_.W = 2;
      _loc2_.H = 2;
      _loc2_.DIAMETER = 300;
      _loc2_.DAMAGE = 12000;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.VROTATING = true;
      _loc2_.ADVANCEDCALC = true;
      _loc2_.NOEXPLODESOUND = true;
      _loc2_.ANNIHILATOR = true;
    }
    if (param1 == "bomblet") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 15;
      _loc2_.GRAVITY = true;
      _loc2_.EXHAUST = true;
      _loc2_.EXHAUSTTYPE = "standard_exhaust";
      _loc2_.EXHAUSTTIME = 4;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
    }
    if (param1 == "rj_bomblet") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 85;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
    }
    if (param1 == "banana") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 75;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
    }
    if (param1 == "breaking_banana") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 75;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.BREAKING = true;
      _loc2_.BREAKINGTYPE = "mini_banana";
      _loc2_.BREAKINGAMOUNT = 3;
    }
    if (param1 == "mini_banana") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
    }
    if (param1 == "dynamite_bomblet") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 150;
      _loc2_.DAMAGE = 75;
      _loc2_.GRAVITY = true;
      _loc2_.INSTANTEXPLODE = true;
      _loc2_.SROTATING = true;
    }
    if (param1 == "putty_bomblet") {
      _loc2_.W = 4;
      _loc2_.H = 4;
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 100;
      _loc2_.GRAVITY = true;
      _loc2_.TIMINGEXPLODE = true;
      _loc2_.SROTATING = true;
      _loc2_.REFLECT = true;
      _loc2_.PUTTY = true;
      _loc2_.TIMEOUT = 3000;
    }
    if (param1 == "worm_explode" || param1 == "worm_explode_100" || param1 == "worm_explode_150" || param1 == "worm_explode_200") {
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 1;
      if (param1 == "worm_explode") {
        _loc2_.DAMAGE = 25;
      }
      if (param1 == "worm_explode_100") {
        _loc2_.DIAMETER = 100;
      }
      if (param1 == "worm_explode_150") {
        _loc2_.DIAMETER = 150;
      }
      if (param1 == "worm_explode_200") {
        _loc2_.DIAMETER = 200;
      }
      _loc2_.INITEXPLODE = true;
    }
    if (param1 == "howitzer_impact") {
      _loc2_.DIAMETER = 50;
      _loc2_.DAMAGE = 500;
      _loc2_.INITEXPLODE = true;
    }
    if (param1 == "turbo_rifle_fail") {
      _loc2_.DIAMETER = 24;
      _loc2_.DAMAGE = 7000;
      _loc2_.INITEXPLODE = true;
    }
    if (param1 == "vindicator_fail") {
      _loc2_.DIAMETER = 24;
      _loc2_.DAMAGE = 10000;
      _loc2_.INITEXPLODE = true;
    }
    if (param1 == "null_explode") {
      _loc2_.DIAMETER = 0;
      _loc2_.DAMAGE = 0;
      _loc2_.INITEXPLODE = true;
    }
    if (param1 == "drum") {
      _loc2_.DIAMETER = 25;
      _loc2_.DAMAGE = 50;
    }
    if (param1 == "crate") {
      _loc2_.DIAMETER = 100;
      _loc2_.DAMAGE = 50;
      _loc2_.GRAVITY = true;
      _loc2_.REFLECT = true;
      _loc2_.W = 10;
      _loc2_.H = 10;
    }
    return _loc2_;
  }

  // PORT: VK removed: init_context_menu() built a context menu whose items opened vkontakte.ru pages.

  // PORT: VK removed: openPeachful() opened http://vkontakte.ru/peachful.

  // PORT: VK removed: openGroup() opened http://vkontakte.ru/fwproject_group.

  set_invisible_button(param1: boolean): any {
    param1 = Boolean(param1);
    this.display_big_inv.visible = param1;
  }

  add_interf(): any {
    this._interf = new new_interf(this);
  }

  add_proc(): any {
    this._proc = new proc(this);
  }

  add_sound(): any {
    this._sound = new sound(this);
  }

  add_world(param1: any, param2: any, param3: any, param4: any): any {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    param3 = as3.str(param3);
    param4 = as3.str(param4);
    this._world = new world(this, param1, param2, param3, param4);
  }

  add_obj(param1: any, param2: any, param3: any, param4: any): number {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    param3 = as3.str(param3);
    ++this.max_object;
    this._obj["obj" + this.max_object] = this.display_obj.addChild(new obj(this, param1, param2, param3, param4));
    if (param1 == "P" || param1 == "M" || param1 == "W" || param1 == "C") {
      this._interf.add_distance_box(this.max_object, param1);
    }
    return Number(this.max_object);
  }

  remove_obj(param1: any): any {
    this.display_obj.removeChild(param1);
  }

  // ---------------------------------------------------------------- PORT: own backend bridge
  // PORT: The original string protocol (net_command/net_success/main_execute: "get user id", "get storage",
  // PORT: "set sid", "prove sid", "save storage", ...) and the offline build's VK-shaped local stub are replaced by
  // PORT: async calls to the injected GameBackend (src/game/services.ts; docs/architecture.md sections 5-6).
  // PORT: The game's own control flow is kept: main_mode "NET_INIT" + wait_for_complete freeze _main while a call is
  // PORT: pending, a failed retryable call is retried after a countdown (the original NET_INIT main_timeout; now a
  // PORT: wall-clock backoff, see net_fail), and the continuations (net_initialized, success_saved, execute_after) are
  // PORT: the original ones.
  // PORT: Removed without replacement (VK-only): VK profiles/avatars/friends, admin-friend bonus, VK "votes"
  // PORT: payments and balance, wall posts, sid/multi-window check, the VK-uid auction (already disabled in the
  // PORT: offline build) and the 2018 VK-uid snapshots of tops/clans/assault zones.

  /**
   * Runs one backend call inside the NET_INIT state; `done` receives the result (failures: see net_fail).
   * `requestTimeoutMs`: the per-request timeout `call` passes to GameServices (long server-side work); the watchdog
   * then waits for the whole time the transport needs to fail it (netCallTimeoutMs). Default: the transport's.
   */
  net_run(label: any, call: () => Promise<any>, done: (result: any) => void, requestTimeoutMs?: number): any {
    this.main_execute = label;
    this.net_pending = { "label": label, "call": call, "done": done, "attempts": 0, "internal": 0, "reauths": 0, "reason": "offline", "token": 0, "timeout": netCallTimeoutMs(requestTimeoutMs) };
    this.net_send();
  }

  /**
   * PORT: design D3 - the server owns the game state: a meta-game operation of the interface (buy, sell, craft, a
   * PORT: claim, a setting, ...) is the backend command `param1` (a player.command op, e.g. "shop.buy") with the
   * PORT: arguments `param2` the handler collected from the interface. Blocking like init_save_params (splash_wait,
   * PORT: NET_INIT, watchdog); the local state is never changed before the server answered. On success the game adopts
   * PORT: the canonical state the server returns (apply_state, revision), `param4.done(result, outcome)` copies what the
   * PORT: screens read from the op's result into the interface memory (it may change execute_after), and the game goes
   * PORT: on with success_saved(`param3` = execute_after). A refusal (VALIDATION_FAILED: a precondition the server
   * PORT: checks) re-loads the stored profile, calls `param4.refused(reason, details)` and shows a short message.
   * PORT: `param4.timeoutMs`: per-request timeout of long server-side work. A lost reply is retried with the same
   * PORT: operation id (GameServices.command), so the server never applies it twice.
   */
  net_command(param1: any, param2: any = {}, param3: any = "render", param4: any = null): any {
    let _loc5_: any = param4 || {};
    this.display_system.addChild(this.splash_wait);
    this.execute_after = param3;
    this.net_run("command " + param1, () => this.services.command(param1, param2, _loc5_.timeoutMs ? { "timeoutMs": _loc5_.timeoutMs } : {}).catch((error: any) => {
      if (netErrorCode(error) == "VALIDATION_FAILED") {
        return { "refused": error };
      }
      throw error;
    }), (outcome: any) => {
      if (outcome && outcome.refused) {
        this.net_command_refused(param1, outcome.refused, _loc5_);
        return;
      }
      this.net_adopt_command(outcome);
      if (_loc5_.done) {
        _loc5_.done(outcome.result, outcome);
      }
      this.success_saved();
    }, _loc5_.timeoutMs);
  }

  /** Starts (again) the pending call. */
  net_send(): any {
    let _loc1_: any = this.net_pending;
    if (!_loc1_) {
      return;
    }
    this.net_attempt(_loc1_, _loc1_.call, true, (result: any) => {
      this.net_pending = null;
      this.main_mode = "IDLE";
      this.net_call_recovered(_loc1_);
      _loc1_.done(result);
    });
  }

  /**
   * One asynchronous step of the pending call `param1` (the call itself or a re-authentication). A failure counts
   * only while this attempt is the current one; a late success of an earlier attempt of the same call (the watchdog
   * gave up on it, a retry is waiting) is still the call's result when `param3`.
   */
  net_attempt(param1: any, param2: () => any, param3: boolean, param4: (result: any) => void): any {
    const _loc5_: any = ++param1.token;
    this.main_mode = "NET_INIT";
    this.wait_for_complete = true;
    this.net_retry_at = 0;
    this.net_started_at = this.net_now();
    let _loc6_: Promise<any>;
    try {
      _loc6_ = Promise.resolve(param2());
    } catch (error: any) {
      _loc6_ = Promise.reject(error);
    }
    _loc6_.then((result: any) => {
      if (this.net_pending !== param1 || (param1.token !== _loc5_ && !(param3 && param1.call === param2))) {
        return;
      }
      this.wait_for_complete = false;
      param4(result);
    }, (error: any) => {
      if (this.net_pending !== param1 || param1.token !== _loc5_) {
        return;
      }
      this.net_fail(error);
    });
  }

  /**
   * PORT: failures are classified (src/game/runtime/net-policy.ts) instead of being retried forever: an unreachable,
   * PORT: overloaded or failing server is retried with backoff and a visible countdown (mutations keep their request
   * PORT: id, GameServices.request); UNAUTHORIZED re-authenticates and repeats the call (a different player restarts
   * PORT: the game); BANNED shows the original ban splash; a CONFLICT of a profile save reloads the profile instead of
   * PORT: the original fatal "one window" error; anything else stops with a readable message and a restart button.
   */
  net_fail(param1: any): any {
    let _loc2_: any = this.net_pending;
    as3.trace("backend call failed: " + this.main_execute + ": " + (param1 && param1.message ? param1.message : String(param1)));
    if (!_loc2_) {
      return;
    }
    ++_loc2_.token; // the failed attempt is over: its late failure is ignored
    this.wait_for_complete = false;
    if (netErrorCode(param1) == "INTERNAL") {
      ++_loc2_.internal;
    }
    let _loc3_: any = classifyNetFailure(param1, _loc2_.internal, _loc2_.reauths);
    if (_loc3_.action == "retry") {
      ++_loc2_.attempts;
      _loc2_.reason = _loc3_.reason;
      this.main_mode = "NET_INIT";
      this.net_retry_at = this.net_now() + netRetryDelayMs(_loc2_.attempts);
      if (this.loading_screen) {
        this.loading_screen.stat_text.text = "Нет связи с сервером, повтор...";
      }
      this.net_show_retry();
      return;
    }
    if (_loc3_.action == "reauth") {
      this.net_reauth(_loc2_);
      return;
    }
    if (_loc3_.action == "conflict" && _loc2_.label == "save storage") {
      this.net_conflict_reload(_loc2_);
      return;
    }
    if (_loc3_.code == "UNAUTHORIZED" && this.net_accounts()) {
      this.net_session_ended();
      return;
    }
    this.net_stop();
    if (_loc3_.action == "banned") {
      this.net_show_ban(_loc3_.ban);
      return;
    }
    if (_loc3_.action == "version") {
      this.net_error(MSG_VERSION_MISMATCH);
      return;
    }
    this.net_error(_loc3_.code == "UNAUTHORIZED" ? MSG_SESSION_EXPIRED : netErrorText(_loc3_.code));
  }

  /** Bridge clock in ms (see FWPDeps.netClock). */
  net_now(): number {
    return this.deps && this.deps.netClock ? this.deps.netClock() : as3.now();
  }

  /**
   * UNAUTHORIZED (expired or revoked access): the stored session is renewed (auth.resume / auth.refresh), then the call
   * runs again. PORT: with the game's accounts a session that cannot be renewed any more (the refresh token expired or
   * the session was ended) is over: the game restarts into the sign-in screen, which says so (net_session_ended). A
   * guest (headless hosts, tests) gets a new session for the stored guest account instead.
   */
  net_reauth(param1: any): any {
    ++param1.reauths;
    const accounts = this.net_accounts();
    const renew = accounts
      ? () =>
          this.services.ensureSession().catch((error: any) => {
            if (netErrorCode(error) == "UNAUTHORIZED") {
              return { "ended": true };
            }
            throw error;
          })
      : () => this.services.ensureSession();
    this.net_attempt(param1, renew, false, (identity: any) => {
      if (identity && identity.ended) {
        this.net_session_ended();
        return;
      }
      if (this.user.player_id && identity && identity.playerId && identity.playerId != this.user.player_id) {
        // The stored account is gone (e.g. its device token was revoked): the game state in memory belongs to
        // another player, so the game starts over with the new one.
        this.net_stop();
        this.net_error(MSG_PLAYER_CHANGED);
        this.net_restart("player_changed");
        return;
      }
      this.net_send();
    });
  }

  /**
   * PORT: the player's session ended and cannot be renewed (UNAUTHORIZED that the backend services could not refresh):
   * nothing of this run may survive, so the game restarts cleanly into the sign-in screen with a "session expired"
   * message. A host without a restart (tools) shows the error splash instead.
   */
  net_session_ended(): any {
    this.net_stop();
    // The account signed in on another device (one active session per account): the sign-in screen says so and its
    // button signs in here again; nothing signs in by itself.
    const replaced = !!(this.services && this.services.sessionReplaced);
    if (this.deps && this.deps.restart) {
      this.net_forget_player();
      this.net_restart(replaced ? "session_replaced" : "session_expired");
      return;
    }
    this.net_error(replaced ? MSG_SESSION_REPLACED : MSG_SESSION_EXPIRED);
  }

  /**
   * PORT: the server ended this session because the account signed in (or resumed another session) on another device:
   * the game stops at once, whatever it was doing (an online battle included: the server treats the player as it treats
   * any lost connection), and - with the game's accounts - restarts into the sign-in screen with that message. Before
   * the player is known (the boot, the sign-in screen) the boot's own path reports it.
   */
  net_session_replaced(): any {
    if (!this.user.player_id || this.auth_screen) {
      return;
    }
    if (this.net_accounts()) {
      this.net_session_ended();
      return;
    }
    this.net_stop();
    this.net_error(MSG_SESSION_REPLACED);
  }

  /** Profile save CONFLICT (another window or device saved first): adopt the stored profile and go on. */
  net_conflict_reload(param1: any): any {
    let _loc2_: any = this.loading_screen != null;
    this.net_run("reload profile", () => this.services.loadProfile(), (profile: any) => {
      this.net_adopt_profile(profile);
      if (_loc2_) {
        // During the boot chain the continuation only goes on to the next boot stage.
        param1.done({ "revision": this.profile_revision });
        return;
      }
      // The local change that failed to save is dropped; the screen shows the stored data.
      this.execute_after = "";
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      this.refresh_temp_params();
      if (this._interf) {
        this._interf.render();
      }
      this.net_notice(MSG_PROFILE_CONFLICT, 6000);
    });
  }

  /**
   * Reload of the stored profile after a change by another connection (profile.changed, see net_frame). `param1`:
   * say so on screen (false: a reload the player did not cause by another window, e.g. an operator's test setup).
   */
  net_reload_profile(param1: boolean = true): any {
    if (this.splash_wait) {
      this.display_system.addChild(this.splash_wait);
    }
    this.net_run("reload profile", () => this.services.loadProfile(), (profile: any) => {
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      this.net_adopt_profile(profile);
      this.refresh_temp_params();
      if (this._interf) {
        this._interf.render();
      }
      if (param1) {
        this.net_notice(MSG_PROFILE_UPDATED, 3000);
      }
    });
  }

  /** Applies a loaded profile (LoadedProfile) to the running game, as the boot does (net_load_profile, net_initialized). */
  net_adopt_profile(param1: any): any {
    this.profile_revision = param1.revision;
    if (param1.displayName) {
      this.user.name = param1.displayName;
    }
    if (param1.gameState != null && param1.gameState.format == GAME_STATE_FORMAT) {
      this.apply_state(param1.gameState);
      this.player.level.value = this.get_player_level(this.player.exp.value);
    }
    // PORT: L2 - the facts of the server's session entry that the original kept in memory (FWP.modify_papa).
    if (param1.volatile) {
      this.PAPA_HERE = Boolean(param1.volatile.papaHere);
    }
    if (param1.serverTime) {
      this.user.unix_time = param1.serverTime;
    }
  }

  /** PORT: design D3 - adopts the canonical state of a command's response (CommandOutcome) as the load does. */
  net_adopt_command(param1: any): any {
    this.profile_revision = param1.revision;
    if (param1.serverTime) {
      this.user.unix_time = param1.serverTime;
    }
    if (param1.gameState != null && param1.gameState.format == GAME_STATE_FORMAT) {
      this.apply_state(param1.gameState);
      this.player.level.value = this.get_player_level(this.player.exp.value);
    }
  }

  /**
   * PORT: design D3 - the server refused command `param1` (VALIDATION_FAILED `param2`): nothing changed on the server,
   * PORT: and nothing was changed locally. The stored profile is loaded again (another window may have changed it),
   * PORT: the screen is drawn from it, and a short message says why.
   */
  net_command_refused(param1: any, param2: any, param3: any): any {
    let _loc4_: any = param2 && param2.details && typeof param2.details.reason == "string" ? param2.details.reason : "";
    as3.trace("command refused: " + param1 + ": " + _loc4_);
    this.execute_after = "";
    this.net_run("reload profile", () => this.services.loadProfile(), (profile: any) => {
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      this.net_adopt_profile(profile);
      this.refresh_temp_params();
      if (param3 && param3.refused) {
        param3.refused(_loc4_, param2 && param2.details ? param2.details.details : null);
      }
      if (this._interf) {
        this._interf.render();
      }
      this.net_notice(commandRefusedText(_loc4_), 5000);
    });
  }

  /** Ends the pending call without a result (the failure is reported by the caller). */
  net_stop(): any {
    this.net_pending = null;
    this.wait_for_complete = false;
    this.net_retry_at = 0;
    if (this.net_overlay) {
      this.net_overlay.setRetry(null);
    }
    if (this.splash_wait && this.splash_wait.parent == this.display_system) {
      this.display_system.removeChild(this.splash_wait);
    }
    this.main_mode = "IDLE";
  }

  /** A failure the game cannot recover from: the system error splash with a restart button (Enter/Space too). */
  net_error(param1: any): any {
    let _loc2_: any = undefined;
    if (this.loading_screen) {
      this.loading_screen.stat_text.text = "Ошибка сервера";
    }
    _loc2_ = this.system_error(param1);
    addRestartButton(_loc2_, MSG_RESTART, () => this.net_restart("error"));
    if (!this.net_restart_armed && this.stage) {
      this.net_restart_armed = true;
      this.stage.addEventListener(KeyboardEvent.KEY_DOWN, this.net_restart_key);
    }
    this.OOPS_BANNED = true;
  }

  net_restart_key(param1: any): any {
    if (param1.keyCode == 13 || param1.keyCode == 32) {
      this.net_restart("error");
    }
  }

  /** Restarts the game from scratch through the host (FWPDeps.restart), saying why (RestartReason). */
  net_restart(param1: RestartReason = "error"): any {
    // A restart ends this run: e.g. the ended session a background request sees right after the account was deleted
    // must not turn the sign-in screen's "account deleted" into "session expired".
    if (this.net_restart_reason) {
      return;
    }
    if (this.deps && this.deps.restart) {
      this.net_restart_reason = param1;
      this.deps.restart(param1);
    }
  }

  /** Retry countdown of the pending call on the overlay (and a click on it retries at once). */
  net_show_retry(): any {
    let _loc1_: any = this.net_pending;
    if (!_loc1_) {
      return;
    }
    let _loc2_: any = this.wait_for_complete ? 0 : Math.max(1, Math.ceil((this.net_retry_at - this.net_now()) / 1000));
    this.net_ui().setRetry(netRetryText(_loc1_.reason, _loc2_), this.wait_for_complete ? null : MSG_NET_RETRY_NOW);
  }

  /** The connection overlay, created when there is first something to show. */
  net_ui(): any {
    if (!this.net_overlay) {
      this.net_overlay = new NetOverlay();
      this.net_overlay.onRetryClick = () => {
        if (this.net_pending && !this.wait_for_complete && this.main_mode == "NET_INIT") {
          this.net_send();
        }
      };
    }
    return this.net_overlay;
  }

  /** The pending call succeeded: hide its countdown (after retries: say that the connection is back). */
  net_call_recovered(param1: any): any {
    if (!this.net_overlay) {
      return;
    }
    this.net_overlay.setRetry(null);
    if (param1.attempts > 0) {
      this.net_notice(MSG_CONNECTION_RESTORED, 3000);
      if (this.loading_screen) {
        this.loading_screen.stat_text.text = "Инициализация...";
      }
    }
  }

  /** A short message over the game for `param2` ms; `param3`: DISPLAY text (translated already, names in it). */
  net_notice(param1: any, param2: number, param3: boolean = false): any {
    this.net_ui().showNotice(param1, this.net_now() + param2, param3);
  }

  /**
   * PORT: the server changed the stored game state outside a command of this window (an ad bonus it granted): the
   * PORT: profile is loaded again and adopted, `param2` runs, and the game goes on with success_saved(`param1`).
   */
  net_reload_state(param1: any, param2: (() => void) | null = null): any {
    this.display_system.addChild(this.splash_wait);
    this.execute_after = param1;
    this.net_run("reload profile", () => this.services.loadProfile(), (profile: any) => {
      this.net_adopt_profile(profile);
      this.refresh_temp_params();
      if (param2) {
        param2();
      }
      this.success_saved();
    });
  }

  /**
   * PORT: an opt-in rewarded ad the player chose (runtime/monetization-ui.ts): the host shows it and waits for the
   * PORT: server's grant; `param3(result)` then reloads the state. The screen stays blocked while it runs; a closed
   * PORT: ad changes nothing, a verification still on its way is credited later by the server (a notice says so).
   */
  net_ad(param1: any, param2: any, param3: (result: any) => void): any {
    const ads = this.deps && this.deps.monetization ? this.deps.monetization.ads : null;
    if (!ads || this.ad_busy) {
      return;
    }
    this.ad_busy = true;
    this.display_system.addChild(this.splash_wait);
    void ads.watch(param1, param2).then((r: any) => {
      this.ad_busy = false;
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      if (r.status == "granted") {
        param3(r.result);
        return;
      }
      if (r.status == "delayed") {
        this.net_notice(MSG_AD_DELAYED, 6000);
      } else if (r.status == "failed") {
        this.net_notice(MSG_AD_FAILED, 4000);
      }
      if (this._interf) {
        this._interf.render();
      }
    });
  }

  /**
   * PORT: buys a product of the store (the purchase dialog of the pay screen, runtime/monetization-ui.ts): the
   * PORT: platform's purchase flow and the server's credit (player.command 'store.redeem'); the game adopts the
   * PORT: credited state. `param2` (the dialog) gets the store's answer and says it itself; without it the answer is a
   * PORT: notice. The skeleton of the shop answers 'unavailable': nothing charged, nothing credited.
   */
  net_store_buy(param1: any, param2: ((r: any) => void) | null = null): any {
    const store = this.deps && this.deps.monetization ? this.deps.monetization.store : null;
    if (!store || this.ad_busy) {
      if (param2) {
        param2({ "status": "failed", "reason": "busy" });
      }
      return;
    }
    this.ad_busy = true;
    // (the dialog shows its own "please wait")
    if (!param2) {
      this.display_system.addChild(this.splash_wait);
    }
    void store.buy(String(param1)).then((r: any) => {
      this.ad_busy = false;
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      if (param2) {
        param2(r);
      }
      if (r.status == "credited") {
        this.net_adopt_command(r.outcome);
        this.refresh_temp_params();
        if (this._interf) {
          this._interf.PLAYER_DIAM = this.player.diam.value;
          this._interf.DIAM_TO_TRANS = 0;
        }
        this.net_notice(MSG_STORE_CREDITED, 4000);
      } else if (param2) {
        // (said in the dialog)
      } else if (r.status == "pending") {
        this.net_notice(MSG_STORE_PENDING, 6000);
      } else if (r.status == "failed") {
        this.net_notice(MSG_STORE_FAILED, 5000);
      } else if (r.status == "unavailable") {
        this.net_notice(MSG_STORE_SOON, 5000);
      }
      if (this._interf) {
        this._interf.render();
      }
    });
  }

  /** Event queue, connection indicator and overlay; runs every frame of _main (non-blocking). */
  net_frame(): any {
    let _loc1_: any = this.net_events;
    let _loc2_: any = undefined;
    if (_loc1_) {
      while ((_loc2_ = _loc1_.take("status"))) {
        if (_loc2_.status == "open") {
          if (this.net_link_lost) {
            this.net_link_lost = false;
            this.net_ui().setStatus(null);
            this.net_notice(MSG_CONNECTION_RESTORED, 3000);
          }
        } else {
          this.net_link_lost = true;
          this.net_ui().setStatus(MSG_CONNECTION_LOST);
        }
      }
      // A profile change by another connection is shown once the interface is idle (never inside a battle, a
      // blocking call or an animation); older revisions (our own saves) are ignored.
      if (this.main_mode == "IDLE" && !this.net_pending && !this.animation && !this.grave_searching && !this.execute_after_animation && this._interf && !this.loading_screen && !this.OOPS_BANNED) {
        _loc2_ = _loc1_.take("profile.changed");
        if (_loc2_ && Number(_loc2_.revision) > Number(this.profile_revision)) {
          this.net_reload_profile();
        }
      }
    }
    if (this.net_overlay) {
      this.net_overlay.update(this.display_system, this.net_now(), () => this.net_overlay_view());
    }
  }

  /**
   * PORT (wide screens): where the connection overlay is laid out (ui/net-overlay.ts): the shown safe area, its text
   * enlarged for a phone, in a battle above the touch controls' clusters while they are shown (the opponent's turn
   * hides them: at the area's bottom then, not above clusters no one sees); null without a wide host (a desktop): at
   * the bottom of the window, as before. Presentation only.
   */
  net_overlay_view(): NetOverlayView | null {
    const st: any = this.stage;
    if (!st || !st.$visibleArea) return null;
    const r = st.safeRect;
    let above: NetOverlayView["above"] = null;
    let top: number | null = null;
    if (this._proc && typeof this.touch_zones === "function") {
      try {
        if (typeof this.touch_shown !== "function" || this.touch_shown()) above = this.touch_zones();
      } catch {
        above = null;
      }
      // (the local turn: the touch controls' hint takes the bottom; under the team bars instead)
      const teams = above ? this.wide_screen?.hudRect("teams") : null;
      if (teams) top = teams.y + teams.height + 8 / (st.cssScale > 0 ? st.cssScale : 1);
    }
    return { area: { x: r.x, y: r.y, width: r.width, height: r.height }, css: st.cssScale, above, top, avoid: top !== null ? this.hud_focus() : null };
  }

  /** Subscribes the event queue to the backend (once, at boot). */
  net_attach(): any {
    if (!this.net_events) {
      this.net_events = new NetEventQueue();
      this.net_events.attach(this.services);
      // PORT: a purchase credited outside the shop's own flow (a pending payment completed, a recovered purchase):
      // PORT: the stored profile changed, reload it when idle.
      const store = this.deps && this.deps.monetization ? this.deps.monetization.store : null;
      if (store) {
        store.onCredited(() => {
          if (this.net_events) this.net_events.push({ "type": "profile.changed", "revision": Number.POSITIVE_INFINITY });
        });
      }
    }
    if (!this.net_replaced_off && this.services && typeof this.services.onSessionReplaced == "function") {
      this.net_replaced_off = this.services.onSessionReplaced(() => this.net_session_replaced());
    }
    // PORT: design D5 - the online lobby (rooms, online battles) listens to the server from the boot on.
    if (!this.online) {
      this.online = new OnlineLobby(this);
    }
    // PORT: item 8 - the clans of the players (their events: the player's clan changed, requests to decide).
    if (!this.clans) {
      this.clans = new ClanHub(this);
    }
  }

  // PORT: VK removed: net_get_avatar (VK profile photo loaders, wait_for_avatars) is not ported; every backend uid
  // PORT: gets the placeholder avatar the offline build drew for every VK uid (ensure_avatar).
  ensure_avatar(param1: any): any {
    let _loc2_: any = null;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    if (this.avdatas["id" + param1]) {
      return;
    }
    _loc2_ = new BitmapData(50, 50, true, 4294901760);
    _loc3_ = 0;
    while (_loc3_ < 5) {
      _loc4_ = 0;
      while (_loc4_ < 5) {
        _loc2_.fillRect(new Rectangle(_loc3_ * 10, _loc4_ * 10, 10, 10), _loc3_ % 2 == _loc4_ % 2 ? as3.uint(4291611852) : as3.uint(4281545523));
        _loc4_++;
      }
      _loc3_++;
    }
    this.avdatas["id" + param1] = _loc2_;
  }

  /** Backend player id -> the numeric uid used by the game logic (tops, clans, assault owners). */
  uid_of(param1: any): number {
    if (param1 == null || param1 === "") {
      return 0;
    }
    let _loc2_: any = numericPlayerId(String(param1));
    this.player_ids[_loc2_] = String(param1);
    return _loc2_;
  }

  /** Unix seconds from a backend timestamp (milliseconds or seconds). */
  unix_seconds(param1: any): number {
    let _loc2_: any = Number(param1);
    if (!(_loc2_ > 0)) {
      return 0;
    }
    return _loc2_ > 100000000000 ? Math.floor(_loc2_ / 1000) : Math.floor(_loc2_);
  }

  // ---------------------------------------------------------------- boot (replaces the "get user id" -> ... -> "prove sid" chain)
  /**
   * PORT: the game's accounts (nickname + password, docs/backend.md): the session stored for this backend is restored
   * (restoreSession: the player card, or null); without one the sign-in screen opens instead of the main interface
   * (net_show_auth), and the boot goes on once the player signed up or in (net_auth_done). A guest host (headless
   * tools and tests, `?autoguest=1` with an in-process backend: guestSignIn) and test doubles without accounts sign in
   * as a guest as before (ensureSession).
   */
  net_boot(): any {
    this.net_attach();
    this.loading_screen.loading.text = "1 / " + this.INIT_STAGES;
    if (this.net_accounts()) {
      this.net_run("auth", () => this.services.restoreSession(), (card: any) => {
        if (card) {
          this.net_signed_in(card);
        } else {
          this.net_show_auth();
        }
      });
      return;
    }
    this.net_run("auth", () => this.services.ensureSession(), (identity: any) => {
      this.net_signed_in({ "playerId": identity.playerId, "nickname": identity.displayName, "guest": identity.username == null });
    });
  }

  /** PORT: the game plays with an account of the backend (no guest allowed): the sign-in screen when there is no session. */
  net_accounts(): boolean {
    return !!this.services && typeof this.services.restoreSession == "function" && !this.services.guestSignIn;
  }

  /** PORT: the player is known (the boot's session or a sign-in): the one player state of the game, then the boot chain. */
  net_signed_in(param1: any): any {
    this.user.user_id = this.uid_of(param1.playerId);
    this.user.player_id = param1.playerId;
    this.user.guest = Boolean(param1.guest);
    if (param1.nickname) {
      this.user.name = param1.nickname;
    }
    this.win_auc_weapons.annihilator = 0;
    this.auc_weapons.annihilator = 0;
    this.loading_screen.loading.text = "2 / " + this.INIT_STAGES;
    this.net_refresh_social(() => {
      this.loading_screen.loading.text = "4 / " + this.INIT_STAGES;
      this.net_load_profile();
    });
  }

  /**
   * PORT: the sign-in screen instead of the main interface (the loading screen is hidden meanwhile). After a restart
   * it says why (the session expired, the player signed out); a stored session the backend refused opens the sign-in
   * form with the same "session expired" message.
   */
  net_show_auth(): any {
    let _loc1_: AuthView = "landing";
    let _loc2_: any = null;
    const reason = this.deps ? this.deps.restartReason : null;
    const refused = this.services.restoreFailure;
    let takeOver = false;
    if (reason == "session_replaced" || refused == "replaced") {
      _loc1_ = "signIn";
      _loc2_ = { "text": MSG_AUTH_SESSION_REPLACED, "error": true };
      takeOver = true;
    } else if (reason == "session_expired" || (refused && refused != "none")) {
      _loc1_ = "signIn";
      _loc2_ = { "text": MSG_AUTH_SESSION_EXPIRED, "error": true };
    } else if (reason == "signed_out") {
      _loc2_ = { "text": MSG_AUTH_SIGNED_OUT, "error": false };
    } else if (reason == "account_deleted") {
      _loc2_ = { "text": MSG_AUTH_ACCOUNT_DELETED, "error": false };
    }
    if (this.loading_screen && this.loading_screen.parent == this.display_system) {
      this.display_system.removeChild(this.loading_screen);
    }
    this.main_mode = "IDLE";
    this.auth_screen = new AuthScreen(this, {
      "view": _loc1_,
      "notice": _loc2_,
      "takeOver": takeOver,
      "onSignedIn": (card: any) => this.net_auth_done(card),
      "onBanned": (ban: any) => {
        this.net_close_auth();
        this.net_show_ban(ban);
      },
      "onLanguage": (lang: any) => {
        this.pref("lang", lang);
        this.net_restart("language");
      },
    });
    this.display_interf.addChild(this.auth_screen.root);
    this.auth_screen.attach();
  }

  /** PORT: the sign-in screen is closed for good (its fields emptied). */
  net_close_auth(): any {
    if (this.auth_screen) {
      this.auth_screen.dispose();
      this.auth_screen = null;
    }
  }

  /** PORT: signed up or in on the sign-in screen: the loading screen again and the rest of the boot chain. */
  net_auth_done(param1: any): any {
    this.net_close_auth();
    if (this.loading_screen) {
      this.display_system.addChild(this.loading_screen);
      this.loading_screen.stat_text.text = "Инициализация...";
    }
    this.net_signed_in(param1);
  }

  /**
   * PORT: the sign-out button (settings screen): auth.logout, then this backend's stored credentials and every cached
   * profile value are wiped (GameBackend.signOut), and the game restarts cleanly into the sign-in screen, so the next
   * player never sees anything of this one.
   */
  net_sign_out(): any {
    // A guest (restored from an earlier version of the game) has no password to come back with: no sign-out.
    if (this.net_pending || this.main_mode != "IDLE" || !this.net_accounts() || this.user.guest || !this.user.player_id) {
      return;
    }
    this.display_system.addChild(this.splash_wait);
    this.net_run("sign out", () => this.services.signOut(), () => {
      // Nothing of this player stays on the device: the credentials and cached profile are gone (signOut), and so are
      // the player's client preferences (its online room and battle).
      this.pref_delete(FWP.PLAYER_PREFS);
      this.net_forget_player();
      this.net_restart("signed_out");
    });
  }

  /**
   * PORT: the delete-account button (settings screen): the confirmation dialog (src/game/ui/delete-account.ts: the
   * password of an account, the session alone of a guest), then account.delete (GameBackend.deleteAccount: the backend
   * services drop the session and wipe every credential stored for this backend and every cached profile value). The
   * player's client preferences go too, and the game restarts cleanly into the sign-in screen, which says that the
   * account was deleted. Nothing is deleted when the dialog is closed.
   */
  /**
   * PORT: the settings screen's "Account and language" button: the dialog with the languages, sign out, delete the
   * account and the app's account extras (src/game/ui/account-dialog.ts), over the settings screen.
   */
  net_account_dialog(): any {
    if (this.account_dialog || this.delete_dialog || this.net_pending || this.main_mode != "IDLE" || !this.user.player_id) {
      return;
    }
    const dialog = new AccountDialog(this, {
      "onLanguage": (lang: any) => {
        this.pref("lang", lang);
        this.net_restart("language");
      },
      "onSignOut": () => this.net_sign_out(),
      "onDelete": () => this.net_delete_account_prompt(),
      // PORT: "About" first (the game's name and the trademark notice: ui/legal-notice.ts), then the app's.
      "extras": [{ "name": ABOUT_NAME, "label": MSG_ABOUT, "run": () => this._interf.init("error", ABOUT_MODE) }, ...this.net_account_extras(), ...accountExtras(this._interf)],
      "onClosed": () => {
        this.account_dialog = null;
      },
    });
    this.account_dialog = dialog;
    this.display_system.addChild(dialog.root);
    dialog.attach();
  }

  /**
   * PORT: the account dialog's own extras: the e-mail of the account where the server offers e-mail sign-in (not for a
   * guest of an earlier version: it has no password), and the app's diagnostics and notification switches.
   */
  net_account_extras(): Array<{ name: string; label: string; run(): void }> {
    const out: Array<{ name: string; label: string; run(): void }> = [];
    if (this.services && typeof this.services.emailAvailable == "function" && this.services.emailAvailable() && !this.user.guest) {
      out.push({ "name": "account_email", "label": MSG_EMAIL_BUTTON, "run": () => this.net_email_dialog() });
    }
    return [...out, ...integrationExtras(this.deps ? this.deps.integrations : null, (text) => this.net_notice(text, 4000))];
  }

  /** PORT: the account's e-mail dialog (ui/email-dialog.ts) over the settings screen. */
  net_email_dialog(): any {
    if (this.email_dialog || this.delete_dialog || this.net_pending || this.main_mode != "IDLE" || !this.user.player_id) {
      return;
    }
    const dialog = new EmailDialog(this, {
      "onSessionEnded": () => {
        this.email_dialog = null;
        this.net_session_ended();
      },
      "onClosed": () => {
        this.email_dialog = null;
      },
    });
    this.email_dialog = dialog;
    this.display_system.addChild(dialog.root);
    dialog.attach();
  }

  /**
   * PORT: the main map's chat button: the chat panel over the main map (ui/chat-panel.ts; not over a battle or another
   * screen, not while the game waits for the server).
   */
  net_chat_open(): any {
    const i: any = this._interf;
    const top: any = i && i.cur_screen ? i.cur_screen[i.cur_screen[0]] : null;
    if (this.chat_ui || this.net_pending || this.animation || this.main_mode != "IDLE" || !this.user.player_id || !top || top.name != "mainframe" || !this.chat.available()) {
      return;
    }
    const panel = new ChatPanel(this, this.chat, {
      "onClosed": () => {
        this.chat_ui = null;
        this.chat.panel = null;
      },
      "onSessionEnded": () => this.net_session_ended(),
    });
    this.chat_ui = panel;
    this.chat.panel = panel;
    this.display_system.addChild(panel.root);
    panel.attach();
  }

  net_delete_account_prompt(): any {
    if (this.delete_dialog || this.net_pending || this.main_mode != "IDLE" || !this.user.player_id) {
      return;
    }
    const dialog = new DeleteAccountDialog(this, {
      "onDeleted": () => {
        this.delete_dialog = null;
        this.net_stop();
        this.pref_delete(FWP.PLAYER_PREFS);
        this.net_forget_player();
        this.net_restart("account_deleted");
      },
      "onSessionEnded": () => {
        this.delete_dialog = null;
        this.net_session_ended();
      },
      "onClosed": () => {
        this.delete_dialog = null;
      },
    });
    this.delete_dialog = dialog;
    this.display_system.addChild(dialog.root);
    dialog.attach();
  }

  /** PORT: forgets the signed-in player on screen until the restart (the interface is hidden, the identity cleared). */
  net_forget_player(): any {
    if (this.chat_ui) this.chat_ui.close();
    this.chat.reset();
    this.user.player_id = null;
    this.user.user_id = 0;
    this.user.name = "";
    this.user.guest = false;
    this.display_interf.visible = false;
  }

  /**
   * The player's clan and the three top lists from the backend. PORT: item 8 - the original loaded the fixed clans and
   * PORT: every member list (CLANS_PEOPLE_DEF, clans_rating, clan_people_assaults); now clan.mine is the player's own
   * PORT: clan and the tops' entries carry each player's clan (clan_of_uid), so no member list is needed.
   */
  net_refresh_social(param1: any): any {
    this.net_run("get social", async () => {
      // PORT: a backend without clans (a test double) just has no clan.
      const mine: any = typeof this.services.getMyClan == "function" ? await this.services.getMyClan().catch(() => null) : null;
      const boards: any = {};
      for (const b of ["level", "rating", "assaults"]) {
        boards[b] = await this.services.getLeaderboard(b, 1);
      }
      // PORT: design D7 - the player's Annihilators of the auction for the arsenal (the original counted them from the
      // PORT: auction info at login, FWP.as:14436-14463); a server without the auction just has none.
      const auction: any = await loadAuction(this.services).catch(() => ({}));
      return { "mine": mine, "boards": boards, "auction": auction.info || null };
    }, (result: any) => {
      if (result.auction) {
        this.net_auction_adopt(result.auction, result.auction.mine, this.unix_seconds(result.auction.serverTime));
      }
      if (result.mine && this.clans) {
        this.clans.adoptMine(result.mine);
      }
      this.TOP_BY_LEVEL = this.top_from_page(result.boards.level);
      this.TOP_BY_RATING = this.top_from_page(result.boards.rating);
      this.TOP_BY_ASSAULT = this.top_from_page(result.boards.assaults);
      param1();
    });
  }

  /** PORT: item 8 - what the server says of a player's clan (a ClanBrief or null) under the game's numeric uid. */
  note_clan(param1: any, param2: any): any {
    if (param1 == 0) {
      return;
    }
    this.clan_of_uid["id" + param1] = param2 && param2.clanId ? { "clanId": Number(param2.clanId), "name": String(param2.name), "emblem": Number(param2.emblem) } : null;
  }

  /** Leaderboard page -> original TOP_BY_* rows ({uid, exp, rating, achiev_rank, assaults}) + top_names. */
  top_from_page(param1: any): any {
    let _loc2_: any = new Array();
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    for (_loc3_ of param1.entries) {
      _loc4_ = this.uid_of(_loc3_.playerId);
      // PORT: the player's specialisation: the portrait the row shows (ui/avatar.ts).
      _loc2_[_loc3_.rank - 1] = { "uid": _loc4_, "exp": Number(_loc3_.exp), "rating": Number(_loc3_.rating), "achiev_rank": 0, "assaults": Number(_loc3_.assaults), "spec": Number(_loc3_.spec) || 0 };
      this.top_names["id" + _loc4_] = { "uid": _loc4_, "first_name": _loc3_.displayName, "last_name": "" };
      // PORT: item 8 - the entry's clan (its emblem next to the player in the tops).
      if (_loc3_.clan !== undefined) {
        this.note_clan(_loc4_, _loc3_.clan);
      }
      this.ensure_avatar(_loc4_);
    }
    return _loc2_;
  }

  /**
   * PORT: design D1 - at trust level L2 the backend owns the state: profile.load creates a new account's state (the
   * PORT: developer profile only where the backend allows it) and runs the session entry (tiredness, Papa John, wins,
   * PORT: the daily entry) itself, so the game only adopts it (the load's `volatile` facts: PAPA_HERE, the present).
   * PORT: At L0/L1 (tests, tools) the game still creates, enters and saves its own state as the original did.
   */
  net_load_profile(): any {
    this.net_run("get storage", () => this.services.loadProfile(this.DEV_PROFILE ? { "dev": true } : {}), (profile: any) => {
      let _loc2_: any = 0;
      this.loading_screen.loading.text = "6 / " + this.INIT_STAGES;
      this.profile_revision = profile.revision;
      if (profile.displayName) {
        this.user.name = profile.displayName;
      }
      if (profile.volatile && profile.gameState != null && profile.gameState.format == GAME_STATE_FORMAT) {
        this.apply_state(profile.gameState);
        this.user.unix_time = profile.serverTime || this.get_current_time();
        this.PAPA_HERE = Boolean(profile.volatile.papaHere);
        this.get_present = profile.volatile.presentAvailable ? profile.volatile.present : 0;
        this.loading_screen.loading.text = "8 / " + this.INIT_STAGES;
        this.net_initialized();
        return;
      }
      // PORT: here the game owns its state (L0/L1) and creates it itself: the developer profile only with the
      // PORT: in-process (offline) backend. A remote server decides only for the states it creates (L2, DEV_PROFILES),
      // PORT: so `?dev=1` never builds and saves a cheat profile on a shared server that leaves the state to the game.
      if (this.services.transportKind !== "in-process") {
        this.DEV_PROFILE = false;
      }
      if (profile.gameState != null && profile.gameState.format == GAME_STATE_FORMAT) {
        this.apply_state(profile.gameState);
      } else if (profile.legacySave != null && String(profile.legacySave).length > 1) {
        // PORT: one-time import of an original save string (legacy codec kept for migration only). The backend hands
        // PORT: it over only while it accepts the import into this (fresh) profile; the imported state is then loaded
        // PORT: again, so the session entry runs on it like on every other load.
        this.user.params = String(profile.legacySave);
        _loc2_ = this.read_storage(this.user.params, "my");
        if (_loc2_ != 0) {
          this.create_new_params("first time");
        }
        this.net_save("import", () => {
          this.net_load_profile();
        });
        return;
      } else {
        this.create_new_params("first time");
        this.state_dirty_reason = "action";
      }
      this.net_refresh_time(() => {
        this.modify_last_enter();
        if (this.state_dirty_reason != "") {
          this.net_save(this.state_dirty_reason, () => {
            this.net_initialized();
          });
        } else {
          this.net_initialized();
        }
        this.state_dirty_reason = "";
      });
    });
  }

  /** "get time" / "refresh time": server clock + the original daily/tiredness/Papa John/win counters. */
  net_refresh_time(param1: any): any {
    this.net_run("refresh time", () => this.services.getServerTime(), (time: any) => {
      this.user.unix_time = this.unix_seconds(time) || this.get_current_time();
      // PORT: VK removed: CURRENT_SID = unix_time (VK sid multi-window check, check_sid) is not ported.
      this.modify_tired();
      this.modify_papa();
      this.modify_wins();
      if (this.loading_screen) {
        this.loading_screen.loading.text = "8 / " + this.INIT_STAGES;
      }
      param1();
    });
  }

  /** "save storage" + "check storage": persists the JSON game state; `done` runs when the backend confirmed it. */
  net_save(param1: any, param2: any): any {
    const state = this.serialize_state();
    this.net_run("save storage", () => this.services.saveProfile(state, { "reason": param1 }), (result: any) => {
      this.profile_revision = result.revision;
      param2();
    });
  }

  /** The player's persistent state as plain JSON (replaces the VK-storage v45 string of write_storage). */
  serialize_state(): any {
    return { "format": GAME_STATE_FORMAT, "player": as3.toJsonState(this.player, ProtectedInt), "map_info": as3.toJsonState(this.map_info, ProtectedInt) };
  }

  apply_state(param1: any): any {
    this.player = as3.fromJsonState(param1.player, (v: number) => new ProtectedInt(v));
    this.map_info = as3.fromJsonState(param1.map_info, (v: number) => new ProtectedInt(v));
    // PORT: the load-time normalizations of read_storage (FWP.as:16911-16918) ran on every load of the original, so
    // PORT: they run on every JSON load too (the VK banner bonus of FWP.as:16921-16925 is not ported).
    if (this.player.flags && this.player.flags["flag_spec"] == 1 && this.player.missions_max < 20) {
      this.player.flags = {};
    }
    if (this.player.exp.value > 0 && this.player.rating == 0) {
      this.player.rating = (this.player.level.value - 1) * 100;
    }
    // PORT: product change - a profile that had the Russian or Soviet flag equipped falls back to no flag, so the
    // PORT: battle HUD does not draw it. (A save may be applied before init_data_arrays, hence the FLAGS_DEF check.)
    for (const k of ["flag_russia", "flag_ussr"]) {
      if (this.FLAGS_DEF && this.player.cur_flag == this.FLAGS_DEF[k].id) this.player.cur_flag = 0;
    }
    this.read_params_end = true;
  }

  private get_current_time(): number {
    return Math.floor(as3.now() / 1000);
  }

  net_success(param1: any): any {
    // PORT: kept for API compatibility; every backend result is delivered to the continuation passed to net_run.
  }

  do_nothing(param1: any): any {
    this.wait_for_complete = false;
  }

  net_initialized(): any {
    this.display_system.removeChild(this.loading_screen);
    this.loading_screen = null;
    // PORT: removed the LocalConnection double-connect garbage-collection hack (Flash Player specific; docs/architecture.md section 5).
    // PORT: item 8 - user_clan comes from clan.mine (net_refresh_social, ClanHub.adoptMine).
    this.player.level.value = this.get_player_level(this.player.exp.value);
    this.refresh_temp_params();
    this.main_mode = "IDLE";
    this.add_interf();
    this.add_sound();
    this.display_interf.addChild(this._interf);
    this._sound.on_change("music");
  }

  net_get_assault(): any {
    this.display_system.addChild(this.splash_wait);
    this.net_run("get assault", () => this.services.getAssaultZones(), (result: any) => {
      this.net_proceed_assault(result);
    });
  }

  net_proceed_assault(param1: any): any {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    this.ASSAULT = new Array();
    this.assault_uids = "";
    this.assault_names = {};
    for (_loc2_ of param1.zones) {
      _loc3_ = this.uid_of(_loc2_.ownerId);
      this.ASSAULT[_loc2_.zone] = { "zone_stage": Number(_loc2_.stage), "user_id": _loc3_, "last_assault": this.unix_seconds(_loc2_.lastCaptureAt) };
      if (_loc3_ != 0) {
        this.assault_names["key" + _loc2_.zone] = { "uid": _loc3_, "first_name": _loc2_.ownerName || "", "last_name": "" };
        // PORT: item 8 - the owner's clan now (a clan holds the zones of its members): the emblem on the map, the
        // PORT: zones of the player's clan (no attack button, the tribute's tenth).
        if (_loc2_.ownerClan !== undefined) {
          this.note_clan(_loc3_, _loc2_.ownerClan);
        }
        this.ensure_avatar(_loc3_);
      }
    }
    this.assault_cur_time = this.unix_seconds(param1.serverTime);
    this.fwproject_time = this.assault_cur_time;
    this.display_system.removeChild(this.splash_wait);
    if (this._interf.cur_screen[this._interf.cur_screen[0]].name != "assault") {
      // PORT: after a won assault battle: the zone list with the real owners (battle.finish captured the zone or
      // PORT: told why not), then the result screens (design D4; the capture is no longer reported by the client).
      this.net_assault_update_complete(null);
    } else {
      this._interf.render();
    }
  }

  // PORT: net_update_assault (assault.report { zone, outcome: 'win', expectedStage } after a won battle, then the
  // PORT: 'battle_end' save) is gone: the server captures the zone itself when it accepts the battle (battle.finish:
  // PORT: result.captured / captureBlocked, design D4), and the rewards come with the state battle.finish returns.
  net_assault_update_complete(param1: any): any {
    if (!this.OOPS_BANNED) {
      this._interf.init_win_or_lose();
    }
  }

  // PORT: VK wall posts removed (the buttons are hidden).
  net_post_new_level(): any {
  }

  net_post_win_game(): any {
  }

  /** Replay list (originally VK notes whose text carried the replay string). */
  net_get_notes(): any {
    this.display_system.addChild(this.splash_wait);
    this.net_run("get notes", async () => {
      // PORT: replay.list returns summaries without the replay text; the (at most 23 shown, new_interf.as:4436)
      // PORT: newest replays are fetched with replay.get to obtain their legacy "!PARAMS!ROUNDS!chk!" strings.
      const list = await this.services.listReplays(undefined, 23);
      const items: any[] = [];
      for (const r of list) {
        items.push(await this.services.getReplay(r.replayId));
      }
      return items;
    }, (items: any) => {
      let _loc2_: any = undefined;
      this.display_system.removeChild(this.splash_wait);
      this.USER_NOTES = new Array<any>(0, {});
      for (_loc2_ of items) {
        if (_loc2_.legacy) {
          ++this.USER_NOTES[0];
          // PORT: the row's name in the game's words and the battle's date (runtime/replay-offer.ts replayTitle), not
          // PORT: the internal ids ("FWP career 1").
          this.USER_NOTES[this.USER_NOTES[0]] = { "title": replayTitle(_loc2_), "text": _loc2_.legacy };
        }
      }
      this._interf.render();
    });
  }

  net_get_tops(): any {
    this.net_refresh_social(() => {
      this._interf.render();
    });
  }

  net_get_top_members(): any {
    let _loc1_: any = this._interf.top_mode == 2 ? "rating" : (this._interf.top_mode == 3 ? "assaults" : "level");
    let _loc2_: any = this._interf.top_page;
    this.display_system.addChild(this.splash_wait);
    this.net_run("get profiles", () => this.services.getLeaderboard(_loc1_, _loc2_), (page: any) => {
      let _loc3_: any = undefined;
      let _loc4_: any = this.top_from_page(page);
      let _loc5_: any = _loc1_ == "rating" ? this.TOP_BY_RATING : (_loc1_ == "assaults" ? this.TOP_BY_ASSAULT : this.TOP_BY_LEVEL);
      _loc3_ = 0;
      while (_loc3_ < 100) {
        _loc5_[_loc3_] = _loc4_[_loc3_] || _loc5_[_loc3_] || { "uid": 0, "exp": 0, "rating": 0, "achiev_rank": 0, "assaults": 0 };
        _loc3_++;
      }
      this.ensure_avatar(0);
      this.top_names["id0"] = { "uid": 0, "first_name": "—", "last_name": "" };
      this.display_system.removeChild(this.splash_wait);
      this._interf.render();
    });
  }

  // PORT: item 8 - net_get_clan_members (the member list popup of a fixed clan, unreachable in the original: no
  // PORT: clan_info button exists) is gone; the card of a clan is runtime/clan-hub.ts (clan.get / clan.members).

  /**
   * Other player's card (the original called a missing net_get_user_info; VK storage of other users is gone): the
   * server's public profile (profile.getPublic) - the level, the skills and progress of its summary, the abilities
   * and weapons of the team the player last fought with, the last visit. `get_info_ready`: the card has its data
   * (a new player's experience is 0 too, which the original took for a failed load).
   */
  get_info_ready: boolean = false;

  net_get_user_info(param1: any, param2: any = ""): any {
    let _loc3_: any = this.player_ids[Number(param1)];
    this.get_info_uid = Number(param1);
    this.user_info_mode = as3.str(param2);
    this.create_new_params("empty friend");
    this.get_info_ready = false;
    if (!_loc3_) {
      this._interf.render();
      return;
    }
    this.display_system.addChild(this.splash_wait);
    this.net_run("get user info", () => this.services.getPublicProfile(_loc3_), (profile: any) => {
      const summary: any = profile.summary || {};
      const num = (v: any, d: number = 0): number => (Number.isFinite(Number(v)) ? Number(v) : d);
      this.get_info.exp.value = num(summary.exp);
      this.get_info.level.value = this.get_player_level(this.get_info.exp.value);
      this.get_info.rating = num(summary.rating);
      this.get_info.attack.value = num(summary.attack);
      this.get_info.protect.value = num(summary.protect);
      this.get_info.health.value = num(summary.health);
      this.get_info.worms.value = num(summary.worms);
      this.get_info.spec = num(summary.spec);
      this.get_info.career_max = num(summary.careerMax, 1);
      this.get_info.missions_max = num(summary.missionsMax, 1);
      this.get_info.survival_max = num(summary.survivalMax, 1);
      this.get_info.boss_max = num(summary.bossMax, 1);
      this.get_info.last_enter = this.unix_seconds(profile.updatedAt);
      // The team it last fought with: its abilities (known ones, at their levels) and weapons (copies owned).
      const team: any = profile.team || {};
      this.get_info.perks = {};
      for (const key of Object.keys(team.perks || {})) {
        const lvl = Math.floor(num(team.perks[key]));
        if (this.PERKS_DEF[key] && lvl >= 1) this.get_info.perks[key] = Math.min(lvl, Number(this.PERKS_DEF[key].max_lim) || lvl);
      }
      this.get_info.weapons = {};
      for (const key of Object.keys(team.weapons || {})) {
        const n = Math.floor(num(team.weapons[key]));
        if (this.WEAPONS_DEF[key] && n >= 1) this.get_info.weapons[key] = [n];
      }
      this.friends_names = { "id": { "uid": this.get_info_uid, "first_name": profile.displayName, "last_name": "" } };
      this.get_info_ready = true;
      this.display_system.removeChild(this.splash_wait);
      this._interf.render();
    });
  }

  // PORT: design D7 - the annihilator auction of the original ("king of the hill") on the own backend, paid in
  // PORT: diamonds instead of VK votes (the offline build had it switched off: click_auc returned at once).
  // PORT: click_auc -> net_auction_open: auction.get, then the 'auc' screen (the original: net_init_check_auc_mf ->
  // PORT: "get balance auc" -> net_check_auc_mainframe -> "get auc profile"). auc_buy -> net_auction_buy: the command
  // PORT: auction.buy with the price the screen shows (the original: the VK balance check, net_withdraw_auc, the VK
  // PORT: payment and net_complete_auc); the server takes the diamonds from the canonical state. A battle's
  // PORT: Annihilators come from the server (battle.start); net_check_auc stays as the rules module's reference.
  net_auction_open(param1: boolean = false): any {
    this.display_system.addChild(this.splash_wait);
    this.net_run("get auction", () => loadAuction(this.services), (answer: any) => {
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      if (answer.refused) {
        this.net_notice(MSG_AUCTION_UNAVAILABLE, 4000);
        return;
      }
      this.net_auction_adopt(answer.info, answer.info.mine, this.unix_seconds(answer.info.serverTime));
      if (param1) {
        this._interf.render();
      } else {
        this._interf.init("auc");
      }
    });
  }

  net_auction_buy(): any {
    this.net_command("auction.buy", { "expectedPrice": Number(this.auc_info.price) }, "render", { "done": (result: any, outcome: any) => {
      this.net_auction_adopt(result.auction, result.mine, outcome.serverTime);
      this._interf.init("error", "auc_success");
    }, "refused": () => {
      // The price or the holder changed meanwhile (or too few diamonds): the screen shows the auction as it is now.
      this.net_auction_open(true);
    } });
  }

  /** The backend's auction (AuctionState + the player's holdings) -> auc_info, auc_names, auc_weapons, win_auc_weapons. */
  net_auction_adopt(param1: any, param2: any, param3: any): any {
    const m: any = auctionMemory(param1, param2, Number(param3) || this.user.unix_time, (id: string) => this.uid_of(id));
    this.auc_info = m.info;
    this.auc_names = m.names;
    this.auc_weapons.annihilator = m.held;
    this.win_auc_weapons.annihilator = m.permanent;
    if (m.info.holder) {
      this.ensure_avatar(m.info.user_id);
    }
  }

  net_check_auc(): any {
    this.win_auc_weapons.annihilator = 0;
    this.auc_weapons.annihilator = 0;
    // PORT: original semantics kept: refresh the clock and save the profile before every battle.
    this.net_refresh_time(() => {
      this.execute_after = "init game";
      this.net_save("battle_start", () => {
        this.success_saved();
      });
    });
  }

  // PORT: Loader.loadBytes(get_map_byte_array(id)) replaced by AssetLibrary.getMapBitmap(id) (via get_map_byte_array).
  // PORT: map_downloaded() still runs asynchronously, on the next frame, like the Loader COMPLETE event.
  init_map_downloading(): any {
    const mapId: any = this.main_map_dl_id;
    // PORT: map bundles are loaded lazily by the AssetLibrary (async), then completion is delivered on the next
    // PORT: frame like the original Loader COMPLETE event.
    this.deps.library.loadMap(mapId).then(() => {
      const content: any = { map: this.get_map_byte_array(mapId) };
      this.mapLoader = { content, contentLoaderInfo: { content } };
      const onComplete = (e: any): void => {
        this.removeEventListener(Event.ENTER_FRAME, onComplete);
        this.map_downloaded({ type: Event.COMPLETE, target: this.mapLoader.contentLoaderInfo, currentTarget: this.mapLoader.contentLoaderInfo });
      };
      this.addEventListener(Event.ENTER_FRAME, onComplete);
    }, (error: any) => {
      as3.trace("map load failed: " + mapId + ": " + (error && error.message ? error.message : String(error)));
      this.system_error("Не удалось загрузить карту " + mapId);
    });
  }

  map_downloaded(param1: any): any {
    let _loc2_: any = undefined;
    _loc2_ = new Bitmap();
    _loc2_ = param1.currentTarget.content.map;
    this.LoadedBitmap = new BitmapData(_loc2_.width, Math.max(650, _loc2_.height), true, 0);
    this.LoadedBitmap.copyPixels(_loc2_, new Rectangle(0, 0, _loc2_.width, _loc2_.height), new Point(0, 650 - _loc2_.height));
    // PORT: _loc2_.dispose() removed: the map BitmapData belongs to the AssetLibrary and is reused by later loads.
    _loc2_ = null;
    // PORT: removed the LocalConnection double-connect garbage-collection hack (Flash Player specific; docs/architecture.md section 5).
    this.display_system.removeChild(this.splash_wait);
    if (this.main_execute_after_map_dl == "continue preparing game") {
      this.continue_preparing_game();
    }
    if (this.main_execute_after_map_dl == "continue preparing just play") {
      this.init_game({ "mode": "just", "id": 1 });
    }
    if (this.main_execute_after_map_dl == "init replay") {
      this.init_replay();
    }
    if (this.main_execute_after_map_dl == "start solo battle") {
      this.init_game({ "mode": this._play_mode, "id": this._play_id });
    }
    this.main_map_dl = false;
  }

  // PORT: returned the embedded map sub-SWF bytes (FFDec also truncated its switch); now returns the terrain
  // PORT: BitmapData (the `map` property of that SWF) from the AssetLibrary. Name kept for traceability.
  get_map_byte_array(param1: any): any {
    return this.deps.library.getMapBitmap(param1);
  }

  // PORT: battle input seam and the solo battle entry point (design D4, src/game/runtime/battle-input.ts).
  // PORT: battle_setup: what start_solo_battle fixed for the next init_game (null for the menu flows);
  // PORT: battle_input: the running battle's input seam (proc.battle_input); battle_init: how the running battle was
  // PORT: set up (mode, stage, zoneStage, seed, stims, annihilators, team name and the player state it started from);
  // PORT: battle_result: the recorded input (CommandLog, global ticks), the outcome in the rules' BattleOutcome shape
  // PORT: and the final state hash of the last battle, set by end_game_fn. battle_checkpoints: hash the battle state
  // PORT: at its start and at every move start (battle_result.moves) also in the menu flows (tests, online checks).
  battle_setup: any = null;
  battle_input: any = null;
  battle_init: any = null;
  battle_result: any = null;
  battle_checkpoints: any = false;

  // PORT: the single entry point of a solo battle (career / missions / survival / boss / assault) on the CURRENT
  // PORT: player state: none of prepare_game's mutations (tiredness, attempts, stims, achievements: the backend's
  // PORT: battle.start applied them to the state this game runs on), the team built by init_game exactly as for the
  // PORT: menu flow, `seed` instead of proc.init_gameplay's true_random(0,399), `input` as the battle's input source
  // PORT: (default: the live keyboard, LocalInput). param1 = { mode, stage (the zone of an assault), zoneStage?, seed,
  // PORT: stimsInBattle, annihilators, teamName, input?, checkpoints?, onEnd? }. The battle starts after the map is
  // PORT: loaded (next frames, like prepare_game); battle_result is set when it ends (and onEnd(result) is called).
  start_solo_battle(param1: any): any {
    const mode: string = String(param1.mode);
    const stage: number = Number(param1.stage);
    let def: any = undefined;
    if (this.main_mode != "IDLE" || this.main_map_dl || this._proc || this.REPLAY) {
      throw new Error("start_solo_battle: the game is busy (" + this.main_mode + ")");
    }
    if (mode == "career" || mode == "survival" || mode == "boss" || mode == "assault") {
      def = this.TEAMS_DEF[mode + stage];
    }
    if (mode == "missions") {
      def = this.TEAMS_DEF["mission" + stage];
    }
    if (!def) {
      throw new Error("start_solo_battle: unknown battle " + mode + " " + stage);
    }
    const seed: number = Number(param1.seed);
    if (!Number.isInteger(seed) || seed < 0 || seed > 399) {
      throw new Error("start_solo_battle: invalid seed " + param1.seed);
    }
    this._interf.play_type = mode;
    this._interf.cur_element = stage;
    this._play_mode = mode;
    this._play_id = stage;
    if (mode == "assault") {
      this._play_zone_stage = Number(param1.zoneStage) || 0;
    }
    this.saved_stims = this.clone(param1.stimsInBattle || {});
    this.battle_setup = {
      "seed": seed,
      "annihilators": Number(param1.annihilators) || 0,
      "teamName": param1.teamName != null ? String(param1.teamName) : this.user.name,
      "input": param1.input || null,
      "checkpoints": param1.checkpoints != null ? Boolean(param1.checkpoints) : null,
      "onEnd": param1.onEnd || null,
      "battleId": param1.battleId != null ? String(param1.battleId) : null
    };
    this.display_system.addChild(this.splash_wait);
    this.main_map_dl = true;
    this.main_map_dl_id = def.map;
    this.main_execute_after_map_dl = "start solo battle";
    this.init_map_downloading();
  }

  // PORT: design D4, client side. server_battle: the backend's pending battle the running battle is ({ battleId, mode,
  // PORT: stage }; init_game sets it from start_solo_battle's `battleId`, null for every other battle): end_game_fn
  // PORT: ends it on the backend instead of computing rewards (net_battle_end).
  server_battle: any = null;
  // PORT: the refusal ({ code, reason }) of the last battle.start the game sent, null while none (tools and tests
  // PORT: waiting for a battle to start stop at a refused start instead of waiting for it).
  battle_start_refused: any = null;

  // PORT: 'go' (the fight screen: career / missions / survival / boss) and 'assault_fight' (the assault screen) start
  // PORT: the battle on the backend: player.command battle.start { mode, stage | zone, teamName } instead of
  // PORT: prepare_game's local mutations and the 'battle_start' save (prepare_game -> continue_preparing_game ->
  // PORT: net_check_auc stay as the reference the rules module is proven against, tests/rules/battle-oracle.test.ts;
  // PORT: the interface no longer calls them). The server checks the conditions under which the button is shown,
  // PORT: applies the start costs to the canonical state (tiredness, attempts, stims taken, achievements, tasks, the
  // PORT: time refresh), keeps the battle pending and fixes its seed. The game adopts that state and starts the battle
  // PORT: through start_solo_battle (the map loads behind splash_wait as before) with the returned seed, stims,
  // PORT: annihilators, team name and zone stage; the live keyboard is recorded by LocalInput for battle.finish. A
  // PORT: refused start (VALIDATION_FAILED too_tired, level_too_low, ...) re-loads the profile and says why.
  net_battle_start(param1: any, param2: any): any {
    const mode: string = as3.str(param1);
    const id: number = Number(param2);
    const args: any = mode == "assault" ? { "mode": mode, "zone": id } : { "mode": mode, "stage": id };
    args.teamName = String(this.user.name);
    this.battle_start_refused = null;
    this.display_system.addChild(this.splash_wait);
    this.net_run("battle start", () => battleCommand(this.services.command("battle.start", args)), (answer: any) => {
      if (answer.refused) {
        as3.trace("battle start refused: " + answer.refused.code + " " + answer.refused.reason);
        this.battle_start_refused = answer.refused;
        this.net_battle_reload(battleRefusedText(answer.refused.reason), null);
        return;
      }
      const r: any = answer.outcome.result;
      this.net_battle_adopt(answer.outcome);
      this.PAPA_HERE = Boolean(r.papaHere);
      this.start_solo_battle({
        "mode": r.mode,
        "stage": r.stage,
        "zoneStage": r.zoneStage,
        "seed": r.seed,
        "stimsInBattle": r.stimsInBattle,
        "annihilators": r.annihilators,
        "teamName": r.teamName,
        "battleId": r.battleId
      });
    });
  }

  /** PORT: design D4 - adopts the canonical state of a battle command's response (CommandOutcome). */
  net_battle_adopt(param1: any): any {
    this.profile_revision = param1.revision;
    if (param1.serverTime) {
      this.user.unix_time = param1.serverTime;
    }
    if (param1.gameState != null && param1.gameState.format == GAME_STATE_FORMAT) {
      this.apply_state(param1.gameState);
    }
    this.refresh_temp_params();
  }

  /**
   * PORT: design D4 - the server did not carry a battle command out (a refused start, a battle no longer in progress, a
   * PORT: refused grave): the stored profile is loaded again, the screen is drawn from it and `param1` says why; then
   * PORT: `param2` (if any) goes on (the result screens of a battle that is over anyway).
   */
  net_battle_reload(param1: any, param2: any): any {
    this.net_run("reload profile", () => this.services.loadProfile(), (profile: any) => {
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      this.net_adopt_profile(profile);
      this.refresh_temp_params();
      this.net_notice(param1, 5000);
      if (param2) {
        param2();
      } else if (this._interf) {
        this._interf.render();
      }
    });
  }

  /**
   * PORT: design D4 - end_game_fn of a server battle (`param1` = the winner end_game_fn got): the battle ends on the
   * PORT: backend, which decides the outcome and the rewards. The surrender button (proc.cancel_fight: the recorded
   * PORT: input ends with a surrender) is battle.forfeit { battleId } - a loss, the start costs stay, as in the
   * PORT: original; any other end is battle.finish { battleId, claim: the outcome the game observed, log: the recorded
   * PORT: input in global ticks }, which the server may re-simulate. The legacy replay (REPLAY_STRING) was built by
   * PORT: end_game_fn before, so the manual save_replay feature is unchanged.
   */
  net_battle_end(param1: number): any {
    const battle: any = this.server_battle;
    const result: any = this.battle_result;
    this.server_battle = null;
    this.display_system.addChild(this.splash_wait);
    if (!result || battleSurrendered(result.log)) {
      this.net_run("battle forfeit", () => battleCommand(this.services.command("battle.forfeit", { "battleId": battle.battleId })), (answer: any) => {
        if (answer.outcome) {
          this.net_battle_adopt(answer.outcome);
        }
        this.execute_after = "init win or lose";
        this.success_saved();
      });
      return;
    }
    this.net_battle_finish({ "battleId": battle.battleId, "claim": result.outcome, "log": result.log });
  }

  /**
   * PORT: design D4 - battle.finish `param1` with the long per-request timeout of a re-simulation and the waiting line
   * PORT: "Проверка боя…". UNAVAILABLE 'simulation_unavailable' (the server could not verify the battle now; it stays
   * PORT: pending) is retried by the bridge like a busy server (battle-net.ts SimulationBusyError): the same command
   * PORT: again after the backoff, under the same opId (GameServices.command), so the server rewards the battle once.
   */
  net_battle_finish(param1: any): any {
    this.net_run("battle finish", () => {
      this.net_ui().setRetry(MSG_BATTLE_CHECK, null);
      return battleCommand(this.services.command("battle.finish", param1, { "timeoutMs": BATTLE_FINISH_TIMEOUT_MS }), true);
    }, (answer: any) => {
      this.net_battle_over(answer);
    }, BATTLE_FINISH_TIMEOUT_MS);
  }

  /**
   * PORT: design D4 - the answer of battle.finish: the state the server applied the outcome to is adopted, FWP.end_game
   * PORT: is the server's (the win, new level and surprise screens, the graves the server keeps for grave.search), and
   * PORT: the game goes on to the original result screens. The player sees the outcome the server applied: a claim
   * PORT: the re-simulation did not confirm ('rejected') shows the simulated result with a short message. An assault
   * PORT: win refreshes the zone list (the server captured the zone, or says why not) before the result screens. A
   * PORT: battle the server no longer had (INVALID_STATE: abandoned or replaced by another window) is a loss.
   */
  net_battle_over(param1: any): any {
    let _loc2_: any = undefined;
    if (param1.refused) {
      as3.trace("battle finish refused: " + param1.refused.code + " " + param1.refused.reason);
      this.end_game = serverEndGame(null, this.end_game);
      this.execute_after = "";
      this.net_battle_reload(MSG_BATTLE_NOT_COUNTED, () => {
        this._interf.init_win_or_lose();
      });
      return;
    }
    _loc2_ = param1.outcome.result;
    this.net_battle_adopt(param1.outcome);
    this.end_game = serverEndGame(_loc2_, this.end_game);
    this.last_win = _loc2_.win ? { "battleId": _loc2_.battleId, "mode": this.end_game.game_mode, "gold": _loc2_.granted ? Number(_loc2_.granted.gold) || 0 : 0 } : null;
    replayOfferResult(this, String(_loc2_.battleId), Boolean(this.end_game.win));
    this.last_grave_index = 0;
    if (_loc2_.refused) {
      this.net_notice(MSG_BATTLE_NOT_COUNTED, 5000);
    } else if (_loc2_.verdict == "rejected") {
      this.net_notice(MSG_BATTLE_REJECTED, 5000);
    }
    if (this.end_game.game_mode == "assault" && Boolean(this.end_game.win)) {
      if (!_loc2_.captured) {
        this.net_notice(MSG_ZONE_NOT_CAPTURED, 5000);
      }
      this.net_get_assault();
      return;
    }
    this.execute_after = "init win or lose";
    this.success_saved();
  }

  /**
   * PORT: design D4 - the search of grave `param1` (1-based, the gsearch_<n> button) after its progress bar ran: the
   * PORT: server draws the find from the graves of the last won battle it keeps, adds it to the canonical state and
   * PORT: refuses a second search of the same grave; the game adopts the state and shows the find (grave_result).
   */
  net_grave_search(param1: number): any {
    this.display_system.addChild(this.splash_wait);
    this.net_run("grave search", () => battleCommand(this.services.command("grave.search", { "index": Number(param1) })), (answer: any) => {
      if (answer.refused) {
        as3.trace("grave search refused: " + answer.refused.code + " " + answer.refused.reason);
        this.execute_after = "";
        this.net_battle_reload(MSG_GRAVE_REFUSED, null);
        return;
      }
      this.net_battle_adopt(answer.outcome);
      this.last_grave_index = Number(param1);
      this.grave_found = answer.outcome.result.id;
      this.grave_found_type = answer.outcome.result.ptype;
      this.execute_after = "init grave result";
      this.success_saved();
    });
  }

  // PORT: design D4 - the interface no longer calls prepare_game (-> continue_preparing_game -> net_check_auc and the
  // PORT: 'battle_start' save): 'go' / 'assault_fight' call net_battle_start. Kept as the original reference of the
  // PORT: rules' applyBattleStart (tests/rules/battle-oracle.test.ts).
  prepare_game(param1: boolean = false): any {
    param1 = Boolean(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    if (param1) {
      this._interf.play_type = "assault";
    }
    this._play_mode = this._interf.play_type;
    this._play_id = this._interf.cur_element;
    _loc2_ = 0;
    if (this.player.perks["less_tired"]) {
      _loc2_ = this.get_perk_effect("less_tired", this.player.perks.less_tired, "");
    }
    if (this.player.spec == 3) {
      _loc2_ = this.get_perk_effect("less_tired", 3, "");
    }
    this.player.tired += this.TIRED_INC - _loc2_;
    if (this._play_mode == "career") {
      ++this.player.career_attempt;
    }
    if (this._play_mode == "missions") {
      ++this.player.missions_attempt;
    }
    if (this._play_mode == "survival") {
      ++this.player.survival_attempt;
    }
    if (this._play_mode == "boss") {
      ++this.player.boss_attempt;
    }
    if (this.player.tired > 3599) {
      this.player.tired = 3599;
    }
    if (this.player.career_attempt > 50) {
      this.player.career_attempt = 50;
    }
    if (this.player.missions_attempt > 50) {
      this.player.missions_attempt = 50;
    }
    if (this.player.survival_attempt > 50) {
      this.player.survival_attempt = 50;
    }
    if (this.player.boss_attempt > 50) {
      this.player.boss_attempt = 50;
    }
    _loc3_ = 0;
    for (_loc4_ of as3.keys(this.player.stim_to_fight)) {
      this.player.tired += this.STIM_DEF[_loc4_].tired * this.player.stim_to_fight[_loc4_];
      _loc3_ += this.player.stim_to_fight[_loc4_];
    }
    this.player.achiev["stims"] += _loc3_;
    if (_loc3_ > 1 && this.player.cur_task == 6) {
      this.player.cur_task = 7;
    }
    if (this.player.tired < 0) {
      this.player.tired = 0;
    }
    this.saved_stims = this.clone(this.player.stim_to_fight);
    this.player.stim_to_fight = {};
    if (this._play_mode == "career") {
      _loc5_ = this.TEAMS_DEF["career" + this._play_id];
    }
    if (this._play_mode == "missions") {
      _loc5_ = this.TEAMS_DEF["mission" + this._play_id];
    }
    if (this._play_mode == "survival") {
      _loc5_ = this.TEAMS_DEF["survival" + this._play_id];
    }
    if (this._play_mode == "boss") {
      _loc5_ = this.TEAMS_DEF["boss" + this._play_id];
    }
    if (this._play_mode == "assault") {
      _loc5_ = this.TEAMS_DEF["assault" + this._play_id];
    }
    this.display_system.addChild(this.splash_wait);
    this.main_map_dl = true;
    this.main_map_dl_id = _loc5_.map;
    this.main_execute_after_map_dl = "continue preparing game";
    this.init_map_downloading();
  }

  continue_preparing_game(): any {
    // PORT: VK removed: `this.flashVars.api_id == 4329578` was always true in the offline build; the LOCAL_TEST
    // PORT: shortcut (init_game without the network) is gone.
    this.display_system.addChild(this.splash_wait);
    this.net_check_auc();
  }

  init_just_play(): any {
    this._play_mode = "just";
    this.display_system.addChild(this.splash_wait);
    this.main_map_dl = true;
    this.main_map_dl_id = this._interf.barracks.map;
    this.main_execute_after_map_dl = "continue preparing just play";
    this.init_map_downloading();
  }

  init_game(param1: any): any {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    this.REPLAY_STRING = "";
    // PORT: the setup of start_solo_battle (seed, annihilators, team name, input source); the menu flows have none.
    const setup: any = this.battle_setup || {};
    this.battle_setup = null;
    this.battle_result = null;
    // PORT: design D4 - the backend's pending battle this battle is (net_battle_start), ended by net_battle_end.
    this.server_battle = setup.battleId ? { "battleId": setup.battleId, "mode": param1.mode, "stage": param1.id } : null;
    if (param1.mode == "career") {
      _loc2_ = this.TEAMS_DEF["career" + param1.id];
    }
    if (param1.mode == "missions") {
      _loc2_ = this.TEAMS_DEF["mission" + param1.id];
    }
    if (param1.mode == "survival") {
      _loc2_ = this.TEAMS_DEF["survival" + param1.id];
    }
    if (param1.mode == "boss") {
      _loc2_ = this.TEAMS_DEF["boss" + param1.id];
    }
    if (param1.mode == "assault") {
      _loc2_ = this.TEAMS_DEF["assault" + param1.id];
    }
    if (param1.mode == "just") {
      _loc2_ = this._interf.barracks;
    }
    this._interf.remove_all();
    this._interf.init_game_menu();
    this.add_proc();
    this.add_world(_loc2_.map, _loc2_.water, _loc2_.bg_type, _loc2_.bg_mode);
    this.display_front_world.addChild(this._world.objects.water_front);
    this.display_back_world.addChild(this._world.objects.background);
    this.display_back_world.addChild(this._world.objects.bg_art);
    this.display_back_world.addChild(this._world.objects.water_back);
    this.display_back_world.addChild(this._world.objects.map);
    this.main_mode = "LOCAL_GAME";
    _loc3_ = {};
    _loc3_.weapons = {};
    _loc3_.worms = Math.min(2 + this.player.worms.value, _loc2_.worm_limit);
    _loc3_.color = this.C_BLUE;
    _loc3_.name = setup.teamName != null ? setup.teamName : this.user.name;
    _loc3_.cpu_level = 0;
    _loc4_ = {
      "attack": this.player.attack.value,
      "protect": this.player.protect.value,
      "health": 100 + 50 * this.player.health.value,
      "worms": 2 + this.player.worms.value,
      "tired": this.player.tired,
      "stim_to_fight": this.saved_stims,
      "impl": this.player.cur_impl
    };
    _loc5_ = this.get_affected_skills(_loc4_);
    _loc6_ = 1;
    while (_loc6_ <= _loc3_.worms) {
      _loc3_["worm" + _loc6_] = {
        "attack": _loc5_.attack,
        "protect": _loc5_.protect,
        "health": _loc5_.health,
        "spec": this.player.spec,
        "perks": {},
        "name": "Worm " + _loc6_
      };
      for (_loc8_ of as3.keys(this.player.perks)) {
        _loc3_["worm" + _loc6_].perks[_loc8_] = this.player.perks[_loc8_];
      }
      _loc6_++;
    }
    for (_loc7_ of as3.keys(this.player.weapons)) {
      _loc3_.weapons[_loc7_] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      _loc9_ = 1;
      while (_loc9_ <= this.player.weapons[_loc7_][0]) {
        ++_loc3_.weapons[_loc7_][this.player.weapons[_loc7_][_loc9_].range];
        _loc9_++;
      }
    }
    const annihilators: number = setup.annihilators != null ? setup.annihilators : this.win_auc_weapons.annihilator + this.auc_weapons.annihilator;
    if (annihilators != 0) {
      _loc3_.weapons["annihilator"] = [0, annihilators, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    }
    // PORT: every battle takes its input through the seam (and records it); the backend's seed replaces the roll.
    this.battle_init = {
      "mode": param1.mode,
      "stage": param1.id,
      "stimsInBattle": this.clone(this.saved_stims),
      "annihilators": annihilators,
      "teamName": _loc3_.name,
      "gameState": this.serialize_state()
    };
    if (param1.mode == "assault") {
      this.battle_init.zoneStage = Number(this._play_zone_stage);
    }
    this.battle_input = new BattleInput(setup.input || new LocalInput(this), { "checkpoints": setup.checkpoints != null ? Boolean(setup.checkpoints) : Boolean(this.battle_checkpoints) });
    this.battle_input.on_end = setup.onEnd || null;
    this._proc.battle_input = this.battle_input;
    this._proc.battle_seed = setup.seed != null ? setup.seed : null;
    this._proc.init_gameplay(_loc3_, _loc2_, param1.mode, param1.id);
    this.battle_init.seed = this._proc.battle_seed_used;
    this.battle_input.attach(this._proc);
    this.init_listeners();
  }

  prepare_replay(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    _loc1_ = this.REPLAY_STRING.split("!")[1];
    this.REPLAY_STRING_MOVES = this.REPLAY_STRING.split("!")[2];
    _loc2_ = "DD";
    if (this.REPLAY_STRING.search("!DD") != -1) {
      _loc2_ = "DD";
    }
    if (this.REPLAY_STRING.search("!DE") != -1) {
      _loc2_ = "DE";
    }
    _loc3_ = _loc1_.split(_loc2_)[1].charAt(0);
    if (_loc3_ == "c") {
      _loc3_ = "career";
    }
    if (_loc3_ == "m") {
      _loc3_ = "missions";
    }
    if (_loc3_ == "s") {
      _loc3_ = "survival";
    }
    if (_loc3_ == "b") {
      _loc3_ = "boss";
    }
    _loc4_ = this.decode_replay_number(_loc1_.split("I")[1].split("$")[0]);
    if (_loc3_ == "career") {
      _loc5_ = this.TEAMS_DEF["career" + _loc4_];
    }
    if (_loc3_ == "missions") {
      _loc5_ = this.TEAMS_DEF["mission" + _loc4_];
    }
    if (_loc3_ == "survival") {
      _loc5_ = this.TEAMS_DEF["survival" + _loc4_];
    }
    if (_loc3_ == "boss") {
      _loc5_ = this.TEAMS_DEF["boss" + _loc4_];
    }
    this.display_system.addChild(this.splash_wait);
    this.main_map_dl = true;
    this.main_map_dl_id = _loc5_.map;
    this.main_execute_after_map_dl = "init replay";
    this.init_map_downloading();
  }

  init_replay(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    let _loc16_: any = undefined;
    let _loc17_: any = undefined;
    let _loc18_: any = undefined;
    let _loc19_: any = undefined;
    let _loc20_: any = undefined;
    let _loc21_: any = undefined;
    let _loc22_: any = undefined;
    let _loc23_: any = undefined;
    let _loc24_: any = undefined;
    this.REPLAY = true;
    _loc1_ = this.REPLAY_STRING.split("!")[1];
    this.REPLAY_STRING_MOVES = this.REPLAY_STRING.split("!")[2];
    _loc2_ = "DD";
    if (this.REPLAY_STRING.search("!DD") != -1) {
      _loc2_ = "DD";
    }
    if (this.REPLAY_STRING.search("!DE") != -1) {
      _loc2_ = "DE";
    }
    _loc3_ = _loc1_.split(_loc2_)[1].charAt(0);
    if (_loc3_ == "c") {
      _loc3_ = "career";
    }
    if (_loc3_ == "m") {
      _loc3_ = "missions";
    }
    if (_loc3_ == "s") {
      _loc3_ = "survival";
    }
    if (_loc3_ == "b") {
      _loc3_ = "boss";
    }
    _loc4_ = this.decode_replay_number(_loc1_.split("I")[1].split("$")[0]);
    _loc5_ = this.decode_replay_number(_loc1_.split("R")[1].split("$")[0]);
    _loc6_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[0]);
    _loc7_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[1]);
    _loc8_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[2]);
    _loc9_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[3]);
    _loc10_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[4]);
    _loc11_ = this.decode_replay_number(_loc1_.split("P")[1].split("$")[5]);
    _loc12_ = _loc1_.split("S")[1].split("Z")[0];
    _loc13_ = _loc12_.split("$");
    _loc14_ = {};
    _loc15_ = 0;
    // PORT: perk record id 0 = the recorded team's own emg_teleport count (see proc.append_replay_param).
    let replayEmgTeleport: any = null;
    while (_loc15_ < _loc13_.length - 1) {
      _loc21_ = this.decode_replay_number(_loc13_[_loc15_].split("%")[0]);
      _loc22_ = this.decode_replay_number(_loc13_[_loc15_].split("%")[1]);
      for (_loc23_ of as3.keys(this.PERKS_DEF)) {
        if (this.PERKS_DEF[_loc23_].id == _loc21_) {
          _loc14_[_loc23_] = _loc22_;
        }
      }
      if (_loc21_ == 0) {
        replayEmgTeleport = _loc22_;
      }
      _loc15_++;
    }
    _loc16_ = this.decode_replay_number(_loc1_.split("Z")[1].split("$")[0]);
    _loc17_ = 0;
    if (_loc2_ == "DE") {
      _loc17_ = this.decode_replay_number(_loc1_.split("X")[1].split("$")[0]);
    }
    this.player_perks_saved = {};
    this.player_perks_saved = this.clone(this.player.perks);
    this.player.perks = {};
    this.player.perks = this.clone(_loc14_);
    this.player_tired_saved = this.player.tired;
    this.player.tired = _loc11_;
    // PORT: the original stored the implant in `this.player.impl_saved` (FWP.as:15951) while end_replay restores
    // PORT: `this.player_impl_saved` (FWP.as:16328, always 0 then): watching a replay silently removed the player's
    // PORT: implant and left a stray `impl_saved` field in the saved profile. Replays are reachable in the port
    // PORT: (end_game_fn keeps REPLAY_STRING), so the implant is saved where end_replay restores it from.
    this.player_impl_saved = this.player.cur_impl;
    this.player.cur_impl = _loc17_;
    this.player_skill_saved = this.player.settings.skill;
    this.player.settings.skill = _loc16_;
    if (_loc3_ == "career") {
      _loc18_ = this.TEAMS_DEF["career" + _loc4_];
    }
    if (_loc3_ == "missions") {
      _loc18_ = this.TEAMS_DEF["mission" + _loc4_];
    }
    if (_loc3_ == "survival") {
      _loc18_ = this.TEAMS_DEF["survival" + _loc4_];
    }
    if (_loc3_ == "boss") {
      _loc18_ = this.TEAMS_DEF["boss" + _loc4_];
    }
    this._interf.remove_all();
    this._interf.init_game_menu();
    this.add_proc();
    this.add_world(_loc18_.map, _loc18_.water, _loc18_.bg_type, _loc18_.bg_mode);
    this.display_front_world.addChild(this._world.objects.water_front);
    this.display_back_world.addChild(this._world.objects.background);
    this.display_back_world.addChild(this._world.objects.bg_art);
    this.display_back_world.addChild(this._world.objects.water_back);
    this.display_back_world.addChild(this._world.objects.map);
    this.main_mode = "LOCAL_GAME";
    _loc19_ = {};
    _loc19_.weapons = {};
    _loc19_.worms = Math.min(_loc10_, _loc18_.worm_limit);
    _loc19_.color = this.C_BLUE;
    _loc19_.name = "Игрок";
    _loc19_.cpu_level = 0;
    _loc15_ = 1;
    while (_loc15_ <= _loc19_.worms) {
      _loc19_["worm" + _loc15_] = {
        "attack": _loc6_,
        "protect": _loc7_,
        "health": _loc8_,
        "spec": _loc9_,
        "perks": {},
        "name": "Worm " + _loc15_
      };
      for (_loc24_ of as3.keys(this.player.perks)) {
        _loc19_["worm" + _loc15_].perks[_loc24_] = this.player.perks[_loc24_];
      }
      _loc15_++;
    }
    for (_loc20_ of as3.keys(this.WEAPONS_DEF)) {
      _loc19_.weapons[_loc20_] = [0, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100];
    }
    // PORT: the recorded emg_teleport count (perk record id 0) replaces the 100 copies: the engine uses that item on
    // PORT: its own when a worm drowns. Legacy strings without the record keep the original arsenal.
    if (replayEmgTeleport != null) {
      _loc19_.weapons["emg_teleport"][1] = replayEmgTeleport;
    }
    this.replay_cur_random = _loc5_;
    this._proc.init_gameplay(_loc19_, _loc18_, _loc3_, _loc4_);
    this.init_listeners();
  }

  end_game_fn(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    let _loc16_: any = undefined;
    let _loc17_: any = undefined;
    let _loc18_: any = undefined;
    if (this.REPLAY) {
      this.end_replay();
      return;
    }
    // PORT: a battle that ends in a target mode (surrender while a strike's target is being chosen) takes the target
    // cursor and the girder with it: the original left them (and the hidden system cursor) over the next screens.
    if (this._proc && this._proc.mousecontrol && this._interf && this._interf.CursorClip) {
      this._interf.hide_cursor("cursor");
      this._interf.hide_cursor("girder");
    }
    // PORT: the battle's result for the backend (recorded input, outcome, final hash), taken before the teardown.
    if (this.battle_input && this._proc && this._proc.battle_input == this.battle_input) {
      const seam: any = this.battle_input;
      this.battle_input = null;
      this.battle_result = seam.finish(this, param1);
      if (seam.on_end) {
        seam.on_end(this.battle_result);
      }
    }
    this.REPLAY_STRING = "!" + this._proc.PARAMS_RECORD + "!" + this._proc.ROUNDS_RECORD;
    _loc3_ = 0;
    _loc2_ = 0;
    while (_loc2_ <= this.REPLAY_STRING.length - 1) {
      _loc3_ += this.REPLAY_STRING.charCodeAt(_loc2_);
      _loc2_++;
    }
    this.REPLAY_STRING += "!" + this.encode_replay_number(_loc3_).charAt(0) + "!";
    // PORT: the offline build discarded the replay here (REPLAY_STRING = ""); replays are kept (backend replay store).
    this.init_data_arrays();
    _loc4_ = this.clone(this._proc.ach_killed);
    _loc5_ = this._proc.ach_paralyzed;
    _loc6_ = this._proc.ach_annihilated;
    _loc7_ = this._proc.ach_injure;
    _loc8_ = this._proc.ach_kill;
    _loc9_ = [0, {}];
    _loc10_ = 1;
    while (_loc10_ <= this._proc.OBJ[0]) {
      if (!this._proc.OBJ[_loc10_].free) {
        if (this._obj[this._proc.OBJ[_loc10_].fid].model == "G" && this._obj[this._proc.OBJ[_loc10_].fid].wteam != 1) {
          ++_loc9_[0];
          _loc9_[_loc9_[0]] = {
            "name": this._obj[this._proc.OBJ[_loc10_].fid].wname,
            "cpu_level": this._proc.teams[this._obj[this._proc.OBJ[_loc10_].fid].wteam].cpu_level
          };
        }
        this._proc.remove_object(this._obj[this._proc.OBJ[_loc10_].fid], _loc10_, false);
      }
      _loc10_++;
    }
    if (this._play_mode == "boss" || this._play_mode == "assault") {
      this.display_obj.removeChild(this._proc.ag_animation);
      this._proc.ag_animation = null;
    }
    this._proc = null;
    this.display_front_world.removeChild(this._world.objects.water_front);
    this.display_back_world.removeChild(this._world.objects.background);
    this.display_back_world.removeChild(this._world.objects.bg_art);
    this.display_back_world.removeChild(this._world.objects.map);
    this.display_back_world.removeChild(this._world.objects.water_back);
    this._world.destructor();
    this._world = null;
    if (this._play_mode == "just" || this._play_mode == "assault") {
      this.REPLAY_STRING = "";
    } else {
      this.replay_play_mode = this._play_mode;
      this.replay_play_id = this._play_id;
    }
    // PORT: the main screen's replay button offers this battle's replay once (runtime/replay-offer.ts): accepted or
    // PORT: declined, it is not offered again; a battle without a kept replay leaves no offer.
    offerReplay(this, this.REPLAY_STRING != "" ? { "battleId": this.server_battle ? String(this.server_battle.battleId) : null, "mode": String(this._play_mode || "just"), "stage": Number(this._play_id) || 0, "win": param1 == 1, "legacy": String(this.REPLAY_STRING) } : null);
    this._interf.remove_all();
    this._interf.init_mainframe();
    this.refresh_temp_params();
    this.main_mode = "IDLE";
    this.end_game = {
      "win": (param1 == 1 ? true : false),
      "exp_bonus": 0,
      "gold_bonus": 0,
      "rating_bonus": 0,
      "levels_upg": 0,
      "diam_bonus": 0,
      "skill_pts": 0,
      "perk_pts": 0,
      "surprise_weapons": [0, ""],
      "game_stage": this._play_id,
      "game_mode": this._play_mode,
      "graves": _loc9_
    };
    // PORT: design D4 - a battle the backend started (net_battle_start) ends on the backend: the server decides the
    // PORT: outcome and the rewards (net_battle_end), the game computes none. What follows is the original reward code
    // PORT: (the reference of the rules' applyBattleResult, tests/rules/battle-oracle.test.ts); it runs only for a
    // PORT: battle the backend did not start (a re-simulation, a rebuild), and a 'just' battle changes nothing here.
    if (this.server_battle) {
      this.net_battle_end(param1);
      return;
    }
    if (param1 == 1 && this._play_mode != "just") {
      ++this.player.today_wins;
      this.end_game.rating_bonus = 1 + this.get_rating_inc(this.player.today_wins, this.player.yest_wins);
      this.player.rating += this.end_game.rating_bonus;
      this.player.achiev["kill_p"] += _loc4_["p"];
      this.player.achiev["kill_e"] += _loc4_["e"];
      this.player.achiev["kill_a"] += _loc4_["a"];
      this.player.achiev["kill_c"] += _loc4_["c"];
      this.player.achiev["kill_h"] += _loc4_["h"];
      this.player.achiev["kill_f"] += _loc4_["f"];
      this.player.achiev["kill_l"] += _loc4_["l"];
      this.player.achiev["no_death"] += _loc8_ ? 0 : 1;
      this.player.achiev["no_injure"] += Boolean(_loc8_) || Boolean(_loc7_) ? 0 : 1;
      this.player.achiev["para"] += _loc5_;
      this.player.achiev["annih"] += _loc6_;
      if (this._play_mode == "career" && this.player.cur_task == 1) {
        this.player.cur_task = 2;
      }
      if (this._play_mode == "career" && (this.player.settings.skill == 4 || this.player.settings.skill == 14) && this.player.cur_task == 23) {
        this.player.cur_task = 24;
      }
      if (this._play_mode == "survival" && this.player.cur_task == 5) {
        this.player.cur_task = 6;
      }
      if (this._play_mode == "survival" && this.player.cur_task == 12) {
        this.player.cur_task = 13;
      }
      if (this._play_mode == "missions" && this.player.cur_task == 8) {
        this.player.cur_task = 9;
      }
      if (this._play_mode == "missions" && this.player.cur_task == 14) {
        this.player.cur_task = 15;
      }
      if (this._play_mode == "boss" && this.player.cur_task == 18) {
        this.player.cur_task = 19;
      }
      if (this._play_mode == "boss" && this.player.cur_task == 26) {
        this.player.cur_task = 27;
      }
      if (this._play_mode == "assault" && this.player.cur_task == 28) {
        this.player.cur_task = 29;
      }
      if (this._play_mode == "career") {
        _loc12_ = this.TEAMS_DEF["career" + this._play_id];
      }
      if (this._play_mode == "missions") {
        _loc12_ = this.TEAMS_DEF["mission" + this._play_id];
      }
      if (this._play_mode == "survival") {
        _loc12_ = this.TEAMS_DEF["survival" + this._play_id];
      }
      if (this._play_mode == "boss") {
        _loc12_ = this.TEAMS_DEF["boss" + this._play_id];
      }
      if (this._play_mode == "assault") {
        _loc12_ = this.TEAMS_DEF["assault" + this._play_id];
      }
      _loc13_ = 100;
      _loc14_ = 1;
      if (this._play_mode == "career") {
        if (this.player.career_attempt == 1 && this._play_id == this.player.career_max) {
          _loc14_ = 1.2;
        }
        if (this._play_id < this.player.career_max) {
          _loc14_ = 0.15;
        }
        _loc13_ = this.player.career_max;
        if (this.player.career_max == this._play_id) {
          ++this.player.career_max;
          this.player.career_attempt = 0;
        }
      } else if (this._play_mode == "missions") {
        if (this.player.missions_attempt == 1 && this._play_id == this.player.missions_max) {
          _loc14_ = 1.2;
        }
        if (this._play_id < this.player.missions_max) {
          _loc14_ = 0.15;
        }
        _loc13_ = this.player.missions_max;
        if (this.player.missions_max == this._play_id) {
          ++this.player.missions_max;
          this.player.missions_attempt = 0;
        }
      } else if (this._play_mode == "survival") {
        if (this.player.survival_attempt == 1 && this._play_id == this.player.survival_max) {
          _loc14_ = 1.2;
        }
        if (this._play_id < this.player.survival_max) {
          _loc14_ = 0.15;
        }
        _loc13_ = this.player.survival_max;
        if (this.player.survival_max == this._play_id) {
          ++this.player.survival_max;
          this.player.survival_attempt = 0;
        }
      } else if (this._play_mode == "boss") {
        if (this.player.boss_attempt == 1 && this._play_id == this.player.boss_max) {
          _loc14_ = 1.2;
        }
        if (this._play_id < this.player.boss_max) {
          _loc14_ = 0.15;
        }
        _loc13_ = this.player.boss_max;
        if (this.player.boss_max == this._play_id) {
          ++this.player.boss_max;
          this.player.boss_attempt = 0;
        }
      }
      _loc15_ = 1;
      if (this.player.perks["champion"]) {
        _loc15_ = this.get_perk_effect("champion", this.player.perks.champion, "");
      }
      _loc16_ = 1;
      if (this.player.perks["more_exp"]) {
        _loc16_ = this.get_perk_effect("more_exp", this.player.perks.more_exp, "");
      }
      if (this.player.spec == 4) {
        _loc16_ = this.get_perk_effect("more_exp", 3, "");
      }
      if (this._play_mode != "assault") {
        _loc17_ = Math.round(_loc12_.gold * _loc14_ * _loc15_ * this.get_skill_multi("gold") * this.get_hardcore_multi(this._play_id, _loc13_));
        _loc18_ = Math.round(_loc12_.exp * _loc14_ * _loc16_ * this.get_skill_multi("exp") * this.get_hardcore_multi(this._play_id, _loc13_));
      } else {
        _loc17_ = Math.round(_loc12_.gold * _loc15_);
        _loc18_ = Math.round(_loc12_.exp * _loc16_);
      }
      this.player.gold.value += _loc17_;
      this.player.exp.value += _loc18_;
      this.end_game.gold_bonus = _loc17_;
      this.end_game.exp_bonus = _loc18_;
      this.check_new_level();
    }
    if (this._play_mode != "just" && this._play_mode != "assault" && Boolean(this.end_game.win)) {
      this.execute_after = "init win or lose";
      this.init_save_params();
      return;
    }
    if (this._play_mode == "assault" && Boolean(this.end_game.win)) {
      ++this.player.achiev["assault"];
      this.net_get_assault();
      return;
    }
    if (this._play_mode != "just") {
      this._interf.init_win_or_lose();
    }
  }

  end_replay(): any {
    let _loc1_: any = undefined;
    this.REPLAY = false;
    this.REPLAY_STRING = "";
    this.init_data_arrays();
    _loc1_ = 1;
    while (_loc1_ <= this._proc.OBJ[0]) {
      if (!this._proc.OBJ[_loc1_].free) {
        this._proc.remove_object(this._obj[this._proc.OBJ[_loc1_].fid], _loc1_, false);
      }
      _loc1_++;
    }
    this._proc = null;
    this.display_front_world.removeChild(this._world.objects.water_front);
    this.display_back_world.removeChild(this._world.objects.background);
    this.display_back_world.removeChild(this._world.objects.map);
    this.display_back_world.removeChild(this._world.objects.water_back);
    this._world.destructor();
    this._world = null;
    this.player.perks = {};
    this.player.perks = this.clone(this.player_perks_saved);
    this.player_perks_saved = {};
    this.player.tired = this.player_tired_saved;
    this.player_tired_saved = 0;
    this.player.cur_impl = this.player_impl_saved;
    this.player_impl_saved = 0;
    this.player.settings.skill = this.player_skill_saved;
    this._interf.remove_all();
    this._interf.init_mainframe();
    this.refresh_temp_params();
    this.main_mode = "IDLE";
  }

  check_new_level(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    _loc2_ = 0;
    _loc3_ = this.get_player_level(this.player.exp.value);
    _loc4_ = this.player.level.value;
    if (_loc4_ != _loc3_) {
      _loc2_ = _loc3_ - _loc4_;
      this.player.rating += 100 * _loc2_;
      this.end_game.levels_upg = _loc2_;
      _loc5_ = 0;
      if (this.player.perks["education"]) {
        _loc5_ = this.get_perk_effect("education", this.player.perks.education, "");
      }
      _loc6_ = 0;
      if (this.player.perks["legioner"]) {
        _loc6_ = this.get_perk_effect("legioner", this.player.perks.legioner, "");
      }
      this.end_game.skill_pts = (15 + _loc6_) * _loc2_;
      this.end_game.perk_pts = (20 + _loc5_) * _loc2_;
      this.player.skill_pts.value += this.end_game.skill_pts;
      this.player.perk_pts.value += this.end_game.perk_pts;
      if (this.player.cur_task == 3) {
        this.player.cur_task = 4;
      }
      if (this.player.cur_task == 9) {
        this.player.cur_task = 10;
      }
      if (Math.floor(_loc3_ / 10) != Math.floor(_loc4_ / 10)) {
        this.end_game.diam_bonus = (Math.floor(_loc3_ / 10) - Math.floor(_loc4_ / 10)) * 10;
        this.player.diam.value += this.end_game.diam_bonus;
      }
      if (this.player.perks["surprise"]) {
        _loc7_ = 1;
        while (_loc7_ <= _loc2_) {
          _loc8_ = "";
          _loc9_ = this.ROUL_VER["surprise_" + this.player.perks["surprise"]];
          _loc10_ = new Array<any>(_loc9_[0], 0);
          _loc1_ = 1;
          while (_loc1_ <= 7) {
            _loc10_[_loc1_] = _loc9_[_loc1_] + _loc10_[_loc1_ - 1];
            _loc1_++;
          }
          _loc11_ = 0;
          _loc12_ = this.true_random(0, 99);
          _loc13_ = 0;
          while (_loc13_ <= 7) {
            if (_loc12_ < _loc10_[_loc13_]) {
              _loc11_ = _loc13_;
              break;
            }
            _loc13_++;
          }
          if (_loc11_ != 0) {
            do {
              _loc14_ = this.true_random(1, 200);
              for (_loc15_ of as3.keys(this.WEAPONS_DEF)) {
                if (this.WEAPONS_DEF[_loc15_].id == _loc14_ && this.WEAPONS_DEF[_loc15_].id != 61 && _loc15_.search("rez") == -1) {
                  if (this.WEAPONS_DEF[_loc15_].wpclass == _loc11_ && this.end_game.surprise_weapons[_loc7_ - 1] != _loc15_) {
                    _loc8_ = _loc15_;
                  }
                }
              }
            } while (_loc8_ == "");
            if (Boolean(this.player.weapons[_loc8_]) && this.player.weapons[_loc8_][0] < this.WEAPONS_DEF[_loc8_].limit) {
              ++this.player.weapons[_loc8_][0];
              this.player.weapons[_loc8_][this.player.weapons[_loc8_][0]] = { "range": 1, "stolen": false, "crafted": false };
            } else if (!this.player.weapons[_loc8_]) {
              this.player.weapons[_loc8_] = [1, { "range": 1, "stolen": false, "crafted": false }];
            }
            this.end_game.surprise_weapons[_loc7_] = _loc8_;
            ++this.end_game.surprise_weapons[0];
          }
          _loc7_++;
        }
      }
    }
  }

  init_listeners(): any {
    this.stage!.addEventListener(Event.ENTER_FRAME, this._keylistener, false, 0, true);
    this.stage!.addEventListener(KeyboardEvent.KEY_DOWN, this.keylistener_downkey, false, 0, true);
    this.stage!.addEventListener(KeyboardEvent.KEY_UP, this.keylistener_upkey, false, 0, true);
  }

  _keylistener(param1: any): any {
    this.KEY_PRESSED = "none";
    this.QUICK_WEAPON_PRESSED = "";
    this.SPACE_PRESSED = Boolean(this.KEYS_ARRAY[6]);
    if (this.KEYS_ARRAY[7]) {
      this.KEY_PRESSED = "1";
    }
    if (this.KEYS_ARRAY[8]) {
      this.KEY_PRESSED = "2";
    }
    if (this.KEYS_ARRAY[9]) {
      this.KEY_PRESSED = "3";
    }
    if (this.KEYS_ARRAY[10]) {
      this.KEY_PRESSED = "4";
    }
    if (this.KEYS_ARRAY[11]) {
      this.KEY_PRESSED = "5";
    }
    if (this.KEYS_ARRAY[12]) {
      this.KEY_PRESSED = "-";
    }
    if (this.KEYS_ARRAY[13]) {
      this.KEY_PRESSED = "+";
    }
    if (this.KEYS_ARRAY[2]) {
      this.KEY_PRESSED = "up";
    }
    if (this.KEYS_ARRAY[3]) {
      this.KEY_PRESSED = "down";
    }
    if (Boolean(this.KEYS_ARRAY[2]) && Boolean(this.KEYS_ARRAY[3])) {
      this.KEY_PRESSED = "none";
    }
    if (this.KEYS_ARRAY[0]) {
      this.KEY_PRESSED = "left";
    }
    if (this.KEYS_ARRAY[1]) {
      this.KEY_PRESSED = "right";
    }
    if (Boolean(this.KEYS_ARRAY[0]) && Boolean(this.KEYS_ARRAY[1])) {
      this.KEY_PRESSED = "none";
    }
    if (Boolean(this.KEYS_ARRAY[2]) && Boolean(this.KEYS_ARRAY[0])) {
      this.KEY_PRESSED = "up+left";
    }
    if (Boolean(this.KEYS_ARRAY[2]) && Boolean(this.KEYS_ARRAY[1])) {
      this.KEY_PRESSED = "up+right";
    }
    if (Boolean(this.KEYS_ARRAY[3]) && Boolean(this.KEYS_ARRAY[0])) {
      this.KEY_PRESSED = "down+left";
    }
    if (Boolean(this.KEYS_ARRAY[3]) && Boolean(this.KEYS_ARRAY[1])) {
      this.KEY_PRESSED = "down+right";
    }
    if (this.KEYS_ARRAY[4]) {
      this.KEY_PRESSED = "enter";
    }
    if (this.KEYS_ARRAY[5]) {
      this.KEY_PRESSED = "backspace";
    }
    if (Boolean(this.KEYS_ARRAY[4]) && Boolean(this.KEYS_ARRAY[5])) {
      this.KEY_PRESSED = "none";
    }
    if (this.KEYS_ARRAY[14]) {
      this.KEY_PRESSED = "escape";
    }
    if (this.KEYS_ARRAY[15]) {
      this.QUICK_WEAPON_PRESSED = "q";
    }
    if (this.KEYS_ARRAY[16]) {
      this.QUICK_WEAPON_PRESSED = "z";
    }
    if (this.KEYS_ARRAY[17]) {
      this.QUICK_WEAPON_PRESSED = "x";
    }
    if (this.KEYS_ARRAY[18]) {
      this.QUICK_WEAPON_PRESSED = "c";
    }
    if (this.KEYS_ARRAY[19]) {
      this.QUICK_WEAPON_PRESSED = "v";
    }
    if (this.KEYS_ARRAY[20]) {
      this.QUICK_WEAPON_PRESSED = "b";
    }
    if (this.KEYS_ARRAY[21]) {
      this.QUICK_WEAPON_PRESSED = "n";
    }
    if (this.KEYS_ARRAY[22]) {
      this.QUICK_WEAPON_PRESSED = "m";
    }
    if (this.KEYS_ARRAY[23]) {
      this.QUICK_WEAPON_PRESSED = "k";
    }
    if (this.KEYS_ARRAY[24]) {
      this.QUICK_WEAPON_PRESSED = "l";
    }
  }

  keylistener_downkey(param1: any): any {
    if (param1.keyCode == 37 || param1.keyCode == 65) {
      this.KEYS_ARRAY[0] = true;
    }
    if (param1.keyCode == 39 || param1.keyCode == 68) {
      this.KEYS_ARRAY[1] = true;
    }
    if (param1.keyCode == 38 || param1.keyCode == 87) {
      this.KEYS_ARRAY[2] = true;
    }
    if (param1.keyCode == 40 || param1.keyCode == 83) {
      this.KEYS_ARRAY[3] = true;
    }
    if (param1.keyCode == 13 || param1.keyCode == 69) {
      this.KEYS_ARRAY[4] = true;
    }
    if (param1.keyCode == 8 || param1.keyCode == 82) {
      this.KEYS_ARRAY[5] = true;
    }
    if (param1.keyCode == 32) {
      this.KEYS_ARRAY[6] = true;
    }
    if (param1.keyCode == 49) {
      this.KEYS_ARRAY[7] = true;
    }
    if (param1.keyCode == 50) {
      this.KEYS_ARRAY[8] = true;
    }
    if (param1.keyCode == 51) {
      this.KEYS_ARRAY[9] = true;
    }
    if (param1.keyCode == 52) {
      this.KEYS_ARRAY[10] = true;
    }
    if (param1.keyCode == 53) {
      this.KEYS_ARRAY[11] = true;
    }
    if (param1.keyCode == 189) {
      this.KEYS_ARRAY[12] = true;
    }
    if (param1.keyCode == 187) {
      this.KEYS_ARRAY[13] = true;
    }
    if (param1.keyCode == 27) {
      this.KEYS_ARRAY[14] = true;
    }
    if (param1.keyCode == 81) {
      this.KEYS_ARRAY[15] = true;
    }
    if (param1.keyCode == 90) {
      this.KEYS_ARRAY[16] = true;
    }
    if (param1.keyCode == 88) {
      this.KEYS_ARRAY[17] = true;
    }
    if (param1.keyCode == 67) {
      this.KEYS_ARRAY[18] = true;
    }
    if (param1.keyCode == 86) {
      this.KEYS_ARRAY[19] = true;
    }
    if (param1.keyCode == 66) {
      this.KEYS_ARRAY[20] = true;
    }
    if (param1.keyCode == 78) {
      this.KEYS_ARRAY[21] = true;
    }
    if (param1.keyCode == 77) {
      this.KEYS_ARRAY[22] = true;
    }
    if (param1.keyCode == 75) {
      this.KEYS_ARRAY[23] = true;
    }
    if (param1.keyCode == 76) {
      this.KEYS_ARRAY[24] = true;
    }
  }

  keylistener_upkey(param1: any): any {
    if (param1.keyCode == 37 || param1.keyCode == 65) {
      this.KEYS_ARRAY[0] = false;
    }
    if (param1.keyCode == 39 || param1.keyCode == 68) {
      this.KEYS_ARRAY[1] = false;
    }
    if (param1.keyCode == 38 || param1.keyCode == 87) {
      this.KEYS_ARRAY[2] = false;
    }
    if (param1.keyCode == 40 || param1.keyCode == 83) {
      this.KEYS_ARRAY[3] = false;
    }
    if (param1.keyCode == 13 || param1.keyCode == 69) {
      this.KEYS_ARRAY[4] = false;
    }
    if (param1.keyCode == 8 || param1.keyCode == 82) {
      this.KEYS_ARRAY[5] = false;
    }
    if (param1.keyCode == 32) {
      this.KEYS_ARRAY[6] = false;
    }
    if (param1.keyCode == 49) {
      this.KEYS_ARRAY[7] = false;
    }
    if (param1.keyCode == 50) {
      this.KEYS_ARRAY[8] = false;
    }
    if (param1.keyCode == 51) {
      this.KEYS_ARRAY[9] = false;
    }
    if (param1.keyCode == 52) {
      this.KEYS_ARRAY[10] = false;
    }
    if (param1.keyCode == 53) {
      this.KEYS_ARRAY[11] = false;
    }
    if (param1.keyCode == 189) {
      this.KEYS_ARRAY[12] = false;
    }
    if (param1.keyCode == 187) {
      this.KEYS_ARRAY[13] = false;
    }
    if (param1.keyCode == 27) {
      this.KEYS_ARRAY[14] = false;
    }
    if (param1.keyCode == 81) {
      this.KEYS_ARRAY[15] = false;
    }
    if (param1.keyCode == 90) {
      this.KEYS_ARRAY[16] = false;
    }
    if (param1.keyCode == 88) {
      this.KEYS_ARRAY[17] = false;
    }
    if (param1.keyCode == 67) {
      this.KEYS_ARRAY[18] = false;
    }
    if (param1.keyCode == 86) {
      this.KEYS_ARRAY[19] = false;
    }
    if (param1.keyCode == 66) {
      this.KEYS_ARRAY[20] = false;
    }
    if (param1.keyCode == 78) {
      this.KEYS_ARRAY[21] = false;
    }
    if (param1.keyCode == 77) {
      this.KEYS_ARRAY[22] = false;
    }
    if (param1.keyCode == 75) {
      this.KEYS_ARRAY[23] = false;
    }
    if (param1.keyCode == 76) {
      this.KEYS_ARRAY[24] = false;
    }
  }

  stage_focus(): any {
    this.stage!.focus = this;
  }

  check_ban(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    if (param1.length == 0) {
      return;
    }
    if (param1.charAt(0) == "2" && param1.charAt(3) == " ") {
      return;
    }
    if (param1.substr(0, 3) == "202") {
      return;
    }
    _loc2_ = -1;
    if (param1.charAt(0) == "2") {
      _loc2_ = Number(param1.substr(3, param1.length - 3));
    }
    _loc3_ = (_loc2_ - this.user.unix_time) / 60 / 60;
    if (_loc3_ < 0 && _loc2_ != -1) {
      return;
    }
    this.main_mode = "IDLE";
    _loc4_ = this._assets[FWP.ASSET_ID_FWP].get("_splash_ban");
    this.display_system.addChild(_loc4_);
    _loc4_.mes.text = param1.substr(0, 3) + " : " + this.BAN_DESC[Number(param1.substr(0, 3))];
    _loc4_.ban_time.htmlText = _loc2_ == -1 ? "Срок бана: <b>бессрочно</b>." : "Срок бана истечет через <b>" + Math.round(_loc3_) + "</b> час.";
  }

  /**
   * PORT: a BANNED answer of the own backend (BanInfo {code, reason, until}) shown with the original ban splash of
   * PORT: check_ban: the BAN_DESC text of the code (the server's reason for other codes) and the hours left.
   */
  net_show_ban(param1: any): any {
    let _loc2_: any = banHoursLeft(param1, as3.now());
    let _loc3_: any = this._assets[FWP.ASSET_ID_FWP].get("_splash_ban");
    this.main_mode = "IDLE";
    this.OOPS_BANNED = true;
    this.display_system.addChild(_loc3_);
    _loc3_.mes.text = param1.code + " : " + (this.BAN_DESC[param1.code] || param1.reason);
    _loc3_.ban_time.htmlText = _loc2_ == null ? "Срок бана: <b>бессрочно</b>." : "Срок бана истечет через <b>" + _loc2_ + "</b> час.";
    return _loc3_;
  }

  system_error(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    _loc2_ = this._assets[FWP.ASSET_ID_FWP].get("_splash_system_error");
    this.display_system.addChild(_loc2_);
    _loc2_.mes.text = param1;
    // PORT: the splash is returned so the backend bridge can add its restart button (net_error).
    return _loc2_;
  }

  encode_param(param1: number): any {
    param1 = Number(param1);
    if (param1 < 26) {
      return String.fromCharCode(65 + param1);
    }
    if (param1 < 52) {
      return String.fromCharCode(97 - 26 + param1);
    }
    if (param1 < 60) {
      return String.fromCharCode(48 - 52 + param1);
    }
    return "A";
  }

  decode_param(param1: any): number {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    _loc2_ = param1.charCodeAt(0);
    if (_loc2_ >= 48 && _loc2_ <= 57) {
      return Number(_loc2_ + 4);
    }
    if (_loc2_ >= 65 && _loc2_ <= 90) {
      return _loc2_ - 65;
    }
    if (_loc2_ >= 97 && _loc2_ <= 122) {
      return _loc2_ - 71;
    }
    return 0;
  }

  read_params(param1: any, param2: any): any {
  }

  read_storage(param1: any, param2: any = "my"): number {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    _loc3_ = param1;
    if (param2 != "my") {
      if (_loc3_.length == 0) {
        this.create_new_params("empty friend");
        return 0;
      }
    }
    _loc4_ = _loc3_.charCodeAt(249);
    _loc5_ = 0;
    if (_loc4_ == 116) {
      _loc5_ = this.read_storage_45(param1, param2);
    }
    if (this.player.flags["flag_spec"] == 1 && this.player.missions_max < 20) {
      this.player.flags = {};
    }
    if (this.player.exp.value > 0 && this.player.rating == 0) {
      this.player.rating = (this.player.level.value - 1) * 100;
    }
    if (param2 == "my") {
      if (this.banner_clicked == 1 && this.player.got_cmd_bonus == 0) {
        this.player.got_cmd_bonus = 1;
        this.player.diam.value += 10;
      }
    }
    this.read_params_end = true;
    if (param2 == "my" && (_loc4_ == 80 || _loc4_ == 111)) {
      this.player.spec = 0;
      this.player.diam.value += 10;
      this.player.last_reset = 0;
    }
    return Number(_loc5_);
  }

  read_storage_45(param1: any, param2: any = "my"): number {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc38_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    let _loc16_: any = undefined;
    let _loc17_: any = undefined;
    let _loc18_: any = undefined;
    let _loc19_: any = undefined;
    let _loc20_: any = undefined;
    let _loc21_: any = undefined;
    let _loc22_: any = undefined;
    let _loc23_: any = undefined;
    let _loc24_: any = undefined;
    let _loc25_: any = undefined;
    let _loc26_: any = undefined;
    let _loc27_: any = undefined;
    let _loc28_: any = undefined;
    let _loc29_: any = undefined;
    let _loc30_: any = undefined;
    let _loc31_: any = undefined;
    let _loc32_: any = undefined;
    let _loc33_: any = undefined;
    let _loc34_: any = undefined;
    let _loc35_: any = undefined;
    let _loc36_: any = undefined;
    _loc6_ = param1;
    if (param2 == "my") {
      _loc7_ = this.player;
    } else {
      _loc7_ = this.get_info;
      if (_loc6_.length == 0) {
        this.create_new_params("empty friend");
        return 0;
      }
    }
    if (_loc6_.length == 0) {
      return 1;
    }
    _loc8_ = false;
    if (_loc6_.length == 2000) {
      _loc8_ = true;
      _loc3_ = 0;
      while (_loc3_ < 2000) {
        _loc6_ += "A";
        _loc3_++;
      }
    }
    _loc9_ = new Array();
    _loc3_ = 0;
    while (_loc3_ <= 3999) {
      _loc9_[_loc3_] = this.decode_param(_loc6_.charAt(_loc3_));
      _loc3_++;
    }
    _loc10_ = new MD5();
    _loc11_ = "FWPCOOLLOLOLOL";
    _loc12_ = MD5.hash(_loc6_.substr(0, 4000 - 32));
    _loc13_ = _loc6_.substr(4000 - 32, 32);
    if (param2 == "my") {
      if (_loc12_ != _loc13_ && !_loc8_) {
        this.create_new_params("protect");
        this.check_ban("101");
        return 2;
      }
    }
    _loc7_.view_tutorial = _loc9_[1];
    _loc7_.tween = _loc9_[2];
    _loc7_.admin_state = _loc9_[3];
    _loc7_.inc_state = _loc9_[4];
    _loc7_.last_exp = _loc9_[5] + _loc9_[6] * 60 + _loc9_[7] * this.ret_60(2) + _loc9_[8] * this.ret_60(3) + _loc9_[9] * this.ret_60(4);
    _loc7_.exp = new ProtectedInt(_loc9_[11] + _loc9_[12] * 60 + _loc9_[13] * this.ret_60(2) + _loc9_[14] * this.ret_60(3) + _loc9_[15] * this.ret_60(4));
    _loc7_.level = new ProtectedInt(this.get_player_level(_loc7_.exp.value));
    _loc7_.gold = new ProtectedInt(_loc9_[16] + _loc9_[17] * 60 + _loc9_[18] * this.ret_60(2) + _loc9_[19] * this.ret_60(3) + _loc9_[20] * this.ret_60(4));
    _loc7_.diam = new ProtectedInt(_loc9_[21] + _loc9_[22] * 60 + _loc9_[23] * this.ret_60(2) + _loc9_[24] * this.ret_60(3) + _loc9_[25] * this.ret_60(4));
    _loc7_.tired = _loc9_[26] + _loc9_[27] * 60;
    _loc7_.last_tired = _loc9_[28] + _loc9_[29] * 60 + _loc9_[30] * this.ret_60(2) + _loc9_[31] * this.ret_60(3) + _loc9_[32] * this.ret_60(4) + _loc9_[33] * this.ret_60(5);
    _loc7_.last_enter = _loc9_[34] + _loc9_[35] * 60 + _loc9_[36] * this.ret_60(2) + _loc9_[37] * this.ret_60(3) + _loc9_[38] * this.ret_60(4) + _loc9_[39] * this.ret_60(5);
    _loc7_.perk_slots = _loc9_[40];
    if (_loc7_.perk_slots == 0) {
      _loc7_.perk_slots = Math.max(2, _loc14_);
    }
    _loc7_.diam_magnet = _loc9_[41];
    _loc7_.quick_weapons["z"] = _loc9_[42] + _loc9_[43] * 60;
    _loc7_.quick_weapons["x"] = _loc9_[44] + _loc9_[45] * 60;
    _loc7_.quick_weapons["c"] = _loc9_[46] + _loc9_[47] * 60;
    _loc7_.quick_weapons["v"] = _loc9_[48] + _loc9_[49] * 60;
    _loc7_.quick_weapons["b"] = _loc9_[50] + _loc9_[51] * 60;
    _loc7_.quick_weapons["n"] = _loc9_[52] + _loc9_[53] * 60;
    _loc7_.quick_weapons["m"] = _loc9_[54] + _loc9_[55] * 60;
    _loc7_.quick_weapons["k"] = _loc9_[56] + _loc9_[57] * 60;
    _loc7_.quick_weapons["l"] = _loc9_[58] + _loc9_[59] * 60;
    _loc7_.settings = {};
    _loc7_.settings.skill = _loc9_[62];
    _loc7_.spec = _loc9_[63];
    _loc7_.career_max = _loc9_[72] + _loc9_[73] * 60;
    _loc7_.career_attempt = _loc9_[74];
    _loc7_.missions_max = _loc9_[75] + _loc9_[76] * 60;
    _loc7_.missions_attempt = _loc9_[77];
    _loc7_.survival_max = _loc9_[78] + _loc9_[79] * 60;
    _loc7_.survival_attempt = _loc9_[80];
    if (_loc7_.survival_max == 0) {
      _loc7_.survival_max = 1;
    }
    _loc7_.boss_max = _loc9_[81] + _loc9_[82] * 60;
    _loc7_.boss_attempt = _loc9_[83];
    if (_loc7_.boss_max == 0) {
      _loc7_.boss_max = 1;
    }
    _loc7_.attack = new ProtectedInt(_loc9_[90] + _loc9_[91] * 60);
    _loc7_.protect = new ProtectedInt(_loc9_[92] + _loc9_[93] * 60);
    _loc7_.health = new ProtectedInt(_loc9_[94] + _loc9_[95] * 60);
    _loc7_.worms = new ProtectedInt(_loc9_[96]);
    _loc7_.perk_pts = new ProtectedInt(_loc9_[100] + _loc9_[101] * 60 + _loc9_[102] * this.ret_60(2));
    _loc7_.skill_pts = new ProtectedInt(_loc9_[103] + _loc9_[104] * 60 + _loc9_[105] * this.ret_60(2));
    _loc14_ = 0;
    _loc3_ = 109;
    while (_loc3_ <= 133) {
      _loc16_ = _loc9_[_loc3_] + _loc9_[_loc3_ + 1] * 60;
      _loc17_ = _loc9_[_loc3_ + 2];
      for (_loc5_ of as3.keys(this.PERKS_DEF)) {
        if (this.PERKS_DEF[_loc5_].id == _loc16_ && _loc16_ != 0) {
          _loc7_.perks[_loc5_] = _loc17_;
          _loc14_++;
          break;
        }
      }
      _loc3_ += 3;
    }
    _loc3_ = 139;
    while (_loc3_ <= 148) {
      _loc18_ = _loc9_[_loc3_];
      _loc19_ = _loc3_ - 138;
      for (_loc5_ of as3.keys(this.STIM_DEF)) {
        if (this.STIM_DEF[_loc5_].id == _loc19_) {
          _loc7_.stim_behave[_loc5_] = _loc18_;
        }
      }
      _loc3_++;
    }
    _loc3_ = 149;
    while (_loc3_ <= 158) {
      _loc20_ = _loc9_[_loc3_];
      _loc21_ = _loc3_ - 148;
      for (_loc5_ of as3.keys(this.STIM_DEF)) {
        if (this.STIM_DEF[_loc5_].id == _loc21_) {
          _loc7_.stim_to_fight[_loc5_] = _loc20_;
        }
      }
      _loc3_++;
    }
    for (_loc15_ of as3.keys(_loc7_.quick_weapons)) {
      _loc22_ = false;
      for (_loc23_ of as3.keys(_loc7_.weapons)) {
        if (this.WEAPONS_DEF[_loc23_].id == _loc7_.quick_weapons[_loc15_]) {
          _loc22_ = _loc7_.weapons[_loc23_] > 0;
        }
      }
      if (!_loc22_) {
        _loc7_.quick_weapons[_loc15_] = 0;
      }
    }
    _loc7_.achiev["kill_p"] = _loc9_[500] + _loc9_[501] * 60 + _loc9_[502] * this.ret_60(2);
    _loc7_.achiev["kill_e"] = _loc9_[503] + _loc9_[504] * 60 + _loc9_[505] * this.ret_60(2);
    _loc7_.achiev["kill_a"] = _loc9_[506] + _loc9_[507] * 60 + _loc9_[508] * this.ret_60(2);
    _loc7_.achiev["kill_c"] = _loc9_[509] + _loc9_[510] * 60 + _loc9_[511] * this.ret_60(2);
    _loc7_.achiev["kill_h"] = _loc9_[512] + _loc9_[513] * 60 + _loc9_[514] * this.ret_60(2);
    _loc7_.achiev["kill_f"] = _loc9_[515] + _loc9_[516] * 60 + _loc9_[517] * this.ret_60(2);
    _loc7_.achiev["kill_l"] = _loc9_[518] + _loc9_[519] * 60 + _loc9_[520] * this.ret_60(2);
    _loc7_.achiev["assault"] = _loc9_[521] + _loc9_[522] * 60 + _loc9_[523] * this.ret_60(2);
    _loc7_.achiev["no_death"] = _loc9_[524] + _loc9_[525] * 60 + _loc9_[526] * this.ret_60(2);
    _loc7_.achiev["no_injure"] = _loc9_[527] + _loc9_[528] * 60 + _loc9_[529] * this.ret_60(2);
    _loc7_.achiev["para"] = _loc9_[530] + _loc9_[531] * 60 + _loc9_[532] * this.ret_60(2);
    _loc7_.achiev["annih"] = _loc9_[533] + _loc9_[534] * 60 + _loc9_[535] * this.ret_60(2);
    _loc7_.achiev["stims"] = _loc9_[536] + _loc9_[537] * 60 + _loc9_[538] * this.ret_60(2);
    _loc7_.achiev["buys"] = _loc9_[539] + _loc9_[540] * 60 + _loc9_[541] * this.ret_60(2);
    _loc7_.achiev["steal"] = _loc9_[542] + _loc9_[543] * 60 + _loc9_[544] * this.ret_60(2);
    _loc7_.achiev["crafted"] = _loc9_[545] + _loc9_[546] * 60 + _loc9_[547] * this.ret_60(2);
    _loc7_.achiev["upgraded"] = _loc9_[548] + _loc9_[549] * 60 + _loc9_[550] * this.ret_60(2);
    _loc7_.achiev["wins"] = _loc9_[551] + _loc9_[552] * 60 + _loc9_[553] * this.ret_60(2);
    _loc7_.achiev["days"] = _loc9_[554] + _loc9_[555] * 60 + _loc9_[556] * this.ret_60(2);
    _loc7_.achiev["career"] = _loc7_.career_max - 1;
    _loc7_.achiev["missions"] = _loc7_.missions_max - 1;
    _loc7_.achiev["survival"] = _loc7_.survival_max - 1;
    _loc7_.achiev["boss"] = _loc7_.boss_max - 1;
    _loc7_["a_rank"] = this.get_achiev_rank(_loc7_.achiev);
    _loc7_.last_achiev = {
      "career": 0,
      "missions": 0,
      "survival": 0,
      "boss": 0,
      "assault": 0,
      "no_death": 0,
      "no_injure": 0,
      "kill_p": 0,
      "kill_e": 0,
      "kill_a": 0,
      "kill_c": 0,
      "kill_h": 0,
      "kill_f": 0,
      "kill_l": 0,
      "para": 0,
      "annih": 0,
      "recipes": 0,
      "stims": 0,
      "buys": 0,
      "steal": 0,
      "crafted": 0,
      "upgraded": 0,
      "wins": 0,
      "days": 0
    };
    _loc7_.last_achiev["kill_p"] = _loc9_[558];
    _loc7_.last_achiev["kill_e"] = _loc9_[559];
    _loc7_.last_achiev["kill_a"] = _loc9_[560];
    _loc7_.last_achiev["kill_c"] = _loc9_[561];
    _loc7_.last_achiev["kill_h"] = _loc9_[562];
    _loc7_.last_achiev["kill_f"] = _loc9_[563];
    _loc7_.last_achiev["kill_l"] = _loc9_[564];
    _loc7_.last_achiev["assault"] = _loc9_[565];
    _loc7_.last_achiev["no_death"] = _loc9_[566];
    _loc7_.last_achiev["no_injure"] = _loc9_[567];
    _loc7_.last_achiev["para"] = _loc9_[568];
    _loc7_.last_achiev["annih"] = _loc9_[569];
    _loc7_.last_achiev["stims"] = _loc9_[570];
    _loc7_.last_achiev["buys"] = _loc9_[571];
    _loc7_.last_achiev["steal"] = _loc9_[572];
    _loc7_.last_achiev["crafted"] = _loc9_[573];
    _loc7_.last_achiev["upgraded"] = _loc9_[574];
    _loc7_.last_achiev["wins"] = _loc9_[575];
    _loc7_.last_achiev["days"] = _loc9_[576];
    _loc7_.last_achiev_range = _loc9_[577];
    _loc7_.achiev_rating_bonus = _loc9_[578] + _loc9_[579] * 60 + _loc9_[580] * this.ret_60(2);
    _loc7_.last_achiev["survival"] = _loc9_[581];
    _loc7_.last_achiev["boss"] = _loc9_[582];
    _loc7_.last_achiev["recipes"] = _loc9_[583];
    _loc7_.last_achiev["career"] = _loc9_[584];
    _loc7_.last_achiev["missions"] = _loc9_[585];
    _loc3_ = 750;
    while (_loc3_ <= 798) {
      _loc24_ = _loc9_[_loc3_];
      _loc25_ = _loc3_ - 749;
      for (_loc5_ of as3.keys(this.FLAGS_DEF)) {
        if (this.FLAGS_DEF[_loc5_].id == _loc25_) {
          _loc7_.flags[_loc5_] = _loc24_;
        }
      }
      _loc3_++;
    }
    _loc7_.cur_flag = _loc9_[799];
    _loc3_ = 800;
    while (_loc3_ <= 848) {
      _loc26_ = _loc9_[_loc3_];
      _loc27_ = _loc3_ - 799;
      for (_loc5_ of as3.keys(this.RIPS_DEF)) {
        if (this.RIPS_DEF[_loc5_].id == _loc27_) {
          _loc7_.rip[_loc5_] = _loc26_;
        }
      }
      _loc3_++;
    }
    _loc7_.cur_rip = _loc9_[849];
    _loc7_.last_reset = _loc9_[853] + _loc9_[854] * 60;
    _loc7_.today_steals = _loc9_[900];
    _loc7_.last_steal = _loc9_[901] + _loc9_[902] * 60 + _loc9_[903] * this.ret_60(2) + _loc9_[904] * this.ret_60(3) + _loc9_[905] * this.ret_60(4) + _loc9_[906] * this.ret_60(5);
    _loc7_.rating = _loc9_[910] + _loc9_[911] * 60 + _loc9_[912] * this.ret_60(2) + _loc9_[913] * this.ret_60(3) + _loc9_[914] * this.ret_60(4);
    _loc7_.today_date = _loc9_[916] + _loc9_[917] * 60 + _loc9_[918] * this.ret_60(2) + _loc9_[919] * this.ret_60(3) + _loc9_[920] * this.ret_60(4) + _loc9_[921] * this.ret_60(5);
    _loc7_.today_wins = _loc9_[922] + _loc9_[923] * 60;
    _loc7_.yest_wins = _loc9_[924] + _loc9_[925] * 60;
    _loc3_ = 1;
    while (_loc3_ <= 5) {
      _loc7_["worm_name" + _loc3_] = "";
      _loc4_ = 0;
      while (_loc4_ <= 14) {
        _loc28_ = _loc9_[950 + _loc4_ * 2 + (_loc3_ - 1) * 30] + _loc9_[951 + _loc4_ * 2 + (_loc3_ - 1) * 30] * 60;
        if (_loc28_ != 0) {
          _loc7_["worm_name" + _loc3_] = _loc7_["worm_name" + _loc3_].concat(String.fromCharCode(_loc28_));
        }
        _loc4_++;
      }
      _loc3_++;
    }
    _loc3_ = 1200;
    while (_loc3_ <= 1248) {
      _loc29_ = _loc9_[_loc3_];
      _loc30_ = _loc3_ - 1199;
      for (_loc5_ of as3.keys(this.IMPL_DEF)) {
        if (this.IMPL_DEF[_loc5_].id == _loc30_) {
          _loc7_.impl[_loc5_] = _loc29_;
        }
      }
      _loc3_++;
    }
    _loc7_.cur_impl = _loc9_[1249];
    _loc7_.last_papa = _loc9_[1250] + _loc9_[1251] * 60 + _loc9_[1252] * this.ret_60(2) + _loc9_[1253] * this.ret_60(3) + _loc9_[1254] * this.ret_60(4) + _loc9_[1255] * this.ret_60(5);
    _loc7_.papa_asked = _loc9_[1256];
    _loc7_.settings.no_animation = _loc9_[1257];
    _loc3_ = 1260;
    while (_loc3_ <= 1299) {
      _loc31_ = _loc9_[_loc3_];
      _loc32_ = _loc3_ - 1259;
      for (_loc5_ of as3.keys(this.OBJ_DEF)) {
        if (this.OBJ_DEF[_loc5_].id == _loc32_) {
          _loc7_.objs[_loc5_] = _loc31_;
        }
      }
      _loc3_++;
    }
    _loc7_.recipes[0] = this.CRAFT_DEF[0];
    _loc3_ = 1300;
    while (_loc3_ <= 1450) {
      _loc7_.recipes[_loc3_ - 1299] = _loc9_[_loc3_];
      _loc3_++;
    }
    _loc7_.today_craft_sells = _loc9_[1451];
    _loc7_.next_assault_payment = _loc9_[1470] + _loc9_[1471] * 60 + _loc9_[1472] * this.ret_60(2) + _loc9_[1473] * this.ret_60(3) + _loc9_[1474] * this.ret_60(4) + _loc9_[1475] * this.ret_60(5);
    _loc7_.cur_task = _loc9_[1480];
    _loc7_.last_presented_task = _loc9_[1481];
    if (_loc7_.cur_task == 0) {
      _loc7_.cur_task = 1;
    }
    if (_loc7_.last_presented_task == 0) {
      _loc7_.last_presented_task = 1;
    }
    _loc7_.got_cmd_bonus = _loc9_[1482];
    _loc7_.weapons = {};
    _loc3_ = 1900;
    loop21: for (; _loc3_ < 3900; _loc3_ += 10) {
      if (_loc3_ == 1900 || _loc3_ == 2000) {
        _loc5_ = 0;
        while (_loc5_ <= 9) {
          _loc5_++;
        }
      }
      _loc33_ = (_loc3_ - 1900) / 10 + 1;
      // PORT: FFDec rendered a hasnext2/nextname loop here as `do { for(_loc5_ in _loc38_) {} continue loop21; } while(WEAPONS_DEF[_loc5_].id != _loc33_)`,
      // PORT: which would always skip the slot. Restored intent: find the WEAPONS_DEF key whose id == _loc33_; skip the slot if none matches.
      _loc38_ = false;
      for (_loc5_ of as3.keys(this.WEAPONS_DEF)) {
        if (this.WEAPONS_DEF[_loc5_].id == _loc33_) {
          _loc38_ = true;
          break;
        }
      }
      if (!_loc38_) {
        continue loop21;
      }
      _loc7_.weapons[_loc5_] = [0, {}];
      if (_loc5_.search("rez") == -1) {
        _loc4_ = 0;
        while (_loc4_ <= 9) {
          if (_loc9_[_loc3_ + _loc4_] > 0) {
            _loc34_ = _loc9_[_loc3_ + _loc4_] > 10;
            _loc35_ = _loc9_[_loc3_ + _loc4_] > 20;
            _loc36_ = _loc9_[_loc3_ + _loc4_];
            if (_loc35_) {
              _loc34_ = false;
              _loc36_ -= 20;
            }
            if (_loc34_) {
              _loc36_ -= 10;
            }
            ++_loc7_.weapons[_loc5_][0];
            _loc7_.weapons[_loc5_][_loc7_.weapons[_loc5_][0]] = { "range": _loc36_, "stolen": _loc34_, "crafted": _loc35_ };
          }
          _loc4_++;
        }
      }
    }
    return 0;
  }

  read_map_info(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc2_ = new Array();
    _loc3_ = 0;
    while (_loc3_ <= 2700) {
      _loc2_[_loc3_] = param1.charCodeAt(_loc3_);
      _loc3_++;
    }
    this.map_info = new Array();
    _loc3_ = 1;
    while (_loc3_ <= 60) {
      this.map_info[_loc3_] = new Array();
      _loc4_ = 1;
      while (_loc4_ <= 45) {
        this.map_info[_loc3_][_loc4_] = _loc2_[(_loc3_ - 1) * 45 + _loc4_] - 48;
        _loc4_++;
      }
      _loc3_++;
    }
  }

  write_storage(): any {
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    let _loc16_: any = undefined;
    _loc1_ = new Array();
    _loc2_ = 0;
    while (_loc2_ <= 3999) {
      _loc1_[_loc2_] = 0;
      _loc2_++;
    }
    _loc1_[1] += this.player.view_tutorial;
    _loc1_[2] += this.player.tween;
    _loc1_[3] += this.player.admin_state;
    if (this.player.inc_state < this.LAST_CONTEST_STATE && this.player.level.value <= 10) {
      this.player.inc_state = this.LAST_CONTEST_STATE;
      this.player.last_exp = this.player.exp.value;
    }
    _loc1_[4] += this.player.inc_state;
    _loc1_[5] += this.return_bytes(this.player.last_exp)[0];
    _loc1_[6] += this.return_bytes(this.player.last_exp)[1];
    _loc1_[7] += this.return_bytes(this.player.last_exp)[2];
    _loc1_[8] += this.return_bytes(this.player.last_exp)[3];
    _loc1_[9] += this.return_bytes(this.player.last_exp)[4];
    _loc1_[11] += this.return_bytes(this.player.exp.value)[0];
    _loc1_[12] += this.return_bytes(this.player.exp.value)[1];
    _loc1_[13] += this.return_bytes(this.player.exp.value)[2];
    _loc1_[14] += this.return_bytes(this.player.exp.value)[3];
    _loc1_[15] += this.return_bytes(this.player.exp.value)[4];
    _loc1_[16] += this.return_bytes(this.player.gold.value)[0];
    _loc1_[17] += this.return_bytes(this.player.gold.value)[1];
    _loc1_[18] += this.return_bytes(this.player.gold.value)[2];
    _loc1_[19] += this.return_bytes(this.player.gold.value)[3];
    _loc1_[20] += this.return_bytes(this.player.gold.value)[4];
    _loc1_[21] += this.return_bytes(this.player.diam.value)[0];
    _loc1_[22] += this.return_bytes(this.player.diam.value)[1];
    _loc1_[23] += this.return_bytes(this.player.diam.value)[2];
    _loc1_[24] += this.return_bytes(this.player.diam.value)[3];
    _loc1_[25] += this.return_bytes(this.player.diam.value)[4];
    _loc1_[26] += this.return_bytes(this.player.tired)[0];
    _loc1_[27] += this.return_bytes(this.player.tired)[1];
    _loc1_[28] += this.return_bytes(this.player.last_tired)[0];
    _loc1_[29] += this.return_bytes(this.player.last_tired)[1];
    _loc1_[30] += this.return_bytes(this.player.last_tired)[2];
    _loc1_[31] += this.return_bytes(this.player.last_tired)[3];
    _loc1_[32] += this.return_bytes(this.player.last_tired)[4];
    _loc1_[33] += this.return_bytes(this.player.last_tired)[5];
    _loc1_[34] += this.return_bytes(this.player.last_enter)[0];
    _loc1_[35] += this.return_bytes(this.player.last_enter)[1];
    _loc1_[36] += this.return_bytes(this.player.last_enter)[2];
    _loc1_[37] += this.return_bytes(this.player.last_enter)[3];
    _loc1_[38] += this.return_bytes(this.player.last_enter)[4];
    _loc1_[39] += this.return_bytes(this.player.last_enter)[5];
    _loc1_[40] += this.player.perk_slots;
    _loc1_[41] += this.player.diam_magnet;
    _loc1_[42] += this.return_bytes(this.player.quick_weapons["z"])[0];
    _loc1_[43] += this.return_bytes(this.player.quick_weapons["z"])[1];
    _loc1_[44] += this.return_bytes(this.player.quick_weapons["x"])[0];
    _loc1_[45] += this.return_bytes(this.player.quick_weapons["x"])[1];
    _loc1_[46] += this.return_bytes(this.player.quick_weapons["c"])[0];
    _loc1_[47] += this.return_bytes(this.player.quick_weapons["c"])[1];
    _loc1_[48] += this.return_bytes(this.player.quick_weapons["v"])[0];
    _loc1_[49] += this.return_bytes(this.player.quick_weapons["v"])[1];
    _loc1_[50] += this.return_bytes(this.player.quick_weapons["b"])[0];
    _loc1_[51] += this.return_bytes(this.player.quick_weapons["b"])[1];
    _loc1_[52] += this.return_bytes(this.player.quick_weapons["n"])[0];
    _loc1_[53] += this.return_bytes(this.player.quick_weapons["n"])[1];
    _loc1_[54] += this.return_bytes(this.player.quick_weapons["m"])[0];
    _loc1_[55] += this.return_bytes(this.player.quick_weapons["m"])[1];
    _loc1_[56] += this.return_bytes(this.player.quick_weapons["k"])[0];
    _loc1_[57] += this.return_bytes(this.player.quick_weapons["k"])[1];
    _loc1_[58] += this.return_bytes(this.player.quick_weapons["l"])[0];
    _loc1_[59] += this.return_bytes(this.player.quick_weapons["l"])[1];
    _loc1_[62] += this.player.settings.skill;
    _loc1_[63] += this.player.spec;
    _loc1_[72] += this.return_bytes(this.player.career_max)[0];
    _loc1_[73] += this.return_bytes(this.player.career_max)[1];
    _loc1_[74] += this.player.career_attempt;
    _loc1_[75] += this.return_bytes(this.player.missions_max)[0];
    _loc1_[76] += this.return_bytes(this.player.missions_max)[1];
    _loc1_[77] += this.player.missions_attempt;
    _loc1_[78] += this.return_bytes(this.player.survival_max)[0];
    _loc1_[79] += this.return_bytes(this.player.survival_max)[1];
    _loc1_[80] += this.player.survival_attempt;
    _loc1_[81] += this.return_bytes(this.player.boss_max)[0];
    _loc1_[82] += this.return_bytes(this.player.boss_max)[1];
    _loc1_[83] += this.player.boss_attempt;
    _loc1_[90] += this.return_bytes(this.player.attack.value)[0];
    _loc1_[91] += this.return_bytes(this.player.attack.value)[1];
    _loc1_[92] += this.return_bytes(this.player.protect.value)[0];
    _loc1_[93] += this.return_bytes(this.player.protect.value)[1];
    _loc1_[94] += this.return_bytes(this.player.health.value)[0];
    _loc1_[95] += this.return_bytes(this.player.health.value)[1];
    _loc1_[96] += this.player.worms.value;
    _loc1_[100] += this.return_bytes(this.player.perk_pts.value)[0];
    _loc1_[101] += this.return_bytes(this.player.perk_pts.value)[1];
    _loc1_[102] += this.return_bytes(this.player.perk_pts.value)[2];
    _loc1_[103] += this.return_bytes(this.player.skill_pts.value)[0];
    _loc1_[104] += this.return_bytes(this.player.skill_pts.value)[1];
    _loc1_[105] += this.return_bytes(this.player.skill_pts.value)[2];
    _loc2_ = 109;
    for (_loc3_ of as3.keys(this.player.perks)) {
      _loc1_[_loc2_] += this.return_bytes(this.PERKS_DEF[_loc3_].id)[0];
      _loc1_[_loc2_ + 1] += this.return_bytes(this.PERKS_DEF[_loc3_].id)[1];
      _loc1_[_loc2_ + 2] += this.player.perks[_loc3_];
      _loc2_ += 3;
    }
    _loc1_[139] += this.player.stim_behave.stimpack ? this.player.stim_behave.stimpack : 0;
    _loc1_[140] += this.player.stim_behave.jet ? this.player.stim_behave.jet : 0;
    _loc1_[141] += this.player.stim_behave.buffout ? this.player.stim_behave.buffout : 0;
    _loc1_[142] += this.player.stim_behave.superstimpack ? this.player.stim_behave.superstimpack : 0;
    _loc1_[143] += this.player.stim_behave.psycho ? this.player.stim_behave.psycho : 0;
    _loc1_[144] += this.player.stim_behave.fruit ? this.player.stim_behave.fruit : 0;
    _loc1_[149] += this.player.stim_to_fight.stimpack ? this.player.stim_to_fight.stimpack : 0;
    _loc1_[150] += this.player.stim_to_fight.jet ? this.player.stim_to_fight.jet : 0;
    _loc1_[151] += this.player.stim_to_fight.buffout ? this.player.stim_to_fight.buffout : 0;
    _loc1_[152] += this.player.stim_to_fight.superstimpack ? this.player.stim_to_fight.superstimpack : 0;
    _loc1_[153] += this.player.stim_to_fight.psycho ? this.player.stim_to_fight.psycho : 0;
    _loc1_[154] += this.player.stim_to_fight.fruit ? this.player.stim_to_fight.fruit : 0;
    _loc1_[249] += 45;
    for (_loc4_ of as3.keys(this.player.achiev)) {
      _loc13_ = 1;
      while (_loc13_ <= this.ACHIEV_DEF[0]) {
        if (this.ACHIEV_DEF[_loc13_].id == _loc4_) {
          if (this.player.achiev[_loc4_] > this.ACHIEV_DEF[_loc13_].levels[15]) {
            this.player.achiev[_loc4_] = this.ACHIEV_DEF[_loc13_].levels[15];
          }
        }
        _loc13_++;
      }
    }
    _loc1_[500] += this.return_bytes(this.player.achiev["kill_p"])[0];
    _loc1_[501] += this.return_bytes(this.player.achiev["kill_p"])[1];
    _loc1_[502] += this.return_bytes(this.player.achiev["kill_p"])[2];
    _loc1_[503] += this.return_bytes(this.player.achiev["kill_e"])[0];
    _loc1_[504] += this.return_bytes(this.player.achiev["kill_e"])[1];
    _loc1_[505] += this.return_bytes(this.player.achiev["kill_e"])[2];
    _loc1_[506] += this.return_bytes(this.player.achiev["kill_a"])[0];
    _loc1_[507] += this.return_bytes(this.player.achiev["kill_a"])[1];
    _loc1_[508] += this.return_bytes(this.player.achiev["kill_a"])[2];
    _loc1_[509] += this.return_bytes(this.player.achiev["kill_c"])[0];
    _loc1_[510] += this.return_bytes(this.player.achiev["kill_c"])[1];
    _loc1_[511] += this.return_bytes(this.player.achiev["kill_c"])[2];
    _loc1_[512] += this.return_bytes(this.player.achiev["kill_h"])[0];
    _loc1_[513] += this.return_bytes(this.player.achiev["kill_h"])[1];
    _loc1_[514] += this.return_bytes(this.player.achiev["kill_h"])[2];
    _loc1_[515] += this.return_bytes(this.player.achiev["kill_f"])[0];
    _loc1_[516] += this.return_bytes(this.player.achiev["kill_f"])[1];
    _loc1_[517] += this.return_bytes(this.player.achiev["kill_f"])[2];
    _loc1_[518] += this.return_bytes(this.player.achiev["kill_l"])[0];
    _loc1_[519] += this.return_bytes(this.player.achiev["kill_l"])[1];
    _loc1_[520] += this.return_bytes(this.player.achiev["kill_l"])[2];
    _loc1_[521] += this.return_bytes(this.player.achiev["assault"])[0];
    _loc1_[522] += this.return_bytes(this.player.achiev["assault"])[1];
    _loc1_[523] += this.return_bytes(this.player.achiev["assault"])[2];
    _loc1_[524] += this.return_bytes(this.player.achiev["no_death"])[0];
    _loc1_[525] += this.return_bytes(this.player.achiev["no_death"])[1];
    _loc1_[526] += this.return_bytes(this.player.achiev["no_death"])[2];
    _loc1_[527] += this.return_bytes(this.player.achiev["no_injure"])[0];
    _loc1_[528] += this.return_bytes(this.player.achiev["no_injure"])[1];
    _loc1_[529] += this.return_bytes(this.player.achiev["no_injure"])[2];
    _loc1_[530] += this.return_bytes(this.player.achiev["para"])[0];
    _loc1_[531] += this.return_bytes(this.player.achiev["para"])[1];
    _loc1_[532] += this.return_bytes(this.player.achiev["para"])[2];
    _loc1_[533] += this.return_bytes(this.player.achiev["annih"])[0];
    _loc1_[534] += this.return_bytes(this.player.achiev["annih"])[1];
    _loc1_[535] += this.return_bytes(this.player.achiev["annih"])[2];
    _loc1_[536] += this.return_bytes(this.player.achiev["stims"])[0];
    _loc1_[537] += this.return_bytes(this.player.achiev["stims"])[1];
    _loc1_[538] += this.return_bytes(this.player.achiev["stims"])[2];
    _loc1_[539] += this.return_bytes(this.player.achiev["buys"])[0];
    _loc1_[540] += this.return_bytes(this.player.achiev["buys"])[1];
    _loc1_[541] += this.return_bytes(this.player.achiev["buys"])[2];
    _loc1_[542] += this.return_bytes(this.player.achiev["steal"])[0];
    _loc1_[543] += this.return_bytes(this.player.achiev["steal"])[1];
    _loc1_[544] += this.return_bytes(this.player.achiev["steal"])[2];
    _loc1_[545] += this.return_bytes(this.player.achiev["crafted"])[0];
    _loc1_[546] += this.return_bytes(this.player.achiev["crafted"])[1];
    _loc1_[547] += this.return_bytes(this.player.achiev["crafted"])[2];
    _loc1_[548] += this.return_bytes(this.player.achiev["upgraded"])[0];
    _loc1_[549] += this.return_bytes(this.player.achiev["upgraded"])[1];
    _loc1_[550] += this.return_bytes(this.player.achiev["upgraded"])[2];
    _loc1_[551] += this.return_bytes(this.player.achiev["wins"])[0];
    _loc1_[552] += this.return_bytes(this.player.achiev["wins"])[1];
    _loc1_[553] += this.return_bytes(this.player.achiev["wins"])[2];
    _loc1_[554] += this.return_bytes(this.player.achiev["days"])[0];
    _loc1_[555] += this.return_bytes(this.player.achiev["days"])[1];
    _loc1_[556] += this.return_bytes(this.player.achiev["days"])[2];
    this.player["a_rank"] = this.get_achiev_rank(this.player.achiev);
    _loc1_[557] += this.player["a_rank"];
    _loc1_[558] = this.player.last_achiev["kill_p"];
    _loc1_[559] = this.player.last_achiev["kill_e"];
    _loc1_[560] = this.player.last_achiev["kill_a"];
    _loc1_[561] = this.player.last_achiev["kill_c"];
    _loc1_[562] = this.player.last_achiev["kill_h"];
    _loc1_[563] = this.player.last_achiev["kill_f"];
    _loc1_[564] = this.player.last_achiev["kill_l"];
    _loc1_[565] = this.player.last_achiev["assault"];
    _loc1_[566] = this.player.last_achiev["no_death"];
    _loc1_[567] = this.player.last_achiev["no_injure"];
    _loc1_[568] = this.player.last_achiev["para"];
    _loc1_[569] = this.player.last_achiev["annih"];
    _loc1_[570] = this.player.last_achiev["stims"];
    _loc1_[571] = this.player.last_achiev["buys"];
    _loc1_[572] = this.player.last_achiev["steal"];
    _loc1_[573] = this.player.last_achiev["crafted"];
    _loc1_[574] = this.player.last_achiev["upgraded"];
    _loc1_[575] = this.player.last_achiev["wins"];
    _loc1_[576] = this.player.last_achiev["days"];
    _loc1_[577] = this.player.last_achiev_range;
    _loc1_[578] += this.return_bytes(this.player.achiev_rating_bonus)[0];
    _loc1_[579] += this.return_bytes(this.player.achiev_rating_bonus)[1];
    _loc1_[580] += this.return_bytes(this.player.achiev_rating_bonus)[2];
    _loc1_[581] = this.player.last_achiev["survival"];
    _loc1_[582] = this.player.last_achiev["boss"];
    _loc1_[583] = this.player.last_achiev["recipes"];
    _loc1_[584] = this.player.last_achiev["career"];
    _loc1_[585] = this.player.last_achiev["missions"];
    for (_loc5_ of as3.keys(this.player.flags)) {
      if (this.player.flags[_loc5_] == 1) {
        _loc1_[749 + this.FLAGS_DEF[_loc5_].id] += 1;
      }
    }
    _loc1_[799] += this.player.cur_flag;
    for (_loc6_ of as3.keys(this.player.rip)) {
      if (this.player.rip[_loc6_] == 1) {
        _loc1_[799 + this.RIPS_DEF[_loc6_].id] += 1;
      }
    }
    _loc1_[849] += this.player.cur_rip;
    _loc1_[853] += this.return_bytes(this.player.last_reset)[0];
    _loc1_[854] += this.return_bytes(this.player.last_reset)[1];
    _loc1_[900] += this.player.today_steals;
    _loc1_[901] += this.return_bytes(this.player.last_steal)[0];
    _loc1_[902] += this.return_bytes(this.player.last_steal)[1];
    _loc1_[903] += this.return_bytes(this.player.last_steal)[2];
    _loc1_[904] += this.return_bytes(this.player.last_steal)[3];
    _loc1_[905] += this.return_bytes(this.player.last_steal)[4];
    _loc1_[906] += this.return_bytes(this.player.last_steal)[5];
    _loc1_[910] += this.return_bytes(this.player.rating)[0];
    _loc1_[911] += this.return_bytes(this.player.rating)[1];
    _loc1_[912] += this.return_bytes(this.player.rating)[2];
    _loc1_[913] += this.return_bytes(this.player.rating)[3];
    _loc1_[914] += this.return_bytes(this.player.rating)[4];
    _loc1_[916] += this.return_bytes(this.player.today_date)[0];
    _loc1_[917] += this.return_bytes(this.player.today_date)[1];
    _loc1_[918] += this.return_bytes(this.player.today_date)[2];
    _loc1_[919] += this.return_bytes(this.player.today_date)[3];
    _loc1_[920] += this.return_bytes(this.player.today_date)[4];
    _loc1_[921] += this.return_bytes(this.player.today_date)[5];
    _loc1_[922] += this.return_bytes(this.player.today_wins)[0];
    _loc1_[923] += this.return_bytes(this.player.today_wins)[1];
    _loc1_[924] += this.return_bytes(this.player.yest_wins)[0];
    _loc1_[925] += this.return_bytes(this.player.yest_wins)[1];
    _loc2_ = 1;
    while (_loc2_ <= 5) {
      _loc14_ = 0;
      while (_loc14_ <= 14) {
        _loc1_[950 + _loc14_ * 2 + (_loc2_ - 1) * 30] += Boolean(this.player["worm_name" + _loc2_]) && this.player["worm_name" + _loc2_].length > _loc14_ ? this.return_bytes(this.player["worm_name" + _loc2_].charCodeAt(_loc14_))[0] : 0;
        _loc1_[951 + _loc14_ * 2 + (_loc2_ - 1) * 30] += Boolean(this.player["worm_name" + _loc2_]) && this.player["worm_name" + _loc2_].length > _loc14_ ? this.return_bytes(this.player["worm_name" + _loc2_].charCodeAt(_loc14_))[1] : 0;
        _loc14_++;
      }
      _loc2_++;
    }
    for (_loc7_ of as3.keys(this.player.impl)) {
      if (this.player.impl[_loc7_] == 1) {
        _loc1_[1199 + this.IMPL_DEF[_loc7_].id] += 1;
      }
    }
    _loc1_[1249] += this.player.cur_impl;
    _loc1_[1250] += this.return_bytes(this.player.last_papa)[0];
    _loc1_[1251] += this.return_bytes(this.player.last_papa)[1];
    _loc1_[1252] += this.return_bytes(this.player.last_papa)[2];
    _loc1_[1253] += this.return_bytes(this.player.last_papa)[3];
    _loc1_[1254] += this.return_bytes(this.player.last_papa)[4];
    _loc1_[1255] += this.return_bytes(this.player.last_papa)[5];
    _loc1_[1256] += this.player.papa_asked;
    _loc1_[1257] += this.player.settings.no_animation;
    for (_loc8_ of as3.keys(this.player.objs)) {
      if (this.player.objs[_loc8_]) {
        _loc1_[1259 + this.OBJ_DEF[_loc8_].id] += this.player.objs[_loc8_];
      }
    }
    _loc2_ = 1;
    while (_loc2_ <= 151) {
      if (this.player.recipes[_loc2_]) {
        _loc1_[_loc2_ + 1299] = this.player.recipes[_loc2_];
      }
      _loc2_++;
    }
    _loc1_[1451] += this.player.today_craft_sells;
    _loc1_[1470] += this.return_bytes(this.player.next_assault_payment)[0];
    _loc1_[1471] += this.return_bytes(this.player.next_assault_payment)[1];
    _loc1_[1472] += this.return_bytes(this.player.next_assault_payment)[2];
    _loc1_[1473] += this.return_bytes(this.player.next_assault_payment)[3];
    _loc1_[1474] += this.return_bytes(this.player.next_assault_payment)[4];
    _loc1_[1475] += this.return_bytes(this.player.next_assault_payment)[5];
    _loc1_[1480] += this.player.cur_task;
    _loc1_[1481] += this.player.last_presented_task;
    _loc1_[1482] += this.player.got_cmd_bonus;
    for (_loc9_ of as3.keys(this.player.weapons)) {
      if (this.WEAPONS_DEF[_loc9_].id <= 200) {
        _loc1_[249 + this.WEAPONS_DEF[_loc9_].id] = this.player.weapons[_loc9_][0];
        _loc15_ = 1;
        while (_loc15_ <= this.player.weapons[_loc9_][0]) {
          if (this.player.weapons[_loc9_][_loc15_].range) {
            _loc16_ = this.player.weapons[_loc9_][_loc15_].range;
            if (this.player.weapons[_loc9_][_loc15_].stolen) {
              _loc16_ += 10;
            } else if (this.player.weapons[_loc9_][_loc15_].crafted) {
              _loc16_ += 20;
            }
            _loc1_[1900 + _loc15_ + (this.WEAPONS_DEF[_loc9_].id - 1) * 10 - 1] = _loc16_;
          }
          _loc15_++;
        }
      }
    }
    this.string_to_save = "";
    _loc2_ = 0;
    while (_loc2_ <= 3999 - 32) {
      this.string_to_save = this.string_to_save.concat(this.encode_param(_loc1_[_loc2_]));
      _loc2_++;
    }
    _loc10_ = new MD5();
    _loc11_ = "FWPCOOLLOLOLOL";
    _loc12_ = MD5.hash(this.string_to_save);
    this.string_to_save = this.string_to_save.concat(_loc12_);
    return as3.str(this.string_to_save);
  }

  write_map_info(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc1_ = 48;
    _loc2_ = new Array();
    _loc3_ = 0;
    while (_loc3_ <= 2700) {
      _loc2_[_loc3_] = _loc1_;
      _loc3_++;
    }
    _loc3_ = 1;
    while (_loc3_ <= 60) {
      _loc4_ = 1;
      while (_loc4_ <= 45) {
        _loc2_[(_loc3_ - 1) * 45 + _loc4_] += this.map_info[_loc3_][_loc4_];
        _loc4_++;
      }
      _loc3_++;
    }
    this.world_to_save = "";
    _loc3_ = 0;
    while (_loc3_ <= 2700) {
      this.world_to_save = this.world_to_save.concat(String.fromCharCode(_loc2_[_loc3_]));
      _loc3_++;
    }
  }

  create_new_params(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    if (param1 == "empty friend") {
      _loc2_ = this.get_info;
    } else {
      _loc2_ = this.player;
    }
    if (param1 == "null") {
      // PORT: the next backend save is a "reset" (see init_save_params).
      this.save_reason_override = "reset";
    }
    _loc2_.progress = {
      "wormville_missions": 0,
      "wormville_career": 0,
      "mines_missions": 0,
      "mines_career": 0,
      "alps_missions": 0,
      "alps_career": 0,
      "dust_career": 0,
      "island_missions": 0
    };
    _loc2_.coord_x = 5;
    _loc2_.coord_y = 4;
    _loc2_.attack = new ProtectedInt(0);
    _loc2_.protect = new ProtectedInt(0);
    _loc2_.health = new ProtectedInt(0);
    _loc2_.worms = new ProtectedInt(0);
    _loc2_.perks = {};
    _loc2_.stim_behave = { "stimpack": 5, "jet": 1, "buffout": 1 };
    _loc2_.stim_to_fight = {};
    _loc2_.spec = 0;
    _loc2_.skill_pts = new ProtectedInt(15);
    _loc2_.perk_pts = new ProtectedInt(10);
    _loc2_.gold = new ProtectedInt(800);
    _loc2_.diam = new ProtectedInt(0);
    _loc2_.tired = 0;
    _loc2_.last_tired = 0;
    _loc2_.last_enter = 0;
    _loc2_.tween = 0;
    _loc2_.exp = new ProtectedInt(0);
    _loc2_.level = new ProtectedInt(this.get_player_level(_loc2_.exp.value));
    _loc2_.weapons = {};
    _loc2_.career_max = 1;
    _loc2_.career_attempt = 0;
    _loc2_.missions_max = 1;
    _loc2_.missions_attempt = 0;
    _loc2_.survival_max = 1;
    _loc2_.survival_attempt = 0;
    _loc2_.boss_max = 1;
    _loc2_.boss_attempt = 0;
    _loc2_.settings = {
      "skill": 0,
      "help": 0,
      "sound": 0,
      "exhaust": 0,
      "no_animation": 0
    };
    _loc2_.first_time = true;
    _loc2_.admin_state = 20;
    _loc2_.inc_state = 0;
    _loc2_.last_exp = 0;
    _loc2_.achiev["kill_p"] = 0;
    _loc2_.achiev["kill_e"] = 0;
    _loc2_.achiev["kill_a"] = 0;
    _loc2_.achiev["kill_c"] = 0;
    _loc2_.achiev["kill_h"] = 0;
    _loc2_.achiev["kill_f"] = 0;
    _loc2_.achiev["kill_l"] = 0;
    _loc2_.achiev["career"] = _loc2_.career_max - 1;
    _loc2_.achiev["missions"] = _loc2_.missions_max - 1;
    _loc2_.achiev["survival"] = _loc2_.survival_max - 1;
    _loc2_.achiev["boss"] = _loc2_.boss_max - 1;
    _loc2_.achiev["assault"] = 0;
    _loc2_.achiev["recipes"] = 0;
    _loc2_.achiev["upgraded"] = 0;
    _loc2_.achiev["days"] = 0;
    _loc2_.achiev["no_death"] = 0;
    _loc2_.achiev["no_injure"] = 0;
    _loc2_.achiev["buys"] = 0;
    _loc2_.achiev["wins"] = 0;
    _loc2_.achiev["crafted"] = 0;
    _loc2_.achiev["stims"] = 0;
    _loc2_.achiev["para"] = 0;
    _loc2_.achiev["annih"] = 0;
    _loc2_.achiev["steal"] = 0;
    _loc2_.last_achiev = {
      "career": 0,
      "missions": 0,
      "survival": 0,
      "boss": 0,
      "assault": 0,
      "no_death": 0,
      "no_injure": 0,
      "kill_p": 0,
      "kill_e": 0,
      "kill_a": 0,
      "kill_c": 0,
      "kill_h": 0,
      "kill_f": 0,
      "kill_l": 0,
      "para": 0,
      "annih": 0,
      "recipes": 0,
      "stims": 0,
      "buys": 0,
      "steal": 0,
      "crafted": 0,
      "upgraded": 0,
      "wins": 0,
      "days": 0
    };
    _loc2_.last_achiev_range = 0;
    _loc2_.achiev_rating_bonus = 0;
    _loc2_.perk_slots = 2;
    _loc2_.diam_magnet = 0;
    _loc2_.quick_weapons["z"] = 0;
    _loc2_.quick_weapons["x"] = 0;
    _loc2_.quick_weapons["c"] = 0;
    _loc2_.quick_weapons["v"] = 0;
    _loc2_.quick_weapons["b"] = 0;
    _loc2_.quick_weapons["n"] = 0;
    _loc2_.quick_weapons["m"] = 0;
    _loc2_.quick_weapons["k"] = 0;
    _loc2_.quick_weapons["l"] = 0;
    _loc2_.flags = {};
    _loc2_.rip = {};
    _loc2_.impl = {};
    _loc2_.objs = {};
    _loc2_.cur_flag = 0;
    _loc2_.cur_rip = 0;
    _loc2_.cur_impl = 0;
    if (param1 != "null") {
      _loc2_.view_tutorial = 0;
    }
    _loc2_.today_steals = 0;
    _loc2_.last_steal = 0;
    _loc2_.rating = 0;
    _loc2_.today_date = 0;
    _loc2_.today_wins = 0;
    _loc2_.yest_wins = 0;
    _loc2_.last_papa = 0;
    _loc2_.papa_asked = 0;
    _loc3_ = 1;
    while (_loc3_ <= 5) {
      _loc2_["worm_name" + _loc3_] = "";
      _loc3_++;
    }
    _loc2_.last_reset = 0;
    _loc2_.next_assault_payment = 0;
    _loc2_.cur_task = 1;
    _loc2_.last_presented_task = 1;
    _loc2_.got_cmd_bonus = 0;
    _loc2_.recipes[0] = this.CRAFT_DEF[0];
    _loc4_ = 1;
    while (_loc4_ <= _loc2_.recipes[0]) {
      _loc2_.recipes[_loc4_] = 0;
      _loc4_++;
    }
    // PORT: the developer test profile was keyed to the developer's VK id; now an explicit dev flag.
    if (Boolean(this.DEV_PROFILE) && param1 != "empty friend") {
      _loc2_.attack.value = 199;
      _loc2_.protect.value = 700;
      _loc2_.health.value = 0;
      _loc2_.worms.value = 0;
      _loc2_.tired = 0;
      _loc2_.skill_pts.value = 1000;
      _loc2_.perk_pts.value = 3000;
      _loc2_.gold.value = 5000000;
      _loc2_.diam.value = 5000;
      _loc2_.spec = 0;
      _loc2_.settings.skill = 10;
      _loc2_.career_max = this.TEAMS_DEF.career_max;
      _loc2_.missions_max = this.TEAMS_DEF.missions_max;
      _loc2_.survival_max = this.TEAMS_DEF.survival_max;
      _loc2_.boss_max = this.TEAMS_DEF.boss_max - 2;
      _loc2_.tween = 2;
      _loc2_.perks = { "gsearch": 3 };
      _loc2_.objs = { "obj_ultra_mod": 7, "obj_laser_mod": 7 };
      _loc2_.cur_task = 30;
      _loc2_.last_presented_task = 1;
      for (_loc5_ of as3.keys(this.WEAPONS_DEF)) {
        if (_loc5_.search("rez") == -1 && this.WEAPONS_DEF[_loc5_].id <= 200 && _loc5_ != "annihilator") {
          _loc2_.weapons[_loc5_] = [3, { "range": 1, "stolen": false, "crafted": false }, { "range": 1, "stolen": false, "crafted": false }, { "range": 1, "stolen": false, "crafted": false }];
        }
      }
      _loc2_.exp.value = this.LEVEL_THRESHOLD[10] - 1;
      _loc2_.level.value = this.get_player_level(_loc2_.exp.value);
      _loc2_.perk_slots = 2;
    }
  }

  create_map_info(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    this.map_info = new Array();
    _loc1_ = 1;
    while (_loc1_ <= 60) {
      this.map_info[_loc1_] = new Array();
      _loc2_ = 1;
      while (_loc2_ <= 45) {
        this.map_info[_loc1_][_loc2_] = 0;
        _loc2_++;
      }
      _loc1_++;
    }
  }

  refresh_temp_params(): any {
    this.temp_player = {};
    this.temp_player = this.clone(this.player);
    this.temp_player.exp = new ProtectedInt(this.player.exp.value);
    this.temp_player.level = new ProtectedInt(this.player.level.value);
    this.temp_player.gold = new ProtectedInt(this.player.gold.value);
    this.temp_player.diam = new ProtectedInt(this.player.diam.value);
    this.temp_player.attack = new ProtectedInt(this.player.attack.value);
    this.temp_player.protect = new ProtectedInt(this.player.protect.value);
    this.temp_player.health = new ProtectedInt(this.player.health.value);
    this.temp_player.worms = new ProtectedInt(this.player.worms.value);
    this.temp_player.perk_pts = new ProtectedInt(this.player.perk_pts.value);
    this.temp_player.skill_pts = new ProtectedInt(this.player.skill_pts.value);
    this.temp_slot_buys = [];
    if (this._interf) {
      this._interf.changes_made = false;
    }
  }

  /** PORT: design D3 - skills.commit arguments of the editor session (temp_player against player). */
  skills_commit_args(): any {
    return skillsCommitArgs(this.player, this.temp_player, this.temp_slot_buys);
  }

  /** PORT: design D3 - FWP.end_game of an achievement / task claim from the level-up the server applied. */
  claim_end_game(param1: any): any {
    this.end_game = claimEndGame(param1);
    return this.end_game;
  }

  save_temp_params(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc1_ = false;
    if (this.player.health.value != this.temp_player.health.value || this.player.worms.value != this.temp_player.worms.value) {
      _loc1_ = true;
    }
    _loc2_ = this.get_perks_amount(this.player);
    _loc3_ = this.get_perks_amount(this.temp_player);
    this.player = this.temp_player;
    return { "new_perks": _loc3_ - _loc2_, "health_or_worms": _loc1_ };
  }

  get_perks_amount(param1: any): number {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = 0;
    for (_loc3_ of as3.keys(param1.perks)) {
      if (param1.perks[_loc3_] > 0) {
        _loc2_++;
      }
    }
    return Number(_loc2_);
  }

  modify_tired(): void {
    this.player.tired -= Math.floor((this.user.unix_time - this.player.last_tired) / 120);
    if (this.player.tired < 0) {
      this.player.tired = 0;
    }
    this.player.last_tired = this.user.unix_time;
  }

  modify_last_enter(): any {
    let _loc1_: any = null;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = null;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = null;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    _loc1_ = new Date(this.user.unix_time * 1000);
    _loc2_ = _loc1_.getDate();
    _loc3_ = _loc1_.getMonth() + 1;
    _loc4_ = _loc1_.getFullYear();
    _loc5_ = new Date(this.player.last_enter * 1000);
    _loc6_ = _loc5_.getDate();
    _loc7_ = _loc5_.getMonth() + 1;
    _loc8_ = _loc5_.getFullYear();
    if (_loc2_ != _loc6_ || _loc3_ != _loc7_ || _loc4_ != _loc8_) {
      if (this.mod(_loc2_ - _loc6_) == 1 || _loc2_ == 1 && this.mod(_loc3_ - _loc7_) == 1 || _loc2_ == 1 && _loc3_ == 1 && this.mod(_loc4_ - _loc8_) == 1 || this.player.last_enter == 0) {
        if (this.player.last_enter != 0) {
          ++this.player.tween;
        } else {
          this.player.tween = 1;
        }
      } else {
        this.player.tween = 1;
      }
      this.get_present = this.player.tween;
      ++this.player.achiev["days"];
      this.player.today_craft_sells = 0;
    }
    this.player.last_enter = this.user.unix_time;
    if (this.player.last_steal > 0) {
      _loc9_ = new Date(this.player.last_steal * 1000);
      _loc10_ = _loc9_.getDate();
      _loc11_ = _loc9_.getMonth() + 1;
      _loc12_ = _loc9_.getFullYear();
      if (_loc2_ != _loc10_ || _loc3_ != _loc11_ || _loc4_ != _loc12_) {
        this.player.today_steals = 0;
      }
    } else {
      this.player.today_steals = 0;
    }
  }

  modify_papa(): any {
    let _loc1_: any = null;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = null;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    _loc1_ = new Date(this.user.unix_time * 1000);
    _loc2_ = _loc1_.getHours();
    _loc3_ = _loc1_.getDate();
    _loc4_ = _loc1_.getMonth() + 1;
    _loc5_ = _loc1_.getFullYear();
    // PORT: the original turned the hour into a fixed UTC+4 ("Moscow time" of 2011-2014, FWP.as:18185-18186) while
    // PORT: indexing PAPA_HOURS by the player's local day. Papa now follows the player's own time zone, like every
    // PORT: other daily reset in the game, so the conversion is dropped.
    if (this.player.last_papa > 0) {
      _loc8_ = new Date(this.player.last_papa * 1000);
      _loc9_ = _loc8_.getDate();
      _loc10_ = _loc8_.getMonth() + 1;
      _loc11_ = _loc8_.getFullYear();
      if (_loc3_ != _loc9_ || _loc4_ != _loc10_ || _loc5_ != _loc11_) {
        this.player.last_papa = 0;
        this.player.papa_asked = 0;
      }
    }
    _loc7_ = this.PAPA_HOURS[_loc3_];
    this.PAPA_HERE = _loc2_ == _loc7_;
  }

  modify_wins(): any {
    let _loc1_: any = null;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = null;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    _loc1_ = new Date(this.user.unix_time * 1000);
    _loc2_ = _loc1_.getDate();
    _loc3_ = _loc1_.getMonth() + 1;
    _loc4_ = _loc1_.getFullYear();
    if (this.player.today_date == 0) {
      this.player.today_date = this.user.unix_time;
      this.player.today_wins = 0;
      this.player.yest_wins = 0;
      return;
    }
    _loc5_ = new Date(this.player.today_date * 1000);
    _loc6_ = _loc5_.getDate();
    _loc7_ = _loc5_.getMonth() + 1;
    _loc8_ = _loc5_.getFullYear();
    if (_loc2_ != _loc6_ || _loc3_ != _loc7_ || _loc4_ != _loc8_) {
      if (this.mod(_loc2_ - _loc6_) == 1 || _loc2_ == 1 && this.mod(_loc3_ - _loc7_) == 1 || _loc2_ == 1 && _loc3_ == 1 && this.mod(_loc4_ - _loc8_) == 1) {
        this.player.yest_wins = this.player.today_wins;
        this.player.today_date = this.user.unix_time;
        this.player.today_wins = 0;
      } else {
        this.player.today_date = this.user.unix_time;
        this.player.today_wins = 0;
        this.player.yest_wins = 0;
      }
    }
  }

  init_refresh(): any {
    this.display_system.addChild(this.splash_wait);
    // PORT: the LOCAL_TEST branch (a fixed clock without the network) is gone.
    // PORT: design D3 - "refresh time" -> write_storage -> "save storage" became the server command time.refresh
    // PORT: (modify_tired, modify_papa, modify_wins with the server's clock); PAPA_HERE comes from its result.
    this.net_command("time.refresh", {}, this.execute_after, { "done": (result: any) => {
      this.PAPA_HERE = Boolean(result && result.papaHere);
    } });
  }

  init_save_params(): any {
    this.display_system.addChild(this.splash_wait);
    // PORT: the LOCAL_TEST branch (no save at all) is gone.
    // PORT: write_storage() (4000-char VK-storage string) + "save storage"/"check storage" replaced by net_save.
    // PORT: the backend save reason: "reset" after "start over" (create_new_params("null")), so progress may decrease.
    this.net_save(this.execute_after == "init win or lose" ? "battle_end" : this.save_reason_override || "action", () => {
      this.success_saved();
    });
    this.save_reason_override = "";
  }

  // PORT: check_sid (VK session / multi-window check) removed; concurrent writers get a revision conflict from the backend.

  success_saved(): any {
    this.display_system.removeChild(this.splash_wait);
    if (this.OOPS_BANNED) {
      return;
    }
    if (this.execute_after == "render") {
      this.refresh_temp_params();
      this._interf.render();
    }
    if (this.execute_after == "post new weapon") {
      // PORT: the VK wall post for a newly bought weapon is removed (in the offline build it hung the game, defect D1).
      this.refresh_temp_params();
      this._interf.render();
    }
    if (this.execute_after == "remove") {
      this.refresh_temp_params();
      this._interf.remove();
    }
    if (this.execute_after == "init roul result") {
      this._interf.init("roul result");
    }
    if (this.execute_after == "init grave result") {
      this._interf.init("grave_result");
    }
    if (this.execute_after == "init papa shop") {
      this._interf.init("papa_shop");
    }
    if (this.execute_after == "steal proceed") {
      this._interf.remove();
      this._interf.init("mainframe");
      this._interf.init("steal");
    }
    if (this.execute_after == "init win or lose") {
      this._interf.init_win_or_lose();
    }
    if (this.execute_after == "init game") {
      this.init_game({ "mode": this._interf.play_type, "id": this._interf.cur_element });
    }
    if (this.execute_after == "new recipes added") {
      this._interf.init("error", "new_recipes");
    }
    if (this.execute_after == "init new level") {
      this._interf.init_new_level();
    }
    this.execute_after = "";
  }

  /**
   * PORT: replays were stored as VK notes; now they go to the backend replay store. `param1` is the battle's offer
   * PORT: (runtime/replay-offer.ts) the player accepted: its string is copied to the clipboard as the original did and
   * PORT: saved with its battle id (the server stores one replay per battle, so a retry stores no second one);
   * PORT: `param2(refused)` runs once the server answered (refused: it did not take the string).
   */
  save_replay(param1: ReplayOffer, param2: (refused: boolean) => void): any {
    this.display_system.addChild(this.splash_wait);
    setClipboard(param1.legacy);
    const args: any = { "mode": param1.mode, "stage": param1.stage, "result": { "winnerTeam": param1.win ? 1 : 0 }, "legacy": param1.legacy };
    if (param1.battleId) {
      args.battleId = param1.battleId;
    }
    this.net_run("save replay", () => this.services.saveReplay(args).then(() => false, (error: any) => {
      if (saveRefused(error)) {
        return true;
      }
      throw error;
    }), (refused: any) => {
      if (this.splash_wait && this.splash_wait.parent == this.display_system) {
        this.display_system.removeChild(this.splash_wait);
      }
      param2(Boolean(refused));
    });
  }

  init_save_map(): any {
  }

  get_player_level(param1: number): number {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    _loc2_ = 1;
    while (this.LEVEL_THRESHOLD[_loc2_] <= param1) {
      _loc2_++;
    }
    return _loc2_ - 1;
  }

  get_player_league(param1: number): number {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    _loc2_ = 1;
    while (this.LEAGUE_THRESHOLD[_loc2_] <= param1) {
      _loc2_++;
    }
    return _loc2_ - 1;
  }

  get_affected_skills(param1: any): any {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    _loc2_ = {};
    _loc3_ = { "attack": 0, "protect": 0, "health": 0, "tired": 0 };
    for (_loc4_ of as3.keys(param1.stim_to_fight)) {
      _loc3_.attack += this.STIM_DEF[_loc4_].attack * param1.stim_to_fight[_loc4_];
      _loc3_.protect += this.STIM_DEF[_loc4_].protect * param1.stim_to_fight[_loc4_];
      _loc3_.health += this.STIM_DEF[_loc4_].health * param1.stim_to_fight[_loc4_];
      _loc3_.tired += this.STIM_DEF[_loc4_].tired * param1.stim_to_fight[_loc4_];
    }
    _loc5_ = this.get_tired_effect(param1.tired + _loc3_.tired);
    _loc6_ = { "attack": 0, "protect": 0, "health": 0 };
    for (_loc7_ of as3.keys(this.IMPL_DEF)) {
      if (this.IMPL_DEF[_loc7_].id == param1.impl) {
        _loc6_.attack = this.IMPL_DEF[_loc7_].attack;
        _loc6_.protect = this.IMPL_DEF[_loc7_].protect;
        _loc6_.health = this.IMPL_DEF[_loc7_].health;
      }
    }
    _loc2_.attack = param1.attack + _loc3_.attack + _loc5_.attack + _loc6_.attack;
    _loc2_.protect = param1.protect + _loc3_.protect + _loc5_.protect + _loc6_.protect;
    _loc2_.health = Math.round((param1.health + _loc3_.health) * _loc5_.health) + _loc6_.health;
    _loc2_.tired = param1.tired + _loc3_.tired;
    if (_loc2_.tired < 0) {
      _loc2_.tired = 0;
    }
    _loc2_.desc_attack = { "stim": _loc3_.attack, "tired": _loc5_.attack, "impl": _loc6_.attack };
    _loc2_.desc_protect = { "stim": _loc3_.protect, "tired": _loc5_.protect, "impl": _loc6_.protect };
    _loc2_.desc_health = { "stim": _loc3_.health, "tired": _loc5_.health, "impl": _loc6_.health };
    return _loc2_;
  }

  get_tired_effect(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = { "attack": 0, "protect": 0, "health": 1 };
    _loc3_ = 6;
    while (_loc3_ >= 0) {
      if (this.TIRED_EFFECTS[_loc3_].threshold <= param1) {
        _loc2_.attack = this.TIRED_EFFECTS[_loc3_].attack;
        _loc2_.protect = this.TIRED_EFFECTS[_loc3_].protect;
        _loc2_.health = this.TIRED_EFFECTS[_loc3_].health;
        return _loc2_;
      }
      _loc3_--;
    }
    return _loc2_;
  }

  get_peachful_bonus(param1: any): any {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    _loc2_ = param1.spec;
    _loc3_ = param1.wptype;
    _loc4_ = param1.attack;
    _loc5_ = param1.perks;
    _loc6_ = param1.spec_mod;
    _loc7_ = param1.wpclass;
    _loc8_ = {
      "damage": 0,
      "attack": 0,
      "spec": 0,
      "perks": 0,
      "aff_perks": new Array<any>(0, {})
    };
    _loc9_ = 0;
    _loc10_ = 0;
    _loc11_ = 0;
    if (_loc5_["damage_" + _loc3_]) {
      _loc10_ = _loc5_["damage_" + _loc3_];
      ++_loc8_.aff_perks[0];
      _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "damage_" + _loc3_, "p": _loc10_ * 0.15 };
    }
    if (_loc5_["god_of_war"]) {
      _loc11_ = _loc5_["god_of_war"];
      ++_loc8_.aff_perks[0];
      _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "god_of_war", "p": _loc11_ * 0.15 };
    }
    _loc9_ += _loc10_ * 0.15 + _loc11_ * 0.15;
    _loc12_ = 0;
    if (_loc6_ == "elec") {
      if (Boolean(_loc5_["electrician"]) && _loc5_["electrician"] > 0) {
        _loc12_ += this.get_perk_effect("electrician", _loc5_["electrician"], "");
        ++_loc8_.aff_perks[0];
        _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "electrician", "p": this.get_perk_effect("electrician", _loc5_["electrician"], "") };
      }
    }
    if (_loc6_ == "holy") {
      if (Boolean(_loc5_["angel"]) && _loc5_["angel"] > 0) {
        _loc12_ += this.get_perk_effect("angel", _loc5_["angel"], "");
        ++_loc8_.aff_perks[0];
        _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "angel", "p": this.get_perk_effect("angel", _loc5_["angel"], "") };
      }
    }
    if (_loc6_ == "pistol") {
      if (Boolean(_loc5_["up_pistols"]) && _loc5_["up_pistols"] > 0) {
        _loc12_ += this.get_perk_effect("up_pistols", _loc5_["up_pistols"], "");
        ++_loc8_.aff_perks[0];
        _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "up_pistols", "p": this.get_perk_effect("up_pistols", _loc5_["up_pistols"], "") };
      }
    }
    if (_loc6_ == "rifle" || _loc6_ == "riflegun") {
      if (Boolean(_loc5_["up_rifles"]) && _loc5_["up_rifles"] > 0) {
        _loc12_ += this.get_perk_effect("up_rifles", _loc5_["up_rifles"], "");
        ++_loc8_.aff_perks[0];
        _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "up_rifles", "p": this.get_perk_effect("up_rifles", _loc5_["up_rifles"], "") };
      }
    }
    if (_loc6_ == "minigun" || _loc6_ == "riflegun") {
      if (Boolean(_loc5_["up_miniguns"]) && _loc5_["up_miniguns"] > 0) {
        _loc12_ += this.get_perk_effect("up_miniguns", _loc5_["up_miniguns"], "");
        ++_loc8_.aff_perks[0];
        _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "up_miniguns", "p": this.get_perk_effect("up_miniguns", _loc5_["up_miniguns"], "") };
      }
    }
    if (Boolean(_loc5_["big_guns"]) && Boolean(_loc5_["big_guns"] > 0) && _loc7_ != 0) {
      _loc9_ += this.get_perk_effect("big_guns", _loc5_["big_guns"], String(_loc7_));
      ++_loc8_.aff_perks[0];
      _loc8_.aff_perks[_loc8_.aff_perks[0]] = { "id": "big_guns", "p": this.get_perk_effect("big_guns", _loc5_["big_guns"], String(_loc7_)) };
    }
    _loc13_ = 0;
    if (_loc3_ == "p" && _loc2_ == 3) {
      _loc13_ = 0.5;
    }
    if (_loc3_ == "e" && _loc2_ == 2) {
      _loc13_ = 0.75;
    }
    if (_loc3_ == "a" && _loc2_ == 3) {
      _loc13_ = 0.75;
    }
    if (_loc3_ == "c" && _loc2_ == 1) {
      _loc13_ = 4;
    }
    if (_loc3_ == "h" && _loc2_ == 4) {
      _loc13_ = 0.5;
    }
    if (_loc3_ == "f" && _loc2_ == 2) {
      _loc13_ = 0.5;
    }
    if (_loc3_ == "l" && _loc2_ == 1) {
      _loc13_ = 1;
    }
    _loc14_ = 0;
    if (_loc4_ >= 0) {
      _loc14_ = _loc4_ * this.PEACHFUL_BONUS[_loc3_] / 100;
      _loc8_.damage = _loc14_ + _loc9_ + _loc12_ + _loc13_;
      _loc8_.attack = _loc14_;
      _loc8_.spec = _loc13_;
      _loc8_.perks = _loc9_;
    } else {
      _loc14_ = _loc4_ * 0.25 / 100;
      _loc8_.damage = _loc14_ + _loc9_ + _loc12_;
      _loc8_.attack = _loc14_;
      _loc8_.spec = 0;
      _loc8_.perks = _loc9_;
    }
    this.deep_trace(_loc8_);
    return _loc8_;
  }

  get_range_upgrade(param1: number = 1, param2: any = "damage", param3: number = 1): number {
    param1 = Number(param1);
    param2 = as3.str(param2);
    param3 = Number(param3);
    let _loc4_: any = undefined;
    if (param2 == "damage") {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1.15;
        case 3:
          return 1.3;
        case 4:
          return 1.4;
        case 5:
          return 1.5;
        case 6:
          return 1.6;
        case 7:
          return 1.7;
        case 8:
          return 1.85;
        case 9:
          return 2.1;
        case 10:
          return 2.5;
      }
    }
    if (param2 == "crit") {
      switch (param1) {
        case 1:
          return 0;
        case 2:
          return 5;
        case 3:
          return 10;
        case 4:
          return 13;
        case 5:
          return 16;
        case 6:
          return 20;
        case 7:
          return 25;
        case 8:
          return 30;
        case 9:
          return 37;
        case 10:
          return 45;
      }
    }
    if (param2 == "ap") {
      switch (param1) {
        case 1:
          return 0;
        case 2:
          return 0;
        case 3:
          return 5;
        case 4:
          return 7;
        case 5:
          return 10;
        case 6:
          return 15;
        case 7:
          return 20;
        case 8:
          return 25;
        case 9:
          return 32;
        case 10:
          return 40;
      }
    }
    if (param2 == "price") {
      _loc4_ = 1;
      if (param3 == 1) {
        _loc4_ = 4;
      }
      if (param3 == 2) {
        _loc4_ = 3;
      }
      if (param3 == 3) {
        _loc4_ = 2;
      }
      if (param3 == 4) {
        _loc4_ = 1.7;
      }
      if (param3 == 5) {
        _loc4_ = 1.4;
      }
      switch (param1) {
        case 2:
          return 0.1 * _loc4_;
        case 3:
          return 0.15 * _loc4_;
        case 4:
          return 0.2 * _loc4_;
        case 5:
          return 0.3 * _loc4_;
        case 6:
          return 0.4 * _loc4_;
        case 7:
          return 0.5 * _loc4_;
        case 8:
          return 0.65 * _loc4_;
        case 9:
          return 0.75 * _loc4_;
        case 10:
          return 0.85 * _loc4_;
      }
    }
    if (param2 == "sell_price") {
      switch (param1) {
        case 2:
          return 1.1;
        case 3:
          return 1.25;
        case 4:
          return 1.45;
        case 5:
          return 1.75;
        case 6:
          return 2.15;
        case 7:
          return 2.55;
        case 8:
          return 3;
        case 9:
          return 3.5;
        case 10:
          return 4;
      }
    }
    return 0;
  }

  get_damage_resistance(param1: number): number {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = 100;
    _loc3_ = param1;
    if (_loc3_ > 0) {
      while (_loc3_ != 0) {
        if (_loc3_ >= 200) {
          _loc2_ -= 0.1;
        } else if (_loc3_ >= 100) {
          _loc2_ -= 0.2;
        } else {
          _loc2_ -= 0.3;
        }
        _loc3_--;
      }
    } else {
      while (_loc3_ != 0) {
        _loc2_ += 0.1;
        _loc3_++;
      }
    }
    if (_loc2_ < 20) {
      _loc2_ = 20;
    }
    return Number(_loc2_);
  }

  wptype_to_spec(param1: any): number {
    param1 = as3.str(param1);
    if (param1 == "p") {
      return 1;
    }
    if (param1 == "e") {
      return 2;
    }
    if (param1 == "a") {
      return 3;
    }
    if (param1 == "c") {
      return 4;
    }
    if (param1 == "h") {
      return 5;
    }
    if (param1 == "f") {
      return 6;
    }
    if (param1 == "u") {
      return 7;
    }
    if (param1 == "l") {
      return 8;
    }
    return 0;
  }

  spec_to_name(param1: number): any {
    param1 = Number(param1);
    switch (param1) {
      case 0:
        return "не выбрана";
      case 1:
        return "Мастер ближнего боя";
      case 2:
        return "Подрывник";
      case 3:
        return "Артиллерист";
      case 4:
        return "Инженер";
      default:
        return "не выбрана";
    }
  }

  get_perk_effect(param1: any, param2: number, param3: any): number {
    param1 = as3.str(param1);
    param2 = Number(param2);
    param3 = as3.str(param3);
    if (param1 == "dragonball") {
      switch (param2) {
        case 1:
          return 1.5;
        case 2:
          return 2;
        case 3:
          return 3;
      }
    }
    if (param1 == "builder") {
      switch (param2) {
        case 1:
          return 1.15;
        case 2:
          return 1.3;
        case 3:
          return 1.5;
      }
    }
    if (param1 == "laser_sights") {
      return param2;
    }
    if (param1 == "champion") {
      switch (param2) {
        case 1:
          return 1.1;
        case 2:
          return 1.15;
        case 3:
          return 1.2;
      }
    }
    if (param1 == "education") {
      switch (param2) {
        case 1:
          return 2;
        case 2:
          return 3;
        case 3:
          return 5;
      }
    }
    if (param1 == "fire_punch") {
      switch (param2) {
        case 1:
          return -4.8;
        case 2:
          return -5.2;
        case 3:
          return -5.6;
      }
    }
    if (param1 == "engineer") {
      switch (param2) {
        case 1:
          return 350;
        case 2:
          return 450;
        case 3:
          return 600;
      }
    }
    if (param1 == "miner") {
      switch (param2) {
        case 1:
          return 2;
        case 2:
          return 1;
        case 3:
          return 0;
      }
    }
    if (param1 == "crafting") {
      switch (param2) {
        case 1:
          return 5;
        case 2:
          return 7;
        case 3:
          return 9;
      }
    }
    if (param1 == "electrician") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.2;
        case 3:
          return 0.3;
      }
    }
    if (param1 == "surprise") {
      return param2;
    }
    if (param1 == "merchant") {
      switch (param2) {
        case 1:
          return 0.95;
        case 2:
          return 0.9;
        case 3:
          return 0.85;
      }
    }
    if (param1 == "medic") {
      switch (param2) {
        case 1:
          return 1.05;
        case 2:
          return 1.1;
        case 3:
          return 1.15;
      }
    }
    if (param1 == "para_res") {
      switch (param2) {
        case 1:
          return 0.7;
        case 2:
          return 0.5;
        case 3:
          return 0.3;
      }
    }
    if (param1 == "luck" || param1 == "luck2") {
      switch (param2) {
        case 1:
          return 0.02;
        case 2:
          return 0.07;
        case 3:
          return 0.14;
      }
    }
    if (param1 == "angel") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.25;
        case 3:
          return 0.5;
      }
    }
    if (param1 == "fire_res") {
      switch (param2) {
        case 1:
          return 0.7;
        case 2:
          return 0.5;
        case 3:
          return 0.3;
      }
    }
    if (param1 == "legioner") {
      switch (param2) {
        case 1:
          return 1;
        case 2:
          return 2;
        case 3:
          return 4;
      }
    }
    if (param1 == "comandeer") {
      switch (param2) {
        case 1:
          return 50;
        case 2:
          return 100;
        case 3:
          return 150;
      }
    }
    if (param1 == "poison_res") {
      switch (param2) {
        case 1:
          return -1;
        case 2:
          return -2;
        case 3:
          return -3;
      }
    }
    if (param1 == "flyer") {
      switch (param2) {
        case 1:
          return 10;
        case 2:
          return 20;
        case 3:
          return 30;
      }
    }
    if (param1 == "sprotsman") {
      switch (param2) {
        case 1:
          return 1.05;
        case 2:
          return 1.15;
        case 3:
          return 1.3;
      }
    }
    if (param1 == "heavy_guy") {
      switch (param2) {
        case 1:
          return 0.9;
        case 2:
          return 0.8;
        case 3:
          return 0.1;
      }
    }
    if (param1 == "accuracy") {
      switch (param2) {
        case 1:
          return 5;
        case 2:
          return 2;
        case 3:
          return 1;
      }
    }
    if (param1 == "best_crit") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.25;
        case 3:
          return 0.5;
      }
    }
    if (param1 == "stims") {
      switch (param2) {
        case 1:
          return 1;
        case 2:
          return 2;
        case 3:
          return 3;
      }
    }
    if (param1 == "fast_temp") {
      switch (param2) {
        case 1:
          return 1;
        case 2:
          return 2;
        case 3:
          return 3;
      }
    }
    if (param1 == "thinker") {
      switch (param2) {
        case 1:
          return 15;
        case 2:
          return 30;
        case 3:
          return 45;
      }
    }
    if (param1 == "soft_land") {
      switch (param2) {
        case 1:
          return 1.2;
        case 2:
          return 1.5;
        case 3:
          return 100;
      }
    }
    if (param1 == "regeneration") {
      switch (param2) {
        case 1:
          return 10;
        case 2:
          return 20;
        case 3:
          return 30;
      }
    }
    if (param1 == "vampire") {
      switch (param2) {
        case 1:
          return 0.4;
        case 2:
          return 0.8;
        case 3:
          return 1.5;
      }
    }
    if (param1 == "bar") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.3;
        case 3:
          return 0.5;
      }
    }
    if (param1 == "more_exp") {
      switch (param2) {
        case 1:
          return 1.05;
        case 2:
          return 1.1;
        case 3:
          return 1.15;
      }
    }
    if (param1 == "up_pistols") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.25;
        case 3:
          return 0.4;
      }
    }
    if (param1 == "up_rifles") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.15;
        case 3:
          return 0.3;
      }
    }
    if (param1 == "up_miniguns") {
      switch (param2) {
        case 1:
          return 0.1;
        case 2:
          return 0.15;
        case 3:
          return 0.3;
      }
    }
    if (param1 == "less_fail") {
      switch (param2) {
        case 1:
          return 0.85;
        case 2:
          return 0.7;
        case 3:
          return 0.5;
      }
    }
    if (param1 == "more_fail") {
      switch (param2) {
        case 1:
          return 0.08;
        case 2:
          return 0.15;
        case 3:
          return 0.25;
      }
    }
    if (param1 == "less_tired") {
      switch (param2) {
        case 1:
          return 1;
        case 2:
          return 2;
        case 3:
          return 3;
      }
    }
    if (param1 == "dmg_thr") {
      return param2 * 10;
    }
    if (param1 == "big_guns") {
      if (Number(param3) < 5) {
        switch (param2) {
          case 1:
            return -0.1;
          case 2:
            return -0.2;
          case 3:
            return -0.3;
        }
      }
      if (Number(param3) >= 5) {
        switch (param2) {
          case 1:
            return 0.1;
          case 2:
            return 0.15;
          case 3:
            return 0.2;
        }
      }
    }
    if (param1 == "gsearch") {
      switch (param2) {
        case 1:
          return 0.7;
        case 2:
          return 0.55;
        case 3:
          return 0.3;
      }
    }
    return 0;
  }

  get_skill_multi(param1: any): number {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    _loc2_ = this.player.settings.skill;
    if (_loc2_ >= 10) {
      _loc2_ -= 10;
    }
    if (param1 == "exp" || param1 == "gold") {
      switch (_loc2_) {
        case 0:
          return 1;
        case 1:
          return 1.15;
        case 2:
          return 1.3;
        case 3:
          return 1.6;
        case 4:
          return 2;
      }
    } else if (param1 == "attack" || param1 == "protect") {
      switch (_loc2_) {
        case 0:
          return 0;
        case 1:
          return 60;
        case 2:
          return 120;
        case 3:
          return 180;
        case 4:
          return 250;
      }
    } else if (param1 == "health") {
      switch (_loc2_) {
        case 0:
          return 1;
        case 1:
          return 1.3;
        case 2:
          return 1.6;
        case 3:
          return 1.9;
        case 4:
          return 2.4;
      }
    }
    return 1;
  }

  get_hardcore_multi(param1: number, param2: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    let _loc3_: any = undefined;
    _loc3_ = 1;
    if (this.player.settings.skill >= 10) {
      _loc3_ = 1 + Math.pow(param1 / param2, 2) * 0.75;
    }
    return Number(_loc3_);
  }

  get_assault_multi(param1: any, param2: number, param3: number, param4: number): number {
    param1 = as3.str(param1);
    param2 = Number(param2);
    param3 = Number(param3);
    param4 = Number(param4);
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    _loc5_ = param3;
    if (param3 > this.TEAMS_DEF["assault" + param4].assault_limit) {
      _loc5_ = param3 % this.TEAMS_DEF["assault" + param4].assault_limit;
    }
    if (param1 == "attack" || param1 == "protect") {
      return param2 + Math.round(300 * _loc5_ / this.TEAMS_DEF["assault" + param4].assault_limit);
    }
    if (param1 == "health") {
      _loc6_ = _loc5_ / this.TEAMS_DEF["assault" + param4].assault_limit;
      return Math.round(param2 * (1 + _loc6_) + 15000 * _loc6_);
    }
    return param2;
  }

  get_achiev_level(param1: any, param2: number): number {
    param1 = as3.str(param1);
    param2 = Number(param2);
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc3_ = 1;
    while (_loc3_ <= this.ACHIEV_DEF[0]) {
      if (this.ACHIEV_DEF[_loc3_].id == param1) {
        _loc4_ = 0;
        while (this.ACHIEV_DEF[_loc3_].levels[_loc4_] <= param2) {
          _loc4_++;
        }
        return _loc4_ - 1;
      }
      _loc3_++;
    }
    return 0;
  }

  get_achiev_rank(param1: any): number {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = 0;
    for (_loc3_ of as3.keys(param1)) {
      _loc2_ += this.get_achiev_level(_loc3_, param1[_loc3_]);
    }
    return Math.floor(_loc2_ / 10);
  }

  get_achiev_bonus(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    _loc1_ = {
      "exist": false,
      "gold": 0,
      "diam": 0,
      "exp": 0,
      "rating": 0
    };
    _loc2_ = 0;
    _loc3_ = 1;
    while (_loc3_ <= this.CRAFT_DEF[0]) {
      if (this.player.recipes[_loc3_] == 1) {
        _loc2_++;
      }
      _loc3_++;
    }
    this.player.achiev["recipes"] = _loc2_;
    this.player.achiev["career"] = this.player.career_max - 1;
    this.player.achiev["missions"] = this.player.missions_max - 1;
    this.player.achiev["survival"] = this.player.survival_max - 1;
    this.player.achiev["boss"] = this.player.boss_max - 1;
    for (_loc4_ of as3.keys(this.player.achiev)) {
      if (this.player.last_achiev[_loc4_] != this.get_achiev_level(_loc4_, this.player.achiev[_loc4_])) {
        if (this.player.last_achiev[_loc4_] < this.get_achiev_level(_loc4_, this.player.achiev[_loc4_])) {
          _loc5_ = 0;
          _loc6_ = this.player.last_achiev[_loc4_];
          do {
            if (_loc5_ >= 20) {
              break;
            }
            _loc5_++;
            _loc6_++;
            _loc1_[this.ACHIEV_PRISE_DEF[_loc4_].prise] += this.ACHIEV_PRISE_DEF[_loc4_].amount[_loc6_];
            _loc1_.exist = true;
          } while (_loc6_ != this.get_achiev_level(_loc4_, this.player.achiev[_loc4_]));
        }
      }
    }
    if (this.get_achiev_rank(this.player.achiev) != this.player.last_achiev_range) {
      _loc1_.rating = this.ACHIEV_RATING_BONUS[this.get_achiev_rank(this.player.achiev)];
      _loc1_.exist = true;
    }
    return _loc1_;
  }

  get_task_bonus(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    _loc1_ = {
      "exist": false,
      "gold": 0,
      "diam": 0,
      "exp": 0,
      "rating": 0
    };
    _loc2_ = this.player.last_presented_task;
    while (_loc2_ <= this.player.cur_task - 1) {
      _loc1_[this.TASK_DEF[_loc2_].bonus_type] += this.TASK_DEF[_loc2_].bonus;
      _loc1_.exist = true;
      _loc2_++;
    }
    return _loc1_;
  }

  get_attack_cost(param1: number): number {
    param1 = Number(param1);
    if (param1 < 100) {
      return 1;
    }
    if (param1 < 150) {
      return 2;
    }
    if (param1 < 200) {
      return 3;
    }
    if (param1 < 250) {
      return 4;
    }
    return 5;
  }

  get_fail_chance(param1: number, param2: number, param3: number, param4: boolean): any {
    param1 = Number(param1);
    param2 = Number(param2);
    param3 = Number(param3);
    param4 = Boolean(param4);
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    _loc5_ = { "fail": 0, "tired": 0, "perks": 0 };
    if (param4) {
      _loc5_.fail = 0.01 + param3;
    } else {
      if (param1 < 100) {
        _loc6_ = 0;
      } else if (param1 < 200) {
        _loc6_ = 0.05;
      } else if (param1 < 300) {
        _loc6_ = 0.1;
      } else if (param1 < 400) {
        _loc6_ = 0.2;
      } else if (param1 < 500) {
        _loc6_ = 0.3;
      } else {
        _loc6_ = 0.4;
      }
      if (param1 < 100) {
        _loc5_.fail = _loc6_ + param3;
      } else {
        _loc5_.fail = Math.ceil((_loc6_ * param2 + param3) * 100) / 100;
      }
      _loc5_.tired = _loc6_;
      _loc5_.perks = this.sign(_loc5_.fail - _loc5_.tired) * Math.ceil(this.mod(_loc5_.fail - _loc5_.tired) * 100) / 100;
    }
    return _loc5_;
  }

  get_steal_prob(param1: number, param2: number, param3: number, param4: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    param3 = Number(param3);
    param4 = Number(param4);
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    _loc5_ = param1;
    if (param1 > 70) {
      _loc5_ = 70;
    }
    _loc6_ = 0;
    _loc7_ = (param3 + param4 / 3) / 11;
    _loc6_ = _loc5_ / 70 * 2 + 20 * Math.pow((70 - param2) / 70, 4) * (_loc7_ + 0.5);
    _loc6_ = Math.ceil(_loc6_) - 1;
    if (_loc6_ < 0) {
      _loc6_ = 0;
    }
    return Number(_loc6_);
  }

  get_crate_param(param1: any, param2: any, param3: number): any {
    param2 = as3.str(param2);
    param3 = Number(param3);
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    let _loc15_: any = undefined;
    let _loc16_: any = undefined;
    _loc4_ = { "generate": false, "weapon": "" };
    _loc5_ = 10;
    _loc6_ = 0;
    if (Boolean(param1["luck"]) && param1["luck"] != 0) {
      _loc5_ += param1["luck"] * 3;
      _loc6_ += param1["luck"] * 7;
    }
    if (Boolean(param1["luck2"]) && param1["luck2"] != 0) {
      _loc5_ += param1["luck2"] * 4;
      _loc6_ += param1["luck2"] * 7;
    }
    if (Boolean(param1["crates"]) && Boolean(param1["crates"] != 0) && param3 != 3) {
      _loc5_ += param1["crates"] * 8;
      _loc6_ += param1["crates"] * 20;
    }
    if (param3 == 3) {
      _loc5_ += 3 * 8;
      _loc6_ += 3 * 20;
    }
    if (Boolean(param1["big_guns"]) && param1["big_guns"] != 0) {
      _loc6_ += param1["big_guns"] * 5;
    }
    if (param2 == "just" && _loc6_ > 39) {
      _loc6_ = 39;
    }
    if (_loc5_ > this.pseudo_random(0, 100)) {
      _loc4_.generate = true;
      _loc7_ = "crates_1";
      if (_loc6_ <= 39) {
        _loc7_ = "crates_2";
      }
      if (_loc6_ <= 59) {
        _loc7_ = "crates_3";
      }
      if (_loc6_ <= 79) {
        _loc7_ = "crates_4";
      }
      if (_loc6_ <= 116) {
        _loc7_ = "crates_5";
      }
      if (_loc6_ > 116) {
        _loc7_ = "crates_6";
      }
      _loc8_ = this.ROUL_VER[_loc7_];
      _loc9_ = new Array<any>(_loc8_[0], 0);
      _loc10_ = 0;
      _loc11_ = "";
      _loc12_ = 1;
      while (_loc12_ <= 7) {
        _loc9_[_loc12_] = _loc8_[_loc12_] + _loc9_[_loc12_ - 1];
        _loc12_++;
      }
      _loc13_ = this.pseudo_random(0, 99);
      _loc14_ = 0;
      while (_loc14_ <= 7) {
        if (_loc13_ < _loc9_[_loc14_]) {
          _loc10_ = _loc14_;
          break;
        }
        _loc14_++;
      }
      if (_loc10_ != 0) {
        do {
          _loc15_ = this.pseudo_random(1, 200);
          for (_loc16_ of as3.keys(this.WEAPONS_DEF)) {
            if (this.WEAPONS_DEF[_loc16_].id == _loc15_ && this.WEAPONS_DEF[_loc16_].id != 61 && _loc16_.search("rez") == -1 && Boolean(this.WEAPONS_DEF[_loc16_].shown)) {
              if (this.WEAPONS_DEF[_loc16_].wpclass == _loc14_) {
                _loc11_ = _loc16_;
              }
            }
          }
        } while (_loc11_ == "");
      }
      if (_loc11_ != "") {
        _loc4_.weapon = _loc11_;
      } else {
        _loc4_.generate = false;
      }
    }
    this.deep_trace(_loc4_);
    return _loc4_;
  }

  get_rating_inc(param1: number, param2: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    if (param1 == 10) {
      if (param2 >= 300) {
        return 30;
      }
      if (param2 >= 100) {
        return 20;
      }
      if (param2 >= 50) {
        return 15;
      }
      if (param2 >= 25) {
        return 10;
      }
      if (param2 >= 10) {
        return 8;
      }
      return 5;
    }
    if (param1 == 25) {
      if (param2 >= 300) {
        return 50;
      }
      if (param2 >= 100) {
        return 25;
      }
      if (param2 >= 50) {
        return 20;
      }
      if (param2 >= 25) {
        return 15;
      }
      if (param2 >= 10) {
        return 12;
      }
      return 10;
    }
    if (param1 == 50) {
      if (param2 >= 300) {
        return 75;
      }
      if (param2 >= 100) {
        return 35;
      }
      if (param2 >= 50) {
        return 30;
      }
      if (param2 >= 25) {
        return 25;
      }
      if (param2 >= 10) {
        return 20;
      }
      return 15;
    }
    if (param1 == 100) {
      if (param2 >= 300) {
        return 100;
      }
      if (param2 >= 100) {
        return 60;
      }
      if (param2 >= 50) {
        return 50;
      }
      if (param2 >= 25) {
        return 40;
      }
      if (param2 >= 10) {
        return 25;
      }
      return 20;
    }
    if (param1 == 300) {
      if (param2 >= 300) {
        return 500;
      }
      if (param2 >= 100) {
        return 150;
      }
      if (param2 >= 50) {
        return 100;
      }
      if (param2 >= 25) {
        return 75;
      }
      if (param2 >= 10) {
        return 60;
      }
      return 50;
    }
    return 0;
  }

  get_stealth_eff(param1: number, param2: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    _loc3_ = 0;
    if (Boolean(this.player.perks["ninja"]) && this.player.perks["ninja"] > 0) {
      _loc3_ = 30 * this.player.perks["ninja"];
    }
    _loc4_ = param2 + _loc3_;
    if (_loc4_ < 20) {
      return param1;
    }
    if (_loc4_ >= 20 && _loc4_ < 40) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 3;
        case 4:
          return 4;
        case 5:
          return 5;
        case 6:
          return 6;
      }
    }
    if (_loc4_ >= 40 && _loc4_ < 60) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 2;
        case 4:
          return 3;
        case 5:
          return 5;
        case 6:
          return 6;
      }
    }
    if (_loc4_ >= 60 && _loc4_ < 80) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 1;
        case 4:
          return 3;
        case 5:
          return 5;
        case 6:
          return 6;
      }
    }
    if (_loc4_ >= 80 && _loc4_ < 100) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 1;
        case 4:
          return 1;
        case 5:
          return 4;
        case 6:
          return 6;
      }
    }
    _loc5_ = this.pseudo_random(0, 100);
    if (_loc4_ >= 100 && _loc5_ <= 30) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 1;
        case 4:
          return 1;
        case 5:
          return 3;
        case 6:
          return 4;
      }
    }
    if (_loc4_ >= 100 && _loc5_ <= 50) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 1;
        case 4:
          return 1;
        case 5:
          return 3;
        case 6:
          return 5;
      }
    }
    if (_loc4_ >= 100 && _loc5_ > 50) {
      switch (param1) {
        case 1:
          return 1;
        case 2:
          return 1;
        case 3:
          return 1;
        case 4:
          return 1;
        case 5:
          return 3;
        case 6:
          return 6;
      }
    }
    return param1;
  }

  get_ap_chance(param1: any, param2: any): any {
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc3_ = param2.attack;
    _loc4_ = { "ap": 0, "perks": 0, "skills": 0 };
    if (_loc3_ < 200) {
      _loc4_.skills = 0;
    } else if (_loc3_ < 600) {
      _loc4_.skills = (_loc3_ - 200) / 20 + 5;
    } else if (_loc3_ < 700) {
      _loc4_.skills = (_loc3_ - 600) / 2 + 25;
    } else {
      _loc4_.skills = 75;
    }
    if (param1["ap_shots"]) {
      _loc4_.perks += param1["ap_shots"] * 5;
    }
    _loc4_.ap = (_loc4_.skills + _loc4_.perks) / 100;
    _loc4_.skills = Math.round(_loc4_.skills);
    _loc4_.perks = Math.round(_loc4_.perks);
    return _loc4_;
  }

  get_absorb_chance(param1: any, param2: any): any {
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc3_ = param2.protect;
    _loc4_ = { "absorb": 0, "perks": 0, "skills": 0 };
    if (_loc3_ < 200) {
      _loc4_.skills = 0;
    } else if (_loc3_ < 600) {
      _loc4_.skills = (_loc3_ - 200) / 20 + 5;
    } else {
      _loc4_.skills = (_loc3_ - 600) * 3 / 4 + 25;
    }
    if (param1["absorb"]) {
      _loc4_.perks += param1["absorb"] * 5;
    }
    if (param1["dmg_thr"]) {
      _loc4_.perks += Math.floor(param1["dmg_thr"] / 4);
    }
    _loc4_.absorb = (_loc4_.skills + _loc4_.perks) / 100;
    _loc4_.skills = Math.round(_loc4_.skills);
    _loc4_.perks = Math.round(_loc4_.perks);
    if (_loc4_.absorb > 1) {
      _loc4_.absorb = 1;
    }
    return _loc4_;
  }

  get_papa_weapons(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = null;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    // PORT: design D3 - the assortment of the shop the server opened today (papa.enter, in the player's calendar).
    if (this.papa_assortment && this.papa_assortment[param1]) {
      return [this.papa_assortment[param1].length].concat(this.papa_assortment[param1]);
    }
    _loc2_ = new Date(this.user.unix_time * 1000);
    _loc3_ = _loc2_.getDate();
    if (param1 == "weapons") {
      _loc4_ = this.PAPA_WEAPONS[_loc3_ % 8];
    }
    if (param1 == "objs") {
      _loc4_ = this.PAPA_OBJECTS[_loc3_ % 8];
    }
    return _loc4_;
  }

  get_grave_result(param1: number, param2: any, param3: number): any {
    param1 = Number(param1);
    param2 = as3.str(param2);
    param3 = Number(param3);
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    _loc4_ = { "ptype": "", "id": "" };
    _loc5_ = this.true_random(1, 1000);
    if (param2 == "boss") {
      if (_loc5_ <= 100) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 600) {
        _loc4_.ptype = "diam";
      } else {
        _loc4_.ptype = "obj";
      }
    } else if (param1 == 7) {
      if (_loc5_ <= 300) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 600) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 650) {
        _loc4_.ptype = "diam";
      } else {
        _loc4_.ptype = "obj";
      }
    } else if (param1 == 6) {
      if (_loc5_ <= 400) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 600) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 650) {
        _loc4_.ptype = "diam";
      } else if (_loc5_ <= 900) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "";
      }
    } else if (param1 == 5) {
      if (_loc5_ <= 440) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 690) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 700) {
        _loc4_.ptype = "diam";
      } else if (_loc5_ <= 850) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "";
      }
    } else if (param1 == 4) {
      if (_loc5_ <= 400) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 550) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 600) {
        _loc4_.ptype = "weapon";
      } else if (_loc5_ <= 750) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "";
      }
    } else if (param1 == 3) {
      if (_loc5_ <= 350) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 450) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 550) {
        _loc4_.ptype = "weapon";
      } else if (_loc5_ <= 700) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "";
      }
    } else if (param1 == 2) {
      if (_loc5_ <= 300) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 350) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 450) {
        _loc4_.ptype = "weapon";
      } else if (_loc5_ <= 600) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "";
      }
    } else if (param1 == 1) {
      if (_loc5_ <= 250) {
        _loc4_.ptype = "stim";
      } else if (_loc5_ <= 350) {
        _loc4_.ptype = "gold";
      } else if (_loc5_ <= 500) {
        _loc4_.ptype = "obj";
      } else {
        _loc4_.ptype = "weapon";
      }
    }
    if (_loc4_.ptype == "") {
      return _loc4_;
    }
    _loc6_ = this.true_random(1, 100);
    if (_loc4_.ptype == "stim") {
      if (param2 == "boss" || param1 == 7 || param1 == 6) {
        if (_loc6_ <= 50) {
          _loc4_.id = "stimpack";
        } else if (_loc6_ <= 65) {
          _loc4_.id = "jet";
        } else if (_loc6_ <= 80) {
          _loc4_.id = "buffout";
        } else if (_loc6_ <= 92) {
          _loc4_.id = "superstimpack";
        } else {
          _loc4_.id = "psycho";
        }
      } else if (param1 >= 4) {
        if (_loc6_ <= 70) {
          _loc4_.id = "stimpack";
        } else if (_loc6_ <= 82) {
          _loc4_.id = "jet";
        } else if (_loc6_ <= 94) {
          _loc4_.id = "buffout";
        } else {
          _loc4_.id = "superstimpack";
        }
      } else if (_loc6_ <= 80) {
        _loc4_.id = "stimpack";
      } else if (_loc6_ <= 90) {
        _loc4_.id = "jet";
      } else {
        _loc4_.id = "buffout";
      }
      return _loc4_;
    }
    if (_loc4_.ptype == "gold") {
      if (param2 == "boss" || param1 == 7 || param1 == 6) {
        if (_loc6_ <= 50) {
          _loc4_.id = "300";
        } else if (_loc6_ <= 70) {
          _loc4_.id = "400";
        } else if (_loc6_ <= 80) {
          _loc4_.id = "600";
        } else if (_loc6_ <= 90) {
          _loc4_.id = "800";
        } else if (_loc6_ <= 95) {
          _loc4_.id = "1000";
        } else {
          _loc4_.id = "1500";
        }
      } else if (param1 >= 4) {
        if (_loc6_ <= 50) {
          _loc4_.id = "100";
        } else if (_loc6_ <= 80) {
          _loc4_.id = "200";
        } else if (_loc6_ <= 90) {
          _loc4_.id = "300";
        } else if (_loc6_ <= 95) {
          _loc4_.id = "400";
        } else {
          _loc4_.id = "500";
        }
      } else if (_loc6_ <= 80) {
        _loc4_.id = "50";
      } else if (_loc6_ <= 90) {
        _loc4_.id = "100";
      } else if (_loc6_ <= 95) {
        _loc4_.id = "150";
      } else {
        _loc4_.id = "200";
      }
      return _loc4_;
    }
    if (_loc4_.ptype == "diam") {
      if (param2 == "boss" || param1 == 6) {
        if (_loc6_ <= 95) {
          _loc4_.id = "2";
        } else if (_loc6_ <= 98) {
          _loc4_.id = "3";
        } else {
          _loc4_.id = "4";
        }
      } else if (param1 >= 4) {
        if (_loc6_ <= 95) {
          _loc4_.id = "1";
        } else {
          _loc4_.id = "2";
        }
      }
      return _loc4_;
    }
    if (_loc4_.ptype == "weapon") {
      if (param2 == "career") {
        _loc7_ = this.TEAMS_DEF["career" + param3];
      }
      if (param2 == "missions") {
        _loc7_ = this.TEAMS_DEF["mission" + param3];
      }
      if (param2 == "survival") {
        _loc7_ = this.TEAMS_DEF["survival" + param3];
      }
      if (param2 == "boss") {
        _loc7_ = this.TEAMS_DEF["boss" + param3];
      }
      _loc8_ = [0, ""];
      _loc9_ = 2;
      while (_loc9_ <= _loc7_.teams + 1) {
        for (_loc10_ of as3.keys(_loc7_["team" + _loc9_].weapons)) {
          if (this.WEAPONS_DEF[_loc10_].id < 200 && this.WEAPONS_DEF[_loc10_].wpclass <= 3 && _loc10_ != "skip_go") {
            _loc11_ = false;
            _loc12_ = 1;
            while (_loc12_ <= _loc8_[0]) {
              if (_loc8_[_loc12_] == _loc10_) {
                _loc11_ = true;
              }
              _loc12_++;
            }
            if (!_loc11_) {
              ++_loc8_[0];
              _loc8_[_loc8_[0]] = _loc10_;
            }
          }
        }
        _loc9_++;
      }
      if (_loc8_[0] == 0) {
        return { "ptype": "", "id": "" };
      }
      _loc4_.id = _loc8_[this.true_random(1, _loc8_[0])];
    }
    if (_loc4_.ptype == "obj") {
      if (param2 == "boss" || param1 == 7) {
        if (_loc6_ <= 5) {
          _loc4_.id = "obj_barrel";
        } else if (_loc6_ <= 10) {
          _loc4_.id = "obj_middle_clip";
        } else if (_loc6_ <= 15) {
          _loc4_.id = "obj_small_cell";
        } else if (_loc6_ <= 20) {
          _loc4_.id = "obj_napalm_cont";
        } else if (_loc6_ <= 25) {
          _loc4_.id = "obj_laser_mod";
        } else if (_loc6_ <= 30) {
          _loc4_.id = "obj_plasma_mod";
        } else if (_loc6_ <= 35) {
          _loc4_.id = "obj_fuel";
        } else if (_loc6_ <= 40) {
          _loc4_.id = "obj_small_expl";
        } else if (_loc6_ <= 52) {
          _loc4_.id = "obj_big_clip";
        } else if (_loc6_ <= 64) {
          _loc4_.id = "obj_big_cell";
        } else if (_loc6_ <= 76) {
          _loc4_.id = "obj_hard_jacket";
        } else if (_loc6_ <= 88) {
          _loc4_.id = "obj_poison_cont";
        } else {
          _loc4_.id = "obj_coil";
        }
      } else if (_loc6_ <= 15) {
        _loc4_.id = "obj_barrel";
      } else if (_loc6_ <= 30) {
        _loc4_.id = "obj_middle_clip";
      } else if (_loc6_ <= 45) {
        _loc4_.id = "obj_small_cell";
      } else if (_loc6_ <= 50) {
        _loc4_.id = "obj_napalm_cont";
      } else if (_loc6_ <= 55) {
        _loc4_.id = "obj_laser_mod";
      } else if (_loc6_ <= 60) {
        _loc4_.id = "obj_plasma_mod";
      } else if (_loc6_ <= 75) {
        _loc4_.id = "obj_fuel";
      } else if (_loc6_ <= 90) {
        _loc4_.id = "obj_small_expl";
      } else if (_loc6_ <= 92) {
        _loc4_.id = "obj_big_clip";
      } else if (_loc6_ <= 94) {
        _loc4_.id = "obj_big_cell";
      } else if (_loc6_ <= 96) {
        _loc4_.id = "obj_hard_jacket";
      } else if (_loc6_ <= 98) {
        _loc4_.id = "obj_poison_cont";
      } else {
        _loc4_.id = "obj_coil";
      }
      return _loc4_;
    }
    return _loc4_;
  }

  get_opened_squares(): any {
    let _loc1_: any = undefined;
    _loc1_ = {};
    _loc1_["wormville"] = true;
    _loc1_["papa_john"] = true;
    return _loc1_;
  }

  // PORT: "tester"/"contest" awards were hardcoded lists of VK user ids; no such awards exist without VK.
  get_award(param1: number, param2: any): boolean {
    return false;
  }

  get_player_worm_name(param1: number, param2: any): any {
    param1 = Number(param1);
    param2 = as3.str(param2);
    if (Boolean(this.REPLAY) || Boolean(this.player["worm_name" + param1] == "") || param2 == "just") {
      return "Червь #" + String(param1);
    }
    return as3.str(this.player["worm_name" + param1]);
  }

  get_var_color(param1: number, param2: number): any {
    param1 = Number(param1);
    param2 = Number(param2);
    if (param1 > param2) {
      return "\"#FF0000\"";
    }
    if (param1 < param2) {
      return "\"#009900\"";
    }
    return "\"#000000\"";
  }

  calc_bg_art(param1: number, param2: number, param3: number, param4: number, param5: number, param6: number): any {
    param1 = Number(param1);
    param2 = Number(param2);
    param3 = Number(param3);
    param4 = Number(param4);
    param5 = Number(param5);
    param6 = Number(param6);
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    param2 -= 800;
    _loc7_ = (param5 + param1) / (-param2 + param1);
    _loc8_ = (-param3 + this.WINDOW_WIDTH) * _loc7_;
    return { "x": _loc8_, "y": this.WINDOW_HEIGHT - param4 + param6 / 2 };
  }

  weapon_optimisation(param1: any): any {
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    for (_loc2_ of as3.keys(param1)) {
      _loc3_ = 0;
      _loc4_ = [];
      _loc5_ = 1;
      while (_loc5_ <= 10) {
        if (param1[_loc2_][_loc5_]) {
          if (param1[_loc2_][_loc5_].range > 0) {
            _loc4_[_loc3_] = this.clone(param1[_loc2_][_loc5_]);
            _loc3_++;
          }
        }
        _loc5_++;
      }
      as3.sortOn(_loc4_, "range", as3.ArrayFlags.NUMERIC | as3.ArrayFlags.DESCENDING);
      param1[_loc2_] = {};
      param1[_loc2_] = [_loc3_, {}];
      _loc5_ = 1;
      while (_loc5_ <= _loc3_) {
        param1[_loc2_][_loc5_] = _loc4_[_loc5_ - 1];
        _loc5_++;
      }
    }
  }

  ret_time_format(param1: number): any {
    param1 = Number(param1);
    if (param1 < 60) {
      return this.x3String(param1) + " сек. назад";
    }
    if (param1 < 60 * 60) {
      return this.x3String(Math.floor(param1 / 60)) + " мин. назад";
    }
    if (param1 < 60 * 60 * 24) {
      return this.x3String(Math.floor(param1 / 60 / 60)) + " час. назад";
    }
    return this.x3String(Math.floor(param1 / 60 / 60 / 24)) + " дн. назад";
  }

  /**
   * The clan of a player (by the game's numeric uid), 0 = none. PORT: item 8 - the original searched the member lists
   * PORT: of the fixed clans (return_clan(uid, CLANS_PEOPLE_DEF)); the server now names each player's clan with the
   * PORT: player (clan_of_uid: the tops, the zones' owners, the player's own clan).
   */
  return_clan(param1: number): number {
    const _loc2_: any = this.clan_of_uid["id" + Number(param1)];
    return _loc2_ ? Number(_loc2_.clanId) : 0;
  }

  get_book_recipes(param1: any, param2: any): any {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    _loc3_ = [0, 0];
    _loc4_ = "<b>";
    _loc5_ = 1;
    while (_loc5_ <= this.BOOKS_DEF[param1].param[0]) {
      if (param2 == "text") {
        _loc4_ += this.WEAPONS_DEF[this.CRAFT_DEF[this.BOOKS_DEF[param1].param[_loc5_]].result].name + "\n";
      }
      if (param2 == "array") {
        ++_loc3_[0];
        _loc3_[_loc3_[0]] = this.BOOKS_DEF[param1].param[_loc5_];
      }
      _loc5_++;
    }
    if (param2 == "text") {
      return _loc4_ + "</b>";
    }
    if (param2 == "array") {
      return _loc3_;
    }
    return 0;
  }

  get_bullet_amount(param1: any): number {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    _loc2_ = 0;
    if (this.player.perks["light_clips"]) {
      _loc2_ = this.player.perks["light_clips"];
    }
    _loc3_ = 0;
    if (this.player.perks["medium_clips"]) {
      _loc3_ = this.player.perks["medium_clips"];
    }
    _loc4_ = 0;
    if (this.player.perks["heavy_clips"]) {
      _loc4_ = this.player.perks["heavy_clips"];
    }
    _loc5_ = {
      "pistol": [0, 1, 1, 2, 2, 3, 4, 5, 6, 7, 7],
      "uzi": [0, 2, 4, 5, 6, 8, 10, 11, 12, 13, 15],
      "unfail_uzi": [0, 2, 4, 5, 6, 8, 10, 11, 12, 13, 15],
      "laser_pistol": [0, 0, 1, 1, 2, 3, 3, 4, 5, 6, 7],
      "laser_pistol2": [0, 1, 3, 4, 5, 6, 7, 8, 9, 11, 13],
      "plasma_pistol": [0, 0, 1, 1, 2, 2, 3, 3, 3, 4, 5],
      "gauss_pistol": [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1],
      "gauss_pistol2": [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2]
    };
    _loc6_ = {
      "shotgun": [0, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2],
      "hunting_rifle": [0, 0, 1, 1, 1, 1, 1, 1, 1, 2, 2],
      "peacemaker": [0, 0, 0, 1, 1, 1, 1, 1, 2, 2, 3],
      "hearts_ace": [0, 0, 0, 1, 1, 1, 1, 1, 2, 2, 3],
      "sniper_rifle": [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1],
      "laser_rifle": [0, 1, 2, 4, 6, 8, 10, 11, 12, 13, 15],
      "unfail_laser_rifle": [0, 1, 2, 4, 6, 8, 10, 11, 12, 13, 15],
      "plasma_rifle": [0, 0, 1, 2, 4, 5, 6, 7, 8, 9, 10],
      "unfail_plasma_rifle": [0, 0, 1, 2, 4, 5, 6, 7, 8, 9, 10],
      "gauss_rifle": [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1],
      "unfail_gauss_rifle": [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1]
    };
    _loc7_ = {
      "minigun": [0, 3, 6, 10, 13, 16, 20, 25, 30, 35, 40],
      "unfail_minigun": [0, 3, 6, 10, 13, 16, 20, 25, 30, 35, 40],
      "air_minigun": [0, 3, 6, 10, 12, 14, 16, 18, 20, 25, 30],
      "avenger": [0, 3, 6, 10, 12, 14, 16, 18, 20, 25, 30],
      "rad_rifle": [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
      "bozar": [0, 3, 6, 10, 12, 14, 16, 18, 20, 25, 30],
      "bozar16": [0, 3, 6, 10, 12, 14, 16, 18, 20, 22, 25],
      "bozar24s": [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
      "laser_minigun": [0, 3, 6, 10, 12, 14, 16, 18, 20, 25, 30],
      "plasma_minigun": [0, 3, 6, 10, 12, 14, 16, 18, 20, 22, 25],
      "gauss_minigun": [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    };
    _loc8_ = 0;
    if (_loc2_ != 0 && Boolean(_loc5_[param1])) {
      _loc8_ += _loc5_[param1][_loc2_];
    }
    if (_loc3_ != 0 && Boolean(_loc6_[param1])) {
      _loc8_ += _loc6_[param1][_loc3_];
    }
    if (_loc4_ != 0 && Boolean(_loc7_[param1])) {
      _loc8_ += _loc7_[param1][_loc4_];
    }
    return Number(_loc8_);
  }

  reset_perks(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    this.player.diam.value -= 10;
    _loc1_ = 0;
    _loc2_ = this.clone(this.player.perks);
    for (_loc3_ of as3.keys(this.player.perks)) {
      while (_loc2_[_loc3_] != 0) {
        _loc1_ += this.PERKS_DEF[_loc3_].upg[_loc2_[_loc3_] - 1];
        --_loc2_[_loc3_];
      }
    }
    this.player.perk_pts.value += _loc1_;
    this.player.perks = {};
    while (this.player.attack.value != 0) {
      this.player.skill_pts.value += this.get_attack_cost(this.player.attack.value - 1);
      --this.player.attack.value;
    }
    while (this.player.protect.value != 0) {
      this.player.skill_pts.value += this.get_attack_cost(this.player.protect.value - 1);
      --this.player.protect.value;
    }
    this.player.skill_pts.value += this.player.health.value * 30;
    this.player.skill_pts.value += this.player.worms.value * 150;
    this.player.attack.value = 0;
    this.player.protect.value = 0;
    this.player.health.value = 0;
    this.player.worms.value = 0;
    this.player.spec = 0;
    this.player.last_reset = this.player.level.value;
  }

  improve_spec(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    _loc1_ = this.player.spec;
    _loc2_ = {};
    for (_loc3_ of as3.keys(this.player.perks)) {
      _loc4_ = false;
      _loc5_ = 0;
      while (_loc5_ <= 1) {
        if (_loc3_ == this.SPEC_DEF[_loc1_][_loc5_]) {
          if (this.player.perks[_loc3_] > 0) {
            this.player.perk_pts.value += this.PERKS_DEF[_loc3_].upg[0];
          }
          if (this.player.perks[_loc3_] > 1) {
            this.player.perk_pts.value += this.PERKS_DEF[_loc3_].upg[1];
          }
          if (this.player.perks[_loc3_] > 2) {
            this.player.perk_pts.value += this.PERKS_DEF[_loc3_].upg[2];
          }
          _loc4_ = true;
        }
        _loc5_++;
      }
      if (!_loc4_) {
        _loc2_[_loc3_] = this.player.perks[_loc3_];
      }
    }
    this.player.perks = this.clone(_loc2_);
    this.deep_trace(this.temp_player.perks);
  }

  get_tribute(): any {
    let _loc1_: any = undefined;
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc1_ = 0;
    _loc2_ = 0;
    _loc3_ = 1;
    while (_loc3_ <= 17) {
      if (this.ASSAULT[_loc3_].user_id == this.user.user_id) {
        _loc1_++;
        _loc2_ += Math.ceil(this.TEAMS_DEF["assault" + _loc3_].payments * this.get_tribute_coeff(_loc3_));
      }
      _loc3_++;
    }
    _loc4_ = 0;
    if (this.user_clan != 0) {
      _loc3_ = 1;
      while (_loc3_ <= 17) {
        if (this.return_clan(this.ASSAULT[_loc3_].user_id) == this.user_clan && this.ASSAULT[_loc3_].user_id != this.user.user_id) {
          _loc4_ += Math.ceil(this.TEAMS_DEF["assault" + _loc3_].payments * this.get_tribute_coeff(_loc3_) / 10);
        }
        _loc3_++;
      }
    }
    return { "terr_total": _loc1_, "my_gold": _loc2_, "clans_gold": _loc4_ };
  }

  get_tribute_coeff(param1: any): number {
    let _loc2_: any = undefined;
    _loc2_ = this.assault_cur_time - this.ASSAULT[param1].last_assault;
    if (_loc2_ > 60 * 60) {
      return 1;
    }
    return _loc2_ / 3600;
  }

  get_char_from_key(param1: any): any {
    let _loc2_: any = undefined;
    _loc2_ = "";
    switch (param1.other) {
      case "1":
        _loc2_ += "A";
        break;
      case "2":
        _loc2_ += "B";
        break;
      case "3":
        _loc2_ += "C";
        break;
      case "4":
        _loc2_ += "D";
        break;
      case "5":
        _loc2_ += "E";
        break;
      case "-":
        _loc2_ += "F";
        break;
      case "+":
        _loc2_ += "G";
        break;
      case "up":
        _loc2_ += "H";
        break;
      case "down":
        _loc2_ += "I";
        break;
      case "left":
        _loc2_ += "J";
        break;
      case "right":
        _loc2_ += "K";
        break;
      case "up+left":
        _loc2_ += "L";
        break;
      case "up+right":
        _loc2_ += "M";
        break;
      case "down+left":
        _loc2_ += "N";
        break;
      case "down+right":
        _loc2_ += "O";
        break;
      case "enter":
        _loc2_ += "P";
        break;
      case "backspace":
        _loc2_ += "Q";
        break;
      case "escape":
        _loc2_ += "R";
        break;
      case "weapon":
        _loc2_ += "S";
        break;
      case "click":
        _loc2_ += "T";
    }
    if (param1.space) {
      _loc2_ = "U" + _loc2_;
    }
    return as3.str(_loc2_);
  }

  get_key_from_char(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = { "space": false, "other": "none" };
    if (param1.length == 0) {
      return _loc2_;
    }
    _loc3_ = "";
    if (param1.length > 1) {
      _loc2_.space = true;
      _loc3_ = param1.charAt(1);
    } else {
      _loc3_ = param1.charAt(0);
    }
    switch (_loc3_) {
      case "A":
        _loc2_.other = "1";
        break;
      case "B":
        _loc2_.other = "2";
        break;
      case "C":
        _loc2_.other = "3";
        break;
      case "D":
        _loc2_.other = "4";
        break;
      case "E":
        _loc2_.other = "5";
        break;
      case "F":
        _loc2_.other = "-";
        break;
      case "G":
        _loc2_.other = "+";
        break;
      case "H":
        _loc2_.other = "up";
        break;
      case "I":
        _loc2_.other = "down";
        break;
      case "J":
        _loc2_.other = "left";
        break;
      case "K":
        _loc2_.other = "right";
        break;
      case "L":
        _loc2_.other = "up+left";
        break;
      case "M":
        _loc2_.other = "up+right";
        break;
      case "N":
        _loc2_.other = "down+left";
        break;
      case "O":
        _loc2_.other = "down+right";
        break;
      case "P":
        _loc2_.other = "enter";
        break;
      case "Q":
        _loc2_.other = "backspace";
        break;
      case "R":
        _loc2_.other = "escape";
        break;
      case "S":
        _loc2_.other = "weapon";
        break;
      case "T":
        _loc2_.other = "click";
        break;
      case "U":
        _loc2_.space = true;
    }
    return _loc2_;
  }

  encode_replay_number(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = "";
    if (param1 < 0) {
      _loc2_ += "*";
      param1 *= -1;
    }
    _loc3_ = this.return_bytes_30(param1);
    if (param1 < 30) {
      _loc2_ += String.fromCharCode(_loc3_[0] + 94);
    } else if (param1 < 30 * 30) {
      _loc2_ += String.fromCharCode(_loc3_[0] + 94) + String.fromCharCode(_loc3_[1] + 94);
    } else {
      _loc2_ += String.fromCharCode(_loc3_[0] + 94) + String.fromCharCode(_loc3_[1] + 94) + String.fromCharCode(_loc3_[2] + 94);
    }
    return as3.str(_loc2_);
  }

  decode_replay_number(param1: any): number {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    _loc2_ = 0;
    _loc3_ = 1;
    if (param1.charAt(0) == "*") {
      _loc3_ = -1;
      param1 = as3.str(param1.split("*")[1]);
    }
    _loc4_ = 0;
    while (_loc4_ <= param1.length - 1) {
      _loc2_ += (param1.charCodeAt(_loc4_) - 94) * Math.pow(30, _loc4_);
      _loc4_++;
    }
    return _loc2_ * _loc3_;
  }

  decode_replay_move(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    _loc2_ = new Array();
    _loc3_ = param1.split("$");
    for (_loc4_ of as3.keys(_loc3_)) {
      if (_loc3_[_loc4_].length > 0) {
        _loc5_ = _loc3_[_loc4_];
        _loc6_ = this.decode_replay_number(_loc5_.split("&")[0]);
        _loc7_ = _loc5_.split("&")[1];
        _loc8_ = _loc7_.split("%")[0];
        _loc9_ = 0;
        _loc10_ = 0;
        _loc11_ = 0;
        _loc12_ = 0;
        _loc13_ = 0;
        if (_loc8_ == "S") {
          _loc10_ = this.decode_replay_number(_loc7_.split("%")[1]);
          _loc11_ = this.decode_replay_number(_loc7_.split("%")[2]);
        }
        if (_loc8_ == "T") {
          _loc12_ = this.decode_replay_number(_loc7_.split("%")[1]);
          _loc13_ = this.decode_replay_number(_loc7_.split("%")[2]);
        }
        if (_loc8_ != "S" && _loc8_ != "T" && Boolean(_loc7_.split("%")[1])) {
          _loc9_ = this.decode_replay_number(_loc7_.split("%")[1]);
        }
        _loc2_[_loc6_] = {
          "key_code": this.get_key_from_char(_loc8_),
          "repeats": _loc9_,
          "params": { "weapon": _loc10_, "range": _loc11_, "click_x": _loc12_, "click_y": _loc13_ }
        };
      }
    }
    return _loc2_;
  }

  decode_move(param1: number): any {
    param1 = Number(param1);
    this.REPLAY_STRING_CUR_MOVE = this.REPLAY_STRING_MOVES.split("#")[param1 - 1];
    this.REPLAY_CUR_MOVE = new Array();
    this.REPLAY_CUR_MOVE = this.decode_replay_move(this.REPLAY_STRING_CUR_MOVE);
  }

  clear_move(): any {
    this.REPLAY_CUR_MOVE = new Array();
  }

  /**
   * PORT: a text that is no record at all (a player's paste into the replay tab) is a damaged record (state false,
   * PORT: the tab says so) instead of a TypeError from a missing part: the original's parser takes every part as there.
   */
  check_replay_string(param1: any): any {
    try {
      return this.check_replay_string_parts(param1);
    } catch {
      return { "state": false, "play_type": "", "play_id": 0, "params": {} };
    }
  }

  check_replay_string_parts(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    let _loc7_: any = undefined;
    let _loc8_: any = undefined;
    let _loc9_: any = undefined;
    let _loc10_: any = undefined;
    let _loc11_: any = undefined;
    let _loc12_: any = undefined;
    let _loc13_: any = undefined;
    let _loc14_: any = undefined;
    _loc2_ = "!" + param1.split("!")[1] + "!" + param1.split("!")[2];
    _loc3_ = 0;
    _loc4_ = 0;
    while (_loc4_ <= _loc2_.length - 1) {
      _loc3_ += _loc2_.charCodeAt(_loc4_);
      _loc4_++;
    }
    _loc5_ = param1.split("!")[3].charAt(0);
    _loc6_ = this.encode_replay_number(_loc3_).charAt(0);
    _loc7_ = param1.split("!")[1];
    _loc8_ = "DD";
    if (param1.search("!DD") != -1) {
      _loc8_ = "DD";
    }
    if (param1.search("!DE") != -1) {
      _loc8_ = "DE";
    }
    _loc9_ = _loc7_.split(_loc8_)[1].charAt(0);
    if (_loc9_ == "c") {
      _loc9_ = "career";
    }
    if (_loc9_ == "m") {
      _loc9_ = "missions";
    }
    if (_loc9_ == "s") {
      _loc9_ = "survival";
    }
    if (_loc9_ == "b") {
      _loc9_ = "boss";
    }
    _loc10_ = this.decode_replay_number(_loc7_.split("I")[1].split("$")[0]);
    _loc11_ = {};
    _loc11_.attack = this.decode_replay_number(param1.split("P")[1].split("$")[0]);
    _loc11_.protect = this.decode_replay_number(param1.split("P")[1].split("$")[1]);
    _loc11_.health = this.decode_replay_number(param1.split("P")[1].split("$")[2]);
    _loc11_.worms = this.decode_replay_number(param1.split("P")[1].split("$")[4]);
    _loc11_.skill_level = this.get_skill_level_name(this.decode_replay_number(param1.split("Z")[1].split("$")[0]));
    _loc11_.weapons = {};
    _loc12_ = 1;
    while (_loc12_ <= param1.split("S%").length - 1) {
      _loc13_ = this.decode_replay_number(param1.split("S%")[_loc12_].split("$")[0]);
      for (_loc14_ of as3.keys(this.WEAPONS_DEF)) {
        if (this.WEAPONS_DEF[_loc14_].id == _loc13_) {
          _loc11_.weapons[_loc14_] = 1;
        }
      }
      _loc12_++;
    }
    _loc11_.turns = param1.split("#").length;
    return { "state": _loc5_ == _loc6_, "play_type": _loc9_, "play_id": _loc10_, "params": _loc11_ };
  }

  get_skill_level_name(param1: number): any {
    param1 = Number(param1);
    switch (param1) {
      case 0:
        return "Обычный";
      case 1:
        return "Сложный";
      case 2:
        return "Очень сложный";
      case 3:
        return "Невозмжный";
      case 4:
        return "Кошмар";
      default:
        return "";
    }
  }

  get_bitmap_data(param1: any): any {
    let _loc2_: any = null;
    return new BitmapData(807, 600, true, 1140850688);
  }

  get_screenshot_data(param1: any): any {
    let _loc2_: any = null;
    if (_loc2_) {
      _loc2_ = null;
    }
    _loc2_ = new BitmapData(807, 600);
    _loc2_.draw(param1);
    return _loc2_;
  }

  get_blur_filter(): any {
    return new BlurFilter(2, 2, BitmapFilterQuality.MEDIUM);
  }

  mod(param1: number): number {
    param1 = Number(param1);
    return Math.abs(param1);
  }

  sign(param1: number): number {
    param1 = Number(param1);
    return param1 >= 0 ? 1 : -1;
  }

  ret_60(param1: number): number {
    param1 = Number(param1);
    return Math.pow(60, param1);
  }

  x3String(param1: number): any {
    param1 = Number(param1);
    let _loc3_: any = undefined;
    let _loc2_: any = undefined;
    _loc2_ = String(param1);
    _loc3_ = "";
    if (_loc2_.length == 4) {
      return _loc2_.charAt(0) + " " + _loc2_.substr(1, 3);
    }
    if (_loc2_.length == 5) {
      return _loc2_.substr(0, 2) + " " + _loc2_.substr(2, 3);
    }
    if (_loc2_.length == 6) {
      return _loc2_.substr(0, 3) + " " + _loc2_.substr(3, 3);
    }
    if (_loc2_.length == 7) {
      return _loc2_.charAt(0) + " " + _loc2_.substr(1, 3) + " " + _loc2_.substr(4, 3);
    }
    if (_loc2_.length == 8) {
      return _loc2_.substr(0, 2) + " " + _loc2_.substr(2, 3) + " " + _loc2_.substr(5, 3);
    }
    if (_loc2_.length == 9) {
      return _loc2_.substr(0, 3) + " " + _loc2_.substr(3, 3) + " " + _loc2_.substr(6, 3);
    }
    return as3.str(_loc2_);
  }

  x3SHString(param1: number): any {
    param1 = Number(param1);
    if (param1 < 1000000) {
      return this.x3String(param1);
    }
    return this.x3String(Math.floor(param1 / 1000)) + " k";
  }

  xKString(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    if (param1 < 1000) {
      _loc2_ = String(param1);
    } else if (param1 < 10000) {
      _loc2_ = String(Math.floor(param1 / 100) / 10) + "k";
    } else if (param1 < 100000) {
      _loc2_ = String(Math.floor(param1 / 100) / 10) + "k";
    } else if (param1 < 1000000) {
      _loc2_ = String(Math.floor(param1 / 1000)) + "k";
    } else {
      _loc2_ = String(Math.floor(param1 / 100000) / 10) + "m";
    }
    return as3.str(_loc2_);
  }

  return_bytes(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = null;
    _loc2_ = new Array<any>(0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    if (param1 < 60) {
      _loc2_[0] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[1]) / 60);
      _loc2_[2] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60 * 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[1]) / 60);
      _loc2_[2] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[2]) / 60);
      _loc2_[3] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60 * 60 * 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[1]) / 60);
      _loc2_[2] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[2]) / 60);
      _loc2_[3] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[3]) / 60);
      _loc2_[4] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60 * 60 * 60 * 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[1]) / 60);
      _loc2_[2] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[2]) / 60);
      _loc2_[3] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[3]) / 60);
      _loc2_[4] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[4]) / 60);
      _loc2_[5] = param1;
      return _loc2_;
    }
    if (param1 < 60 * 60 * 60 * 60 * 60 * 60 * 60) {
      _loc2_[0] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[0]) / 60);
      _loc2_[1] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[1]) / 60);
      _loc2_[2] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[2]) / 60);
      _loc2_[3] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[3]) / 60);
      _loc2_[4] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[4]) / 60);
      _loc2_[5] = param1 % 60;
      param1 = Math.round((param1 - _loc2_[5]) / 60);
      _loc2_[6] = param1;
      return _loc2_;
    }
    _loc2_[0] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[0]) / 60);
    _loc2_[1] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[1]) / 60);
    _loc2_[2] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[2]) / 60);
    _loc2_[3] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[3]) / 60);
    _loc2_[4] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[4]) / 60);
    _loc2_[5] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[5]) / 60);
    _loc2_[6] = param1 % 60;
    param1 = Math.round((param1 - _loc2_[6]) / 60);
    _loc2_[7] = param1;
    return _loc2_;
  }

  return_bytes_30(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = null;
    _loc2_ = new Array<any>(0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    if (param1 < 30) {
      _loc2_[0] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[1]) / 30);
      _loc2_[2] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30 * 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[1]) / 30);
      _loc2_[2] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[2]) / 30);
      _loc2_[3] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30 * 30 * 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[1]) / 30);
      _loc2_[2] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[2]) / 30);
      _loc2_[3] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[3]) / 30);
      _loc2_[4] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30 * 30 * 30 * 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[1]) / 30);
      _loc2_[2] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[2]) / 30);
      _loc2_[3] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[3]) / 30);
      _loc2_[4] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[4]) / 30);
      _loc2_[5] = param1;
      return _loc2_;
    }
    if (param1 < 30 * 30 * 30 * 30 * 30 * 30 * 30) {
      _loc2_[0] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[0]) / 30);
      _loc2_[1] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[1]) / 30);
      _loc2_[2] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[2]) / 30);
      _loc2_[3] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[3]) / 30);
      _loc2_[4] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[4]) / 30);
      _loc2_[5] = param1 % 30;
      param1 = Math.round((param1 - _loc2_[5]) / 30);
      _loc2_[6] = param1;
      return _loc2_;
    }
    _loc2_[0] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[0]) / 30);
    _loc2_[1] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[1]) / 30);
    _loc2_[2] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[2]) / 30);
    _loc2_[3] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[3]) / 30);
    _loc2_[4] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[4]) / 30);
    _loc2_[5] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[5]) / 30);
    _loc2_[6] = param1 % 30;
    param1 = Math.round((param1 - _loc2_[6]) / 30);
    _loc2_[7] = param1;
    return _loc2_;
  }

  deep_trace(param1: any, param2: number = 0): any {
    // PORT: debug-only recursive dump whose only effect is trace() output (its text is never even passed to
    // PORT: trace); skipped unless a trace sink is configured, so production frames don't walk whole UI trees.
    if (!as3.traceEnabled()) {
      return;
    }
    param2 = as3.int(param2);
    let _loc3_: any = null;
    let _loc4_: number = 0;
    let _loc5_: any = null;
    _loc3_ = "";
    _loc4_ = 0;
    while (_loc4_ < param2) {
      _loc3_ += "\t";
      _loc4_ = as3.int(_loc4_ + 1);
    }
    for (const __it1 of as3.keys(param1)) {
      _loc5_ = as3.str(__it1);
      this.deep_trace(param1[_loc5_], param2 + 1);
    }
  }

  debug_get_param(param1: any): any {
    param1 = as3.str(param1);
    let _loc2_: any = undefined;
    let _loc3_: any = undefined;
    _loc2_ = "( ";
    _loc3_ = 0;
    while (_loc3_ <= param1.length - 1) {
      _loc2_ += param1.charCodeAt(_loc3_);
      if (_loc3_ != param1.length - 1) {
        _loc2_ += ", ";
      } else {
        _loc2_ += ")";
      }
      _loc3_++;
    }
    return as3.str(_loc2_);
  }

  throw_error(param1: any): any {
    param1 = as3.str(param1);
    this.debug_text += param1;
    this.display_system.addChild(this.splash_error);
    this.splash_error.out.text = this.debug_text;
  }

  clone(param1: any): any {
    // PORT: ByteArray.writeObject/readObject (AMF3 deep copy) replaced by the equivalent as3.amfClone.
    return as3.amfClone(param1);
  }

  find(param1: any, param2: any): any {
    param1 = as3.str(param1);
    param2 = as3.str(param2);
    let _loc3_: any = undefined;
    let _loc4_: any = undefined;
    let _loc5_: any = undefined;
    let _loc6_: any = undefined;
    _loc6_ = {};
    if (param1 == "perks") {
      _loc5_ = this.PERKS_DEF;
    }
    for (_loc3_ of as3.keys(_loc5_)) {
      if (_loc3_ == param2) {
        for (_loc4_ of as3.keys(_loc5_[_loc3_])) {
          _loc6_[_loc4_] = _loc5_[_loc3_][_loc4_];
        }
      }
    }
    return _loc6_;
  }

  pseudo_random(param1: number, param2: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    let _loc3_: number = NaN;
    return Math.floor(this.fwp_random() * (param2 - param1 + 1)) + param1;
  }

  true_random(param1: number, param2: number): number {
    param1 = Number(param1);
    param2 = Number(param2);
    let _loc3_: number = NaN;
    return Math.floor(as3.random() * (param2 - param1 + 1)) + param1;
  }

  fwp_random(): any {
    ++this.cur_random;
    if (this.cur_random >= 400) {
      this.cur_random = 0;
    }
    return this.RANDOM_NUMBERS[this.cur_random];
  }

  init_random_numbers(): any {
    // PORT: the 400 literal assignments this.RANDOM_NUMBERS[i] = v were moved to data/RANDOM_NUMBERS.ts (same values, same order).
    const table: number[] = create_RANDOM_NUMBERS();
    for (let i = 0; i < table.length; i++) this.RANDOM_NUMBERS[i] = table[i];
  }

  // PORT: VK removed: recover_params(uid) returned hardcoded VK-storage save strings of five VK user ids (manual
  // PORT: progress recovery by the developer; never called, FWP.as:22280-23575). Not ported: VK profile data.

  recovery(): boolean {
    return false;
  }

  get_wpij(param1: number): any {
    param1 = Number(param1);
    let _loc2_: any = undefined;
    let _loc3_: number = NaN;
    _loc2_ = Math.floor(param1 / 5);
    _loc3_ = Math.floor(param1 % 5);
    if (_loc3_ == 0) {
      _loc3_ = 5;
      _loc2_--;
    }
    _loc2_++;
    return { "i": _loc2_, "j": _loc3_ };
  }
}
